import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  Pane,
  Marker,
  Polyline,
  Polygon,
  Circle,
  CircleMarker,
  Tooltip,
  LayersControl,
  ScaleControl,
  GeoJSON,
  useMap
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
window.L = L;
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import jsPDF from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";
import PropertyModal from "./PropertyModal";
import PenetrationMarkers from "./PenetrationMarkers";
import GeoJsonWithLayers from "./GeoJsonWithLayers";
import { geoJsonToFeatures } from "./utils/geoJsonToFeatures";
import { featuresToGeoJSON } from "./utils/featuresToGeoJSON";
import { circleToPolygon } from "./utils/circleToPolygon";
import LayerPanel from './components/LayerPanel';
import LayerPanelWithHover from "./components/LayerPanelWithHover";
import { getLineLengthMeters, getAdaptiveStepSize, interpolateLine3D } from './utils/geometryUtils';
import { safeGetLatLngFromGeoJSON, utmPolyToLatLon, expandBulgePolylineToLatLon } from './utils/coordinateUtils';
import CoordinateInputPopup from './CoordinateInputPopup';
import { useLanguage } from './i18n';
import { useUser } from './context/UserContext';
import InformationPopup from './components/InformationPopup';
import HelpPopup from './components/HelpPopup';
import AboutUsModal from './components/AboutUsModal';
import CoordinateInfoBox from './CoordinateInfoBox';
import MeasureTool from "./MeasureTool";
import { savePdfBlob } from "./utils/savePdfBlob";
import { exportPenetrationTablesToPDF } from "./utils/exportPenetrationTablesToPDF";
import ResultatTable from "./components/ResultatTable";
import FlyghinderOrderModal from "./components/FlyghinderOrderModal";
import LoginModal from "./components/LoginModal";
import RegisterModal from "./components/RegisterModal";
import SubscriptionWarningModal from "./components/SubscriptionWarningModal";
import LoginRequiredModal from "./components/LoginRequiredModal";
import FreeExportsExhaustedModal from "./components/FreeExportsExhaustedModal";
import { saveOrderToBackend } from "./api";
import { apiFetch } from "./apiClient";
import { useLayerData } from "./hooks/useLayerData";
import { MapStateTracker } from "./hooks/useMapState";
import LoadingIndicator from "./components/LoadingIndicator";
import ZoomBarControl from "./components/ZoomBarControl";
import TaaResultsPanel from "./components/TaaResultsPanel";
import CirclingResultsPanel from "./components/CirclingResultsPanel";
import GnssCategoryComparison from './components/GnssCategoryComparison';
import LocApproachResults from './components/LocApproachResults';
import { LocApproachOverlay, LOC_LAYER_DEFAULTS } from './components/LocApproachLayers';
import { gnssResultSegments, gnssMocLabel, gnssMissedParameters, gnssHasObstacleResults, gnssObstacleCounts } from './utils/gnssResultSegments';
import { gnssCategoryColor, isGnssProcedureVisible, isGnssDisplayEnabled, isGnssSegmentVisible } from './utils/gnssLayerVisibility';

import {
  getNavaidIcon,
  getNavAidTypeLabel,
  aerodromeCivilLandIcon
} from "./utils/navIcons";

// === BASEMAP CONFIGURATION ===
const basemapUrls = {
  osm: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  cartodb: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
  esri: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  railway: "https://{s}.tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png"
};

// Blå flygplatsikon
const airportIcon = L.icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(
      `<svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="22" cy="22" r="21" fill="#1976d2" stroke="#222" stroke-width="2"/><g><rect x="20" y="10" width="4" height="18" rx="2" fill="#fff"/><polygon points="22,4 26,18 18,18" fill="#fff"/><rect x="10" y="20" width="24" height="4" rx="2" fill="#fff"/><rect x="17" y="29" width="10" height="4" rx="2" fill="#fff"/><rect x="21" y="32" width="2" height="6" rx="1" fill="#fff"/></g></svg>`
    ),
  iconSize: [44, 44],
  iconAnchor: [22, 44],
  popupAnchor: [0, -38],
});

let uniqueId = 0;
function getDefaultZ() { return 50; }
function getDefaultRadius() { return 100; }

// --- Hjälpfunktion för DDMMSS.SS (DMS) ---
function toDMS(coord, isLat) {
  const abs = Math.abs(coord);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = (minFloat - min) * 60;
  const dir = isLat
    ? coord >= 0 ? "N" : "S"
    : coord >= 0 ? "E" : "W";
  return (
    String(deg).padStart(isLat ? 2 : 3, "0") +
    String(min).padStart(2, "0") +
    sec.toFixed(2).padStart(5, "0") +
    dir
  );
}

// --- NY Hjälpfunktion för att rensa koordinater ur name_and_limits ---
function stripCoordinatesFromNameAndLimits(name_and_limits) {
  if (!name_and_limits) return "";
  return name_and_limits.replace(
    /(^.+?)(?:\s+[0-9NSEW\s\-]+to point of origin\.\s*)/i,
    "$1 "
  ).trim();
}

/**
 * Compute destination [lat, lon] from a start point given a bearing (degrees,
 * clockwise from North) and a distance in metres. Uses the spherical law of
 * cosines (Haversine) which is accurate enough for holding-pattern line lengths.
 */
function destPoint(lat, lon, bearingDeg, distM) {
  const R = 6371000;
  const d = distM / R;
  const b = bearingDeg * Math.PI / 180;
  const lat1 = lat * Math.PI / 180;
  const lon1 = lon * Math.PI / 180;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b)
  );
  const lon2 = lon1 + Math.atan2(
    Math.sin(b) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
  );
  return [lat2 * 180 / Math.PI, lon2 * 180 / Math.PI];
}

// Surface hover tooltip, använder t()
function SurfaceHoverTooltip({ surfaceLayers }) {
  const map = useMap();
  const tooltipRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    function getSurfaceInfo(feat) {
      const p = feat.properties || {};
      return `
        <b>${t('airport')}:</b> ${p.airport_name || "-"}<br/>
        <b>${t('runway')}:</b> ${p.runway || "-"}<br/>
        <b>${t('surface')}:</b> ${p.surface_name || "-"}<br/>
        <b>${t('regulation')}:</b> ${p.regulation || "-"}
      `;
    }

    function isPointInLayer(layer, latlng) {
      if (layer.getLatLngs) {
        try {
          return L.Polygon.prototype._containsPoint.call(
            layer,
            map.latLngToLayerPoint(latlng)
          );
        } catch (e) {
          return layer.getBounds && layer.getBounds().contains(latlng);
        }
      }
      return layer.getBounds && layer.getBounds().contains(latlng);
    }

    function onMouseMove(e) {
      const latlng = e.latlng;
      const overlapping = [];
      for (let l of surfaceLayers) {
        if (isPointInLayer(l, latlng)) {
          overlapping.push(l.feature);
        }
      }
      if (overlapping.length) {
        let tooltip = tooltipRef.current;
        if (!tooltip) {
          tooltip = L.tooltip({ permanent: false, direction: "top", sticky: true, className: "surface-hover-tooltip" });
          tooltipRef.current = tooltip;
        }
        tooltip.setLatLng(latlng);
        tooltip.setContent(overlapping.map(getSurfaceInfo).join("<hr style='margin:4px 0'/>"));
        if (!map.hasLayer(tooltip)) map.addLayer(tooltip);
      } else {
        if (tooltipRef.current && map.hasLayer(tooltipRef.current)) {
          map.removeLayer(tooltipRef.current);
        }
      }
    }
    map.on("mousemove", onMouseMove);
    return () => {
      map.off("mousemove", onMouseMove);
      if (tooltipRef.current && map.hasLayer(tooltipRef.current)) {
        map.removeLayer(tooltipRef.current);
      }
    };
  }, [map, surfaceLayers, t]);

  return null;
}

const { BaseLayer } = LayersControl;

function FooterHeader({ onAnalyze, onExportPdf, t, disabledAnalyze, disabledExport, versionInfo, isAuthenticated, isPaid }) {
  // Split subscription info: first sentence, then second sentence
  const fullMessage = t("footerSubscriptionInfo");
  const firstPeriodIndex = fullMessage.indexOf('.');
  const firstSentence = fullMessage.substring(0, firstPeriodIndex + 1);
  const secondSentence = fullMessage.substring(firstPeriodIndex + 1).trim();
  
  // Only show subscription info for non-authenticated users or authenticated but unpaid users
  const showSubscriptionInfo = !isAuthenticated || (isAuthenticated && !isPaid);
  
  return (
    <div
      style={{
        width: "100%",
        background: "#1e3a8a",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 2px",
        boxShadow: "0 -2px 6px rgba(0,0,0,0.10)",
        position: "relative",
        zIndex: 30,
        marginTop: 0
      }}
    >
      <div style={{ flex: 1, paddingLeft: "8px", paddingRight: "8px", maxWidth: "35%" }}>
        {showSubscriptionInfo && (
          <div
            style={{
              fontSize: "12px",
              color: "#e0e0e0",
              lineHeight: "1.3"
            }}
          >
            <div>{firstSentence}</div>
            <div>{secondSentence}</div>
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px"
        }}
      >
        <button
          style={{
            background: "#fff",
            color: "#1976d2",
            border: "none",
            borderRadius: "5px",
            padding: "8px 16px",
            fontWeight: "bold",
            cursor: disabledAnalyze ? "not-allowed" : "pointer",
            opacity: disabledAnalyze ? 0.7 : 1
          }}
          onClick={onAnalyze}
          disabled={disabledAnalyze}
        >
          {t("analyze") || "Analysera"}
        </button>
        <button
          style={{
            background: "#fff",
            color: "#1976d2",
            border: "none",
            borderRadius: "5px",
            padding: "8px 16px",
            fontWeight: "bold",
            cursor: disabledExport ? "not-allowed" : "pointer",
            opacity: disabledExport ? 0.7 : 1
          }}
          onClick={onExportPdf}
          disabled={disabledExport}
        >
          {t("exportPdf") || "Exportera PDF-rapport"}
        </button>
      </div>
      <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", paddingRight: "8px", maxWidth: "35%" }}>
        {versionInfo && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              fontSize: "12px",
              color: "#e0e0e0"
            }}
          >
            <span>{t("version")} {versionInfo.version}</span>
            <span>{t("lastUpdated")} {versionInfo.formattedDate}</span>
          </div>
        )}
      </div>
    </div>
  );
}


function Draw3DMap() {
  // === GLOBAL SPRÅKHANTERING ===
  const { language, t, toggleLanguage } = useLanguage();
  const { user, isAuthenticated, isPaid, login, logout } = useUser();

  // === STATE ===
  const mapRef = useRef();
  const [features, setFeatures] = useState([]);
  const [modalFeature, setModalFeature] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [pendingImportedFeatures, setPendingImportedFeatures] = useState(null);
  const [dbSurfaceLayers, setDbSurfaceLayers] = useState([]);
  const [airports, setAirports] = useState([]);
  const [airportAddresses, setAirportAddresses] = useState([]);
  const fileInputRef = useRef(null);
  const [pdfMode, setPdfMode] = useState(false);
  const mapDivRef = useRef();
  const [navInputsData, setNavInputsData] = useState([]);
  const [waypointsData, setWaypointsData] = useState([]);
  const [tmaTia, setTmaTia] = useState([]);
  const [ctrTizRmz, setCtrTizRmz] = useState([]);
  const [basemap, setBasemap] = useState('osm');
  const [showLayerPanel, setShowLayerPanel] = useState(false);
  const [showSwedishAirports, setShowSwedishAirports] = useState(true);
  const [showNavaids, setShowNavaids] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const [showWaypoints, setShowWaypoints] = useState(false);
  const [showTmaTia, setShowTmaTia] = useState(false);
  const [showCtrTizRmz, setShowCtrTizRmz] = useState(false);
  const [showSurfaces, setShowSurfaces] = useState(false);
  const [showEasa, setShowEasa] = useState(false);
  const [showPansOps, setShowPansOps] = useState(false);
  const [showAnnex14, setShowAnnex14] = useState(false);
  const [showEtod, setShowEtod] = useState(false);
  const [showGeoJson, setShowGeoJson] = useState(false);
  const [showUserData, setShowUserData] = useState(true);
  const mapInputRef = useRef();
  const mapPenRef = useRef();

  // Version info state
  const [versionInfo, setVersionInfo] = useState(null);

  // Map state for optimized layer fetching
  const [mapCenter, setMapCenter] = useState({ lat: null, lon: null });
  const [mapZoom, setMapZoom] = useState(13);

  // Optimized surfaces fetching with caching and debouncing
  // Enabled when ANY surface layer is active OR when user can see Z-values in CoordinateInfoBox
  const anySurfaceLayerEnabled = showSurfaces || showEasa || showPansOps || showAnnex14 || showEtod;
  const surfacesFetchEnabled = anySurfaceLayerEnabled || (isAuthenticated && isPaid);
  
  const {
    data: surfacesData,
    loading: surfacesLoading,
    error: surfacesError,
    invalidateAndRefetch: reloadSurfaces
  } = useLayerData('surfaces', {
    enabled: surfacesFetchEnabled,
    centerLat: mapCenter.lat,
    centerLon: mapCenter.lon,
    radiusKm: 100,
    zoom: mapZoom,
    forAnalysis: false,
    debounceMs: 500
  });

  // Extract surfaces from data
  const surfaces = surfacesData?.features || [];

  // UI
  const [selectedAirport, setSelectedAirport] = useState('');
  const [showInformation, setShowInformation] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAboutUs, setShowAboutUs] = useState(false);
  const [uploadedGeoJson, setUploadedGeoJson] = useState(null);

  // PDF Export Modal
  const [showOrderModal, setShowOrderModal] = useState(false);

  // Authentication Modals
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);
  const [showLoginRequired, setShowLoginRequired] = useState(false);
  const [showFreeExportsExhausted, setShowFreeExportsExhausted] = useState(false);
  
  // Free export tracking
  const [exportStatus, setExportStatus] = useState(null); // Stores { canExport, remainingExports, exportCount }

  // Operation loading states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isAnalyzingOmni, setIsAnalyzingOmni] = useState(false);
  const [omniAnalysis, setOmniAnalysis] = useState(null);
  const [omniError, setOmniError] = useState(null);

  // OAS analysis state
  const [oasAnalysis, setOasAnalysis] = useState(null);
  const [oasError, setOasError] = useState(null);

  // BARO/VNAV OAS analysis state
  const [baroVnavAnalysis, setBaroVnavAnalysis] = useState(null);
  const [baroVnavError, setBaroVnavError] = useState(null);

  // VSS analysis state
  const [vssAnalysis, setVssAnalysis] = useState(null);
  const [vssError, setVssError] = useState(null);

  // HoldingBase analysis state
  const [holdingBaseAnalysis, setHoldingBaseAnalysis] = useState(null);
  const [holdingBaseError, setHoldingBaseError] = useState(null);

  // Shielding analysis state
  const [shieldingAnalysis, setShieldingAnalysis] = useState(null);
  const [shieldingError, setShieldingError] = useState(null);

  // MSA analysis state
  const [msaAnalysis, setMsaAnalysis] = useState(null);
  const [msaError, setMsaError] = useState(null);
  const [msaSortConfig, setMsaSortConfig] = useState({ key: 'ocaM', direction: 'desc' });
  const [showMsaSurfaces, setShowMsaSurfaces] = useState(false);

  // TAA sector state
  const [taaData, setTaaData] = useState(null);
  const [taaAnalysis, setTaaAnalysis] = useState(null);
  const [taaError, setTaaError] = useState(null);
  const [showTaaSectors, setShowTaaSectors] = useState(false);

  // Circling state
  const [circlingAnalysis, setCirclingAnalysis] = useState(null);
  const [circlingError, setCirclingError] = useState(null);
  const [showCirclingAreas, setShowCirclingAreas] = useState(false);
  const [showCirclingCats, setShowCirclingCats] = useState({ A: true, B: true, C: true, D: true });

  // GNSS Approach analysis state
  const [gnssApchAnalysis, setGnssApchAnalysis] = useState(null);
  const [gnssApchError, setGnssApchError] = useState(null);
  const [locAnalysis, setLocAnalysis] = useState(null);
  const [locError, setLocError] = useState(null);
  const [locLayerState, setLocLayerState] = useState(() => ({ ...LOC_LAYER_DEFAULTS, categories: {}, procedures: {} }));
  const [showGnssApchSurfaces, setShowGnssApchSurfaces] = useState(false);
  const [showGnssApchLayers, setShowGnssApchLayers] = useState({
    categories: {}, categoryOptions: {}, procedures: {},
  });

  // Debug: show computed surface polygons on the map (per analysis type)
  const [showOmniSurfaces, setShowOmniSurfaces] = useState(false);
  const [showVssSurfaces, setShowVssSurfaces] = useState(false);
  const [showBaroSurfaces, setShowBaroSurfaces] = useState(false);
  const [showNavSurfaces, setShowNavSurfaces] = useState(false);
  const [showHoldingBaseSurfaces, setShowHoldingBaseSurfaces] = useState(false);

  // Layer-panel per-type toggles (independent of the inline result buttons)
  const [layerOmni, setLayerOmni] = useState(false);
  const [layerOasIls1, setLayerOasIls1] = useState(false);
  const [layerOasIls2, setLayerOasIls2] = useState(false);
  const [layerOasIls2ap, setLayerOasIls2ap] = useState(false);
  const [layerOasSbasApv1, setLayerOasSbasApv1] = useState(false);
  const [layerOasSbasCat1, setLayerOasSbasCat1] = useState(false);
  const [layerBaro, setLayerBaro] = useState(false);
  const [layerVss, setLayerVss] = useState(false);
  const [layerBaseTurn, setLayerBaseTurn] = useState(false);

  // OMNI table sorting and selection state
  const [omniSortConfig, setOmniSortConfig] = useState({ key: 'pdgRequired', direction: 'desc' });
  const [selectedObstacles, setSelectedObstacles] = useState(new Set());

  // OAS table sorting state (default: highest OCA first)
  const [oasSortConfig, setOasSortConfig] = useState({ key: 'ocaM', direction: 'desc' });
  // OAS obstacle map selection state
  const [selectedOasObstacles, setSelectedOasObstacles] = useState(new Set());

  // BARO/VNAV table sorting and selection state
  const [baroSortConfig, setBaroSortConfig] = useState({ key: 'differenceM', direction: 'desc' });
  const [selectedBaroObstacles, setSelectedBaroObstacles] = useState(new Set());

  // VSS/OCS table sorting and selection state
  const [vssSortConfig, setVssSortConfig] = useState({ key: 'differenceM', direction: 'desc' });
  const [selectedVssObstacles, setSelectedVssObstacles] = useState(new Set());

  // HoldingBase table sorting and selection state
  const [holdingBaseSortConfig, setHoldingBaseSortConfig] = useState({ key: 'differenceM', direction: 'desc' });
  const [selectedHoldingBaseObstacles, setSelectedHoldingBaseObstacles] = useState(new Set());

  // HoldingRacetrack analysis state
  const [holdingRacetrackAnalysis, setHoldingRacetrackAnalysis] = useState(null);
  const [holdingRacetrackError, setHoldingRacetrackError] = useState(null);
  const [showHoldingRacetrackSurfaces, setShowHoldingRacetrackSurfaces] = useState(false);
  // Layer-panel per-type toggles (Holding vs Racetrack)
  const [showHoldingSurfaces, setShowHoldingSurfaces] = useState(false);
  const [showRacetrackSurfaces, setShowRacetrackSurfaces] = useState(false);
  const [holdingRacetrackSortConfig, setHoldingRacetrackSortConfig] = useState({ key: 'differenceM', direction: 'desc' });
  const [selectedHoldingRacetrackObstacles, setSelectedHoldingRacetrackObstacles] = useState(new Set());
  const [holdingDistSortConfig, setHoldingDistSortConfig] = useState({ key: 'differenceM', direction: 'desc' });
  const [selectedHoldingDistObstacles, setSelectedHoldingDistObstacles] = useState(new Set());
  const [showHoldingDistSurfaces, setShowHoldingDistSurfaces] = useState(false);

  // VOR/DME Holding (by distance) analysis state
  const [vorDmeHoldingAnalysis, setVorDmeHoldingAnalysis] = useState(null);
  const [vorDmeHoldingError, setVorDmeHoldingError] = useState(null);
  const [showVorDmeHoldingSurfaces, setShowVorDmeHoldingSurfaces] = useState(false);
  const [vorDmeHoldingSortConfig, setVorDmeHoldingSortConfig] = useState({ key: 'differenceM', direction: 'desc' });
  const [selectedVorDmeHoldingObstacles, setSelectedVorDmeHoldingObstacles] = useState(new Set());

  // RNAVHolding analysis state
  const [rnavHoldingAnalysis, setRnavHoldingAnalysis] = useState(null);
  const [rnavHoldingError, setRnavHoldingError] = useState(null);
  const [showRnavHoldingSurfaces, setShowRnavHoldingSurfaces] = useState(false);
  const [rnavHoldingSortConfig, setRnavHoldingSortConfig] = useState({ key: 'differenceM', direction: 'desc' });
  const [selectedRnavHoldingObstacles, setSelectedRnavHoldingObstacles] = useState(new Set());

  // RNPHolding analysis state
  const [rnpHoldingAnalysis, setRnpHoldingAnalysis] = useState(null);
  const [rnpHoldingError, setRnpHoldingError] = useState(null);
  const [showRnpHoldingSurfaces, setShowRnpHoldingSurfaces] = useState(false);
  const [rnpHoldingSortConfig, setRnpHoldingSortConfig] = useState({ key: 'differenceM', direction: 'desc' });
  const [selectedRnpHoldingObstacles, setSelectedRnpHoldingObstacles] = useState(new Set());

  // Shielding table sorting and selection state
  const [shieldingSortConfig, setShieldingSortConfig] = useState({ key: 'heightDeltaM', direction: 'desc' });
  const [selectedShieldingObstacles, setSelectedShieldingObstacles] = useState(new Set());

  // === LEAFLET DRAW LOCALIZATION ===
  useEffect(() => {
    // Configure Leaflet draw tooltips and controls to use i18n translations
    // This needs to run after leaflet-draw is loaded
    const configureDrawLocal = () => {
      if (window.L && window.L.drawLocal) {
        // Configure draw tooltips
        window.L.drawLocal.draw.toolbar.buttons = {
          polyline: t('tooltip_draw') || 'Draw a polyline',
          polygon: t('tooltip_draw') || 'Draw a polygon', 
          rectangle: t('tooltip_draw') || 'Draw a rectangle',
          circle: t('tooltip_draw') || 'Draw a circle',
          marker: t('tooltip_draw') || 'Draw a marker',
          circlemarker: t('tooltip_draw') || 'Draw a circlemarker'
        };

        // Configure edit tooltips
        window.L.drawLocal.edit.toolbar.buttons = {
          edit: t('tooltip_edit') || 'Edit layers',
          editDisabled: t('tooltip_editDisabled') || 'No layers to edit',
          remove: t('tooltip_delete') || 'Delete layers',
          removeDisabled: t('tooltip_deleteDisabled') || 'No layers to delete'
        };

        // Configure draw handlers
        window.L.drawLocal.draw.handlers.polyline = {
          tooltip: {
            start: t('tooltip_drawPolylineStart') || 'Click to start drawing line',
            cont: t('tooltip_drawPolylineCont') || 'Click to continue drawing line',
            end: t('tooltip_drawPolylineEnd') || 'Click last point to finish line'
          }
        };

        window.L.drawLocal.draw.handlers.polygon = {
          tooltip: {
            start: t('tooltip_drawPolygonStart') || 'Click to start drawing shape',
            cont: t('tooltip_drawPolygonCont') || 'Click to continue drawing shape', 
            end: t('tooltip_drawPolygonEnd') || 'Click first point to close this shape'
          }
        };

        window.L.drawLocal.draw.handlers.marker = {
          tooltip: {
            start: t('tooltip_drawMarker') || 'Click map to place marker'
          }
        };

        window.L.drawLocal.draw.handlers.circle = {
          tooltip: {
            start: t('tooltip_drawCircleStart') || 'Click and drag to draw circle',
            radius: t('tooltip_drawCircleRadius') || 'Radius'
          }
        };
      }
    };

    // Try to configure immediately
    configureDrawLocal();

    // Also configure after a brief delay to ensure leaflet-draw is loaded
    const timer = setTimeout(configureDrawLocal, 100);

    return () => clearTimeout(timer);
  }, [t, language]);

  // HANDLERS
  const handleAirportChange = (e) => {
    const airportName = e.target.value;
    setSelectedAirport(airportName);
    const airport = airports.find(a => a.airport_name === airportName);
    if (airport && airport.arp_latitude && airport.arp_longitude) {
      const map = mapRef.current;
      if (map && map.setView) {
        map.setView([parseFloat(airport.arp_latitude), parseFloat(airport.arp_longitude)], 10);
      }
    }
  };

  // === DATA FETCHING ===
  // Note: API calls use REACT_APP_API_BASE_URL environment variable
  // Development: Set to 'http://localhost:5000/api' in .env.development
  // Production: Set to '/api' in .env.production
  useEffect(() => {
    apiFetch("/airport_addresses")
      .then(res => res.json())
      .then(data => setAirportAddresses(data))
      .catch(() => setAirportAddresses([]));
  }, []);

  // Surfaces are now fetched using the optimized useLayerData hook above
  // The old surface fetching code has been removed
  
  // Handle surfaces error from the hook
  useEffect(() => {
    if (surfacesError) {
      setError(t("errorFetchingSurfaces") + ": " + surfacesError);
    }
  }, [surfacesError, t]);

  // Handler for map state changes (center and zoom)
  const handleMapStateChange = useCallback(({ center, zoom }) => {
    setMapCenter(center);
    setMapZoom(zoom);
  }, []);

  useEffect(() => {
    apiFetch("/airports")
      .then(res => res.json())
      .then(data => setAirports(data))
      .catch(err => setError(t("errorFetchingAirports") + ": " + err.message));
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiFetch("/api/nav-inputs-map")
      .then(res => res.json())
      .then(data => setNavInputsData(Array.isArray(data.features) ? data.features : []))
      .catch(err => console.warn("nav-inputs-map fetch error:", err.message));
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiFetch("/api/wpt-inputs-map")
      .then(res => res.json())
      .then(data => setWaypointsData(Array.isArray(data.features) ? data.features : []))
      .catch(err => console.warn("wpt-inputs-map fetch error:", err.message));
  }, [isAuthenticated]);

  // Fetch version information on mount
  useEffect(() => {
    const getLocale = () => language === 'sv' ? 'sv-SE' : 'en-US';
    
    apiFetch("/api/version")
      .then(res => res.json())
      .then(data => {
        const date = new Date(data.updated_at);
        const formattedDate = date.toLocaleDateString(getLocale());
        setVersionInfo({
          version: data.version,
          formattedDate: formattedDate
        });
      })
      .catch(err => {
        console.error("Error fetching version:", err);
        // Set default version on error
        setVersionInfo({
          version: "1.0",
          formattedDate: new Date().toLocaleDateString(getLocale())
        });
      });
  }, [language]);

  useEffect(() => {
    apiFetch("/tma_tia_airspace")
      .then(res => res.json())
      .then(data => setTmaTia(data.features))
      .catch(err => setError(t("errorFetchingTmaTia") + ": " + err.message));
  }, [t]);

  useEffect(() => {
    apiFetch("/ats_airspace")
      .then(res => res.json())
      .then(data => setCtrTizRmz(data.features))
      .catch(err => setError(t("errorFetchingCtrTizRmz") + ": " + err.message));
  }, [t]);
  
    // Hjälpfunktion: Konvertera GeoJSON till features-array
  // Förbättrad för att tilldela unika circleId till alla importerade cirklar
  function geoJsonToFeaturesLocal(geojson) {
    if (!geojson || !geojson.features) return [];
    
    // Säkerställ unika circle-ID:n med timestamp och slumptal
    let circleCounter = Date.now() * 1000 + Math.floor(Math.random() * 1000);
    
    return geojson.features.map((feat, i) => {
      if (!feat.geometry) return null;
      
      let geometry, properties = { ...(feat.properties || {}) };
      
      // Hantera cirklar - tilldela unik circleId om det saknas
      if (
        (feat.geometry.type === "Point" && properties.radius && (properties._originalType === "Circle" || properties.radius > 0))
      ) {
        // Om circleId saknas, tilldela ett unikt ID
        if (properties.circleId === undefined) {
          properties.circleId = circleCounter++;
        }
        
        geometry = {
          type: "Circle",
          center: [feat.geometry.coordinates[0], feat.geometry.coordinates[1]],
          radius: Number(properties.radius),
          z: Number(properties.z ?? (feat.geometry.coordinates[2] ?? getDefaultZ()))
        };
      } else if (feat.geometry.type === "Point") {
        geometry = {
          ...feat.geometry,
          coordinates: [
            feat.geometry.coordinates[0],
            feat.geometry.coordinates[1],
            feat.geometry.coordinates[2] ?? Number(properties.z ?? getDefaultZ())
          ]
        };
      } else if (feat.geometry.type === "LineString") {
        geometry = {
          ...feat.geometry,
          coordinates: feat.geometry.coordinates.map(c =>
            c.length > 2
              ? c
              : [c[0], c[1], Number(properties.z ?? getDefaultZ())]
          )
        };
      } else if (feat.geometry.type === "Polygon") {
        geometry = {
          ...feat.geometry,
          coordinates: feat.geometry.coordinates.map(ring =>
            ring.map(c => {
              if (c.length > 2 && typeof c[2] === "number") return c;
              const fallbackZ =
                ring.find(pt => Array.isArray(pt) && pt.length > 2 && typeof pt[2] === "number")?.[2] ??
                Number(properties.z) ??
                getDefaultZ();
              return [c[0], c[1], fallbackZ];
            })
          )
        };
      } else {
        return null;
      }
      
      return {
        geometry,
        properties,
        id: ++uniqueId
      };
    }).filter(Boolean);
  }

  // GeoJSON upload handler (används för både header och kartan)
  const handleGeoJsonUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const geojson = JSON.parse(e.target.result);
        const importedFeatures = geoJsonToFeaturesLocal(geojson);
        setUploadedGeoJson(geojson);
        if (features.length > 0) {
          setPendingImportedFeatures(importedFeatures);
        } else {
          setFeatures(importedFeatures);
          setPendingImportedFeatures(null);
        }
      } catch (error) {
        setError(t("geojsonParseError") + ": " + error.message);
      }
    };
    reader.readAsText(file);
  };

  const handleGeoJsonDownload = async () => {
    if (!features || features.length === 0) {
      alert(t("noObjectsToExport"));
      return;
    }
    const analyzable = getAnalyzableFeatures(features);
    if (analyzable.length === 0) {
      alert(t("onlyDbSurfacesDrawSomething"));
      return;
    }

    const geojsonObj = featuresToGeoJSON(analyzable);
    const geojsonData = JSON.stringify(geojsonObj, null, 2);
    const defaultFileName = "drawn-3d-features.geojson";

    if (window.showSaveFilePicker) {
      try {
        const options = {
          suggestedName: defaultFileName,
          types: [{
            description: "GeoJSON Files",
            accept: { "application/geo+json": [".geojson", ".json"] }
          }]
        };
        const handle = await window.showSaveFilePicker(options);
        const writable = await handle.createWritable();
        await writable.write(geojsonData);
        await writable.close();
        return;
      } catch (e) {
        if (e.name !== "AbortError") {
          alert(t("couldNotSaveFile") + ": " + e.message);
        }
        // Faller tillbaka till vanlig nedladdning om användaren avbryter eller det blir fel
      }
    }

    // Fallback: vanlig nedladdning (alla webbläsare)
    const blob = new Blob([geojsonData], { type: "application/geo+json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = defaultFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  function getAnalyzableFeatures(features) {
    return features.filter(f => !(f.properties && f.properties.isSurfaceFromDb));
  }

  // Hjälpfunktion: zooma till objekten (features) innan snapshot
  function fitMapToFeaturesAndWait() {
    return new Promise(resolve => {
      const map = mapDivRef.current?.querySelector?.('.leaflet-container')?._leaflet_map;
      if (!map || !features.length) return resolve();
      let lats = [], lngs = [];
      features.forEach(f => {
        if (f.geometry.type === "Point") {
          lats.push(f.geometry.coordinates[1]);
          lngs.push(f.geometry.coordinates[0]);
        } else if (f.geometry.type === "LineString") {
          f.geometry.coordinates.forEach(([lng, lat]) => { lats.push(lat); lngs.push(lng); });
        } else if (f.geometry.type === "Polygon") {
          f.geometry.coordinates[0].forEach(([lng, lat]) => { lats.push(lat); lngs.push(lng); });
        } else if (f.geometry.type === "Circle") {
          lats.push(f.geometry.center[1]);
          lngs.push(f.geometry.center[0]);
        }
      });
      if (!lats.length) return resolve();
      const southWest = L.latLng(Math.min(...lats), Math.min(...lngs));
      const northEast = L.latLng(Math.max(...lats), Math.max(...lngs));
      const bounds = L.latLngBounds(southWest, northEast).pad(0.2);
      map.once('moveend', () => setTimeout(resolve, 350));
      map.fitBounds(bounds);
    });
  }
  
    async function exportGeoJSON() {
    const geojson = {
      type: "FeatureCollection",
      features: features.map(f => {
        let geometry, properties = { ...(f.properties || {}) };
        if (f.geometry.type === "Circle") {
          geometry = {
            type: "Point",
            coordinates: [f.geometry.center[0], f.geometry.center[1], f.geometry.z]
          };
          properties.radius = f.geometry.radius;
          properties.z = f.geometry.z;
          properties._originalType = "Circle";
        } else if (
          f.geometry.type === "Point" ||
          f.geometry.type === "LineString" ||
          f.geometry.type === "Polygon"
        ) {
          geometry = f.geometry;
        }
        return {
          type: "Feature",
          geometry,
          properties
        };
      })
    };

    if (window.showSaveFilePicker) {
      try {
        const options = {
          suggestedName: "drawn-3d-features.geojson",
          types: [{
            description: "GeoJSON Files",
            accept: { "application/geo+json": [".geojson", ".json"] }
          }]
        };
        const handle = await window.showSaveFilePicker(options);
        const writable = await handle.createWritable();
        await writable.write(JSON.stringify(geojson, null, 2));
        await writable.close();
        return;
      } catch (e) {
        if (e.name !== "AbortError") {
          setError(t("couldNotSaveFile") + ": " + e.message);
        }
        return;
      }
    }

    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "drawn-3d-features.geojson";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function importGeoJSONFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const geojson = JSON.parse(ev.target.result);
        if (!geojson.features) throw new Error(t("invalidGeoJsonFormat"));
        
        // Säkerställ unika circle-ID:n med timestamp och slumptal
        let circleCounter = Date.now() * 1000 + Math.floor(Math.random() * 1000);
        
        const imported = geojson.features.map((feat, i) => {
          if (!feat.geometry) return null;
          let geometry, properties = { ...(feat.properties || {}) };
          
          if (
            (feat.geometry.type === "Point" && properties.radius && (properties._originalType === "Circle" || properties.radius > 0))
          ) {
            // Om circleId saknas, tilldela ett unikt ID
            if (properties.circleId === undefined) {
              properties.circleId = circleCounter++;
            }
            
            geometry = {
              type: "Circle",
              center: [feat.geometry.coordinates[0], feat.geometry.coordinates[1]],
              radius: Number(properties.radius),
              z: Number(properties.z ?? (feat.geometry.coordinates[2] ?? getDefaultZ()))
            };
          } else if (feat.geometry.type === "Point") {
            geometry = {
              ...feat.geometry,
              coordinates: [
                feat.geometry.coordinates[0],
                feat.geometry.coordinates[1],
                feat.geometry.coordinates[2] ?? Number(properties.z ?? getDefaultZ())
              ]
            };
          } else if (feat.geometry.type === "LineString") {
            geometry = {
              ...feat.geometry,
              coordinates: feat.geometry.coordinates.map(c =>
                c.length > 2
                  ? c
                  : [c[0], c[1], Number(properties.z ?? getDefaultZ())]
              )
            };
          } else if (feat.geometry.type === "Polygon") {
            geometry = {
              ...feat.geometry,
              coordinates: feat.geometry.coordinates.map(ring =>
                ring.map(c => {
                  if (c.length > 2 && typeof c[2] === "number") return c;
                  const fallbackZ =
                    ring.find(pt => Array.isArray(pt) && pt.length > 2 && typeof pt[2] === "number")?.[2] ??
                    Number(properties.z) ??
                    getDefaultZ();
                  return [c[0], c[1], fallbackZ];
                })
              )
            };
          } else {
            return null;
          }
          return {
            geometry,
            properties,
            id: ++uniqueId
          };
        }).filter(Boolean);
        setPendingImportedFeatures(imported);
      } catch (e) {
        setError(t("importError") + ": " + e.message);
      }
    };
    reader.readAsText(file);
  }

  function openImportDialog() {
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  }

  function onCreated(e) {
    const { layerType, layer } = e;
    let geometry, properties = {};
    if (layerType === "marker") {
      const { lat, lng } = layer.getLatLng();
      geometry = { type: "Point", coordinates: [lng, lat, getDefaultZ()] };
    } else if (layerType === "polyline") {
      const latlngs = layer.getLatLngs();
      geometry = {
        type: "LineString",
        coordinates: latlngs.map(({ lat, lng }) => [lng, lat, getDefaultZ()])
      };
    } else if (layerType === "circle") {
      const { lat, lng } = layer.getLatLng();
      const radius = layer.getRadius();
      geometry = {
        type: "Circle",
        center: [lng, lat],
        radius: radius,
        z: getDefaultZ()
      };
      // Tilldela unikt circleId för nyritade cirklar med förbättrad uniqueness
      properties.circleId = Date.now() * 1000 + Math.floor(Math.random() * 1000);
    } else if (layerType === "polygon") {
      const latlngs = layer.getLatLngs()[0];
      geometry = {
        type: "Polygon",
        coordinates: [
          latlngs.map(({ lat, lng }) => [lng, lat, getDefaultZ()])
        ]
      };
    } else {
      alert(t("drawInstruction"));
      return;
    }
    const id = ++uniqueId;
    setModalFeature({ geometry, properties, id });
    if (layer._map && layer.remove) layer.remove();
  }

  function handleDeleted(e) {
    const idsToDelete = [];
    e.layers.eachLayer(layer => {
      if (layer.options && layer.options.featureId) {
        idsToDelete.push(layer.options.featureId);
      }
    });
    setFeatures(prev => prev.filter(f => !idsToDelete.includes(f.id)));
  }

  function handleSaveProperties({ name, z, radius }) {
    let { geometry, id } = modalFeature;
    let properties = { name };
    if (geometry.type === "Point") {
      geometry = { ...geometry, coordinates: [geometry.coordinates[0], geometry.coordinates[1], Number(z)] };
    } else if (geometry.type === "LineString") {
      geometry = { ...geometry, coordinates: geometry.coordinates.map((c, i) => [c[0], c[1], Array.isArray(z) ? Number(z[i]) : Number(z)]) };
    } else if (geometry.type === "Circle") {
      geometry = {
        ...geometry,
        z: Number(z),
        radius: Math.ceil(Number(radius ?? geometry.radius))
      };
    } else if (geometry.type === "Polygon") {
      geometry = {
        ...geometry,
        coordinates: geometry.coordinates.map(ring =>
          ring.map((c, i) => [c[0], c[1], Array.isArray(z) ? Number(z[i]) : Number(z)])
        )
      };
    }
    setFeatures(prev => [...prev, { geometry, properties, id }]);
    setModalFeature(null);
  }

  function handleCancelProperties() {
    setModalFeature(null);
  }

  // PDF Export handlers
  async function handleExportPdf() {
    // Capture user gesture timestamp immediately for PDF save robustness
    const userGestureTs = Date.now();
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      setShowSubscriptionWarning(true);
      return;
    }
    
    // Paid users can export without limits
    if (isPaid || user?.betald === 'JA') {
      setIsGeneratingPdf(true);
      try {
        await exportPenetrationTablesToPDF({
          t,
          showSurfaces, setShowSurfaces,
          showEasa, setShowEasa,
          showPansOps, setShowPansOps,
          showAnnex14, setShowAnnex14,
          showEtod, setShowEtod,
          showGeoJson, setShowGeoJson,
          showUserData, setShowUserData,
          showSwedishAirports, setShowSwedishAirports,
          showNavaids, setShowNavaids,
          showNav, setShowNav,
          showTmaTia, setShowTmaTia,
          showCtrTizRmz, setShowCtrTizRmz,
          setPdfMode,
          analysis,
          omniAnalysis,
          oasAnalysis,
          baroVnavAnalysis,
          vssAnalysis,
          shieldingAnalysis,
          holdingBaseAnalysis,
          holdingRacetrackAnalysis,
          rnavHoldingAnalysis,
          rnpHoldingAnalysis,
          msaAnalysis,
          taaAnalysis,
          circlingAnalysis,
          gnssApchAnalysis,
          locAnalysis,
          features,
          mapDivRef,
          fitMapToFeaturesAndWait,
          airportAddresses,
          userGestureTs,
          user
        });

        // After successful PDF export for paid users, increment the counter
        try {
          const incrementResponse = await fetch('/api/pdf-exports/increment', {
            method: 'POST',
            credentials: 'include'
          });
          
          if (incrementResponse.ok) {
            await incrementResponse.json();
          } else {
            console.error('Failed to increment export count:', await incrementResponse.text());
          }
        } catch (incrementError) {
          // Log but don't fail the export if tracking fails
          console.error('Error tracking export for paid user:', incrementError);
        }
      } catch (error) {
        console.error('PDF export error:', error);
        setError(t("errorExportingPdf") + ": " + error.message);
      } finally {
        setIsGeneratingPdf(false);
      }
      return;
    }
    
    // Unpaid users must go through subscription warning modal first
    setShowSubscriptionWarning(true);
  }

  async function handleOrderConfirm(fields, isCompany) {
    // Capture user gesture timestamp before async operations
    const userGestureTs = Date.now();
    
    try {
      // Always save order to backend (even if export limit reached)
      await saveOrderToBackend({ ...fields, isCompany, acceptedTerms: true });
      setShowOrderModal(false);
      
      // Check export limits for unpaid users before generating PDF
      if (!isPaid && user?.betald !== 'JA') {
        const response = await fetch('/api/pdf-exports/check', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (!data.canExport) {
            // User has exhausted their free exports
            setShowFreeExportsExhausted(true);
            return;
          }
        }
      }
      
      // Generate PDF
      setIsGeneratingPdf(true);
      try {
        await exportPenetrationTablesToPDF({
          t,
          showSurfaces, setShowSurfaces,
          showEasa, setShowEasa,
          showPansOps, setShowPansOps,
          showAnnex14, setShowAnnex14,
          showEtod, setShowEtod,
          showGeoJson, setShowGeoJson,
          showUserData, setShowUserData,
          showSwedishAirports, setShowSwedishAirports,
          showNavaids, setShowNavaids,
          showNav, setShowNav,
          showTmaTia, setShowTmaTia,
          showCtrTizRmz, setShowCtrTizRmz,
          setPdfMode,
          analysis,
          omniAnalysis,
          oasAnalysis,
          baroVnavAnalysis,
          vssAnalysis,
          shieldingAnalysis,
          holdingBaseAnalysis,
          holdingRacetrackAnalysis,
          rnavHoldingAnalysis,
          rnpHoldingAnalysis,
          msaAnalysis,
          taaAnalysis,
          circlingAnalysis,
          gnssApchAnalysis,
          locAnalysis,
          features,
          mapDivRef,
          fitMapToFeaturesAndWait,
          airportAddresses,
          userGestureTs,
          user
        });
        
        // After successful PDF export for unpaid users, increment the counter
        if (!isPaid && user?.betald !== 'JA') {
          const incrementResponse = await fetch('/api/pdf-exports/increment', {
            method: 'POST',
            credentials: 'include'
          });
          
          if (incrementResponse.ok) {
            const incrementData = await incrementResponse.json();
            
            // Update local export status
            setExportStatus({
              canExport: incrementData.remainingExports > 0,
              remainingExports: incrementData.remainingExports,
              exportCount: incrementData.exportCount
            });
            
            // Show notification if user has limited exports remaining
            if (incrementData.remainingExports > 0 && incrementData.remainingExports <= 2) {
              alert(t('freeExportsRemaining', { count: incrementData.remainingExports }));
            }
          }
        }
      } finally {
        setIsGeneratingPdf(false);
      }
    } catch (error) {
      console.error('Error in handleOrderConfirm:', error);
      setError(t("errorExportingPdf") + ": " + error.message);
    }
  }

  function handleOrderCancel() {
    setShowOrderModal(false);
  }

  // Authentication handlers
  const handleLoginSuccess = (userData) => {
    // Update UserContext with logged in user data
    login(userData);
    setShowLoginModal(false);
  };

  const handleRegisterSuccess = (userData) => {
    // If user data is provided, log them in automatically
    if (userData) {
      login(userData);
    } else {
      alert(t('registrationSuccess'));
      setShowLoginModal(true);
    }
    setShowRegisterModal(false);
  };

  const handleLogout = async () => {
    await logout();
  };

  // Subscription warning modal handlers
  const handleSubscriptionLogin = () => {
    setShowSubscriptionWarning(false);
    setShowLoginModal(true);
  };

  const handleSubscriptionRegister = () => {
    setShowSubscriptionWarning(false);
    setShowRegisterModal(true);
  };

  const handleContinueWithoutRegistration = () => {
    setShowSubscriptionWarning(false);
    // Only registered users can order flight obstacle analysis
    // Unauthenticated users must log in first to ensure proper order tracking
    if (!isAuthenticated) {
      setShowLoginRequired(true);
    } else {
      setShowOrderModal(true);
    }
  };

  const handleContactUs = () => {
    // Open email client or show contact information
    window.open('mailto:info@flightsafe.se?subject=Prenumeration%20-%20OLS%20App', '_blank');
  };

  // Login required modal handlers
  const handleLoginRequiredLogin = () => {
    setShowLoginRequired(false);
    setShowLoginModal(true);
  };

  const handleLoginRequiredRegister = () => {
    setShowLoginRequired(false);
    setShowRegisterModal(true);
  };
  
  // Analysera geometrier - förbättrad för att bevara circleId för cirklar
  async function analyzeGeometries() {
    const analyzable = getAnalyzableFeatures(features);
    if (!analyzable.length) return;
    setError(null);
    setAnalysis(null);
    setOmniAnalysis(null);
    setOmniError(null);
    setOasAnalysis(null);
    setOasError(null);
    setShieldingAnalysis(null);
    setShieldingError(null);
    setMsaAnalysis(null);
    setMsaError(null);
    setIsAnalyzing(true);
    setIsAnalyzingOmni(true);
    setLocAnalysis(null);
    setLocError(null);

    const payload = {
      geometries: analyzable.map(f => {
        if (f.geometry.type === "Circle") {
          const [lng, lat] = f.geometry.center;
          const circleProperties = { ...f.properties };
          if (circleProperties.circleId === undefined) {
            circleProperties.circleId = f.id || `${lng}_${lat}_${f.geometry.radius}`.replace(/\./g, '_');
          }
          // Send as a single center Point with horizontal_tolerance = radius
          // so the result table shows one row per surface, not many polygon-edge points
          return {
            geometry: {
              type: "Point",
              coordinates: [lng, lat, f.geometry.z ?? getDefaultZ()]
            },
            properties: {
              ...circleProperties,
              horizontal_tolerance: f.geometry.radius || 0
            }
          };
        } else {
          return {
            geometry: f.geometry,
            properties: f.properties
          };
        }
      })
    };

    try {
      // Run all analyses in parallel
      const [analysisRes, omniRes, oasRes, baroVnavRes, vssRes, holdingBaseRes, holdingRacetrackRes, vorDmeHoldingRes, rnavHoldingRes, rnpHoldingRes, shieldingRes, msaRes, taaRes, circlingRes, gnssApchRes, locRes] = await Promise.all([
        // Regular analysis
        apiFetch("/api/analyze-z-penetration", {
          method: "POST",
          body: payload
        }),
        // OMNI analysis
        apiFetch("/api/omni-departure", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[OMNI] Error during analysis:", err);
          setOmniError(err.message);
          return null; // Return null so regular analysis can continue
        }),
        // OAS analysis
        apiFetch("/api/oas-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[OAS] Error during analysis:", err);
          setOasError(err.message);
          return null;
        }),
        // BARO/VNAV OAS analysis
        apiFetch("/api/baro-vnav-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[BARO/VNAV] Error during analysis:", err);
          setBaroVnavError(err.message);
          return null;
        }),
        // VSS analysis
        apiFetch("/api/vss-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[VSS] Error during analysis:", err);
          setVssError(err.message);
          return null;
        }),
        // HoldingBase analysis
        apiFetch("/api/holding-base-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[HoldingBase] Error during analysis:", err);
          setHoldingBaseError(err.message);
          return null;
        }),
        // HoldingRacetrack (by time) analysis
        apiFetch("/api/holding-racetrack-bytime-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[HoldingRacetrack] Error during analysis:", err);
          setHoldingRacetrackError(err.message);
          return null;
        }),
        // VOR/DME Holding (by distance) analysis
        apiFetch("/api/vor-dme-holding-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[VorDmeHolding] Error during analysis:", err);
          setVorDmeHoldingError(err.message);
          return null;
        }),
        // RNAV Holding analysis
        apiFetch("/api/rnav-holding-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[RNAVHolding] Error during analysis:", err);
          setRnavHoldingError(err.message);
          return null;
        }),
        // RNP Holding analysis
        apiFetch("/api/rnp-holding-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[RNPHolding] Error during analysis:", err);
          setRnpHoldingError(err.message);
          return null;
        }),
        // Shielding analysis
        apiFetch("/api/shielding-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[Shielding] Error during analysis:", err);
          setShieldingError(err.message);
          return null;
        }),
        // MSA analysis
        apiFetch("/api/msa-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[MSA] Error during analysis:", err);
          setMsaError(err.message);
          return null;
        }),
        // TAA calculation (with obstacle evaluation + 5 NM buffer)
        apiFetch("/api/taa-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[TAA] Error during calculation:", err);
          setTaaError(err.message);
          return null;
        }),
        // Circling calculation
        apiFetch("/api/circling-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable) }
        }).catch(err => {
          console.error("[Circling] Error during calculation:", err);
          setCirclingError(err.message);
          return null;
        }),
        // GNSS Approach calculation
        apiFetch("/api/gnss-apch-calculation", {
          method: "POST",
          body: { obstacles: convertFeaturesToObstacles(analyzable, { includeIdentifiers: true }) }
        }).catch(err => {
          console.error("[GNSS-APCH] Error during calculation:", err);
          setGnssApchError(err.message);
          return null;
        }),
        apiFetch('/api/loc-approach-calculation', {
          method: 'POST', body: { obstacles: convertFeaturesToObstacles(analyzable, { includeIdentifiers: true }) },
        }).catch(err => { setLocError(err.message); return null; }),
      ]);

      // Process regular analysis
      if (!analysisRes.ok) {
        const text = await analysisRes.text();
        throw new Error(t("serverError") + ": " + analysisRes.status + " " + text);
      }
      const analysisData = await analysisRes.json();
      setAnalysis(analysisData.results || []);

      // Process OMNI analysis
      if (omniRes && omniRes.ok) {
        const omniData = await omniRes.json();
        setOmniAnalysis(omniData);
      }

      // Process OAS analysis
      if (oasRes && oasRes.ok) {
        const oasData = await oasRes.json();
        setOasAnalysis(oasData);
      } else if (oasRes && !oasRes.ok) {
        const errData = await oasRes.json().catch(() => ({ error: 'OAS calculation failed' }));
        const msg = errData.details || errData.error || `OAS server error ${oasRes.status}`;
        console.error("[OAS] Server error:", msg);
        setOasError(msg);
      }

      // Process BARO/VNAV OAS analysis
      if (baroVnavRes && baroVnavRes.ok) {
        const baroVnavData = await baroVnavRes.json();
        setBaroVnavAnalysis(baroVnavData);
      } else if (baroVnavRes && !baroVnavRes.ok) {
        const errData = await baroVnavRes.json().catch(() => ({ error: 'BARO/VNAV calculation failed' }));
        const msg = errData.details || errData.error || `BARO/VNAV server error ${baroVnavRes.status}`;
        console.error("[BARO/VNAV] Server error:", msg);
        setBaroVnavError(msg);
      }

      // Process VSS analysis
      if (vssRes && vssRes.ok) {
        const vssData = await vssRes.json();
        setVssAnalysis(vssData);
      } else if (vssRes && !vssRes.ok) {
        const errData = await vssRes.json().catch(() => ({ error: 'VSS calculation failed' }));
        const msg = errData.details || errData.error || `VSS server error ${vssRes.status}`;
        console.error("[VSS] Server error:", msg);
        setVssError(msg);
      }

      // Process HoldingBase analysis
      if (holdingBaseRes && holdingBaseRes.ok) {
        const holdingBaseData = await holdingBaseRes.json();
        setHoldingBaseAnalysis(holdingBaseData);
      } else if (holdingBaseRes && !holdingBaseRes.ok) {
        const errData = await holdingBaseRes.json().catch(() => ({ error: 'HoldingBase calculation failed' }));
        const msg = errData.details || errData.error || `HoldingBase server error ${holdingBaseRes.status}`;
        console.error("[HoldingBase] Server error:", msg);
        setHoldingBaseError(msg);
      }

      // Process HoldingRacetrack analysis
      if (holdingRacetrackRes && holdingRacetrackRes.ok) {
        const holdingRacetrackData = await holdingRacetrackRes.json();
        setHoldingRacetrackAnalysis(holdingRacetrackData);
      } else if (holdingRacetrackRes && !holdingRacetrackRes.ok) {
        const errData = await holdingRacetrackRes.json().catch(() => ({ error: 'HoldingRacetrack calculation failed' }));
        const msg = errData.details || errData.error || `HoldingRacetrack server error ${holdingRacetrackRes.status}`;
        console.error("[HoldingRacetrack] Server error:", msg);
        setHoldingRacetrackError(msg);
      }

      // Process VOR/DME Holding (by distance) analysis
      if (vorDmeHoldingRes && vorDmeHoldingRes.ok) {
        const vorDmeHoldingData = await vorDmeHoldingRes.json();
        setVorDmeHoldingAnalysis(vorDmeHoldingData);
      } else if (vorDmeHoldingRes && !vorDmeHoldingRes.ok) {
        const errData = await vorDmeHoldingRes.json().catch(() => ({ error: 'VOR/DME Holding calculation failed' }));
        const msg = errData.details || errData.error || `VOR/DME Holding server error ${vorDmeHoldingRes.status}`;
        console.error("[VorDmeHolding] Server error:", msg);
        setVorDmeHoldingError(msg);
      }

      // Process RNAV Holding analysis
      if (rnavHoldingRes && rnavHoldingRes.ok) {
        const rnavHoldingData = await rnavHoldingRes.json();
        setRnavHoldingAnalysis(rnavHoldingData);
      } else if (rnavHoldingRes && !rnavHoldingRes.ok) {
        const errData = await rnavHoldingRes.json().catch(() => ({ error: 'RNAV Holding calculation failed' }));
        const msg = errData.details || errData.error || `RNAV Holding server error ${rnavHoldingRes.status}`;
        console.error("[RNAVHolding] Server error:", msg);
        setRnavHoldingError(msg);
      }

      // Process RNP Holding analysis
      if (rnpHoldingRes && rnpHoldingRes.ok) {
        const rnpHoldingData = await rnpHoldingRes.json();
        setRnpHoldingAnalysis(rnpHoldingData);
      } else if (rnpHoldingRes && !rnpHoldingRes.ok) {
        const errData = await rnpHoldingRes.json().catch(() => ({ error: 'RNP Holding calculation failed' }));
        const msg = errData.details || errData.error || `RNP Holding server error ${rnpHoldingRes.status}`;
        console.error("[RNPHolding] Server error:", msg);
        setRnpHoldingError(msg);
      }

      // Process Shielding analysis
      if (shieldingRes && shieldingRes.ok) {
        const shieldingData = await shieldingRes.json();
        setShieldingAnalysis(shieldingData);
      } else if (shieldingRes && !shieldingRes.ok) {
        const errData = await shieldingRes.json().catch(() => ({ error: 'Shielding calculation failed' }));
        const msg = errData.details || errData.error || `Shielding server error ${shieldingRes.status}`;
        console.error("[Shielding] Server error:", msg);
        setShieldingError(msg);
      }

      // Process MSA analysis
      if (msaRes && msaRes.ok) {
        const msaData = await msaRes.json();
        setMsaAnalysis(msaData);
      } else if (msaRes && !msaRes.ok) {
        const errData = await msaRes.json().catch(() => ({ error: 'MSA calculation failed' }));
        const msg = errData.details || errData.error || `MSA server error ${msaRes.status}`;
        console.error("[MSA] Server error:", msg);
        setMsaError(msg);
      }

      // Process TAA calculation
      if (taaRes && taaRes.ok) {
        const taaJson = await taaRes.json();
        setTaaAnalysis(taaJson);
        // Also populate taaData so the map overlay can use precomputed polygons
        setTaaData(taaJson);
      } else if (taaRes && !taaRes.ok) {
        const errData = await taaRes.json().catch(() => ({ error: 'TAA calculation failed' }));
        const msg = errData.details || errData.error || `TAA server error ${taaRes.status}`;
        console.error("[TAA] Server error:", msg);
        setTaaError(msg);
      }

      // Process Circling calculation
      if (circlingRes && circlingRes.ok) {
        const circlingJson = await circlingRes.json();
        setCirclingAnalysis(circlingJson);
      } else if (circlingRes && !circlingRes.ok) {
        const errData = await circlingRes.json().catch(() => ({ error: 'Circling calculation failed' }));
        const msg = errData.details || errData.error || `Circling server error ${circlingRes.status}`;
        console.error("[Circling] Server error:", msg);
        setCirclingError(msg);
      }

      // Process GNSS Approach calculation
      if (gnssApchRes && gnssApchRes.ok) {
        const gnssApchJson = await gnssApchRes.json();
        setGnssApchAnalysis(gnssApchJson);
      } else if (gnssApchRes && !gnssApchRes.ok) {
        const errData = await gnssApchRes.json().catch(() => ({ error: 'GNSS Approach calculation failed' }));
        const msg = errData.details || errData.error || `GNSS Approach server error ${gnssApchRes.status}`;
        console.error("[GNSS-APCH] Server error:", msg);
        setGnssApchError(msg);
      }
      if (locRes?.ok) setLocAnalysis(await locRes.json());
      else if (locRes) {
        const data = await locRes.json().catch(() => ({}));
        setLocError(data.details || data.error || 'LOC calculation failed');
      }
    } catch (e) {
      setError(e.message);
      setAnalysis(null);
    } finally {
      setIsAnalyzing(false);
      setIsAnalyzingOmni(false);
    }
  }

  // Convert features to obstacles format for OMNI API
  function convertFeaturesToObstacles(featureList, { includeIdentifiers = false } = {}) {
    const obstacles = [];
    
    for (const [featureIndex, f] of featureList.entries()) {
      let obstaclePoints = [];
      let radius = undefined; // Track radius for circular obstacles
      
      if (f.geometry.type === "Point") {
        // Point geometry - add directly
        const [lon, lat, elev] = f.geometry.coordinates;
        obstaclePoints.push({
          lon,
          lat,
          elev: elev || getDefaultZ()
        });
        // Check if this Point is actually a Circle (with radius in properties)
        if (f.properties?.radius && f.properties?.radius > 0) {
          radius = f.properties.radius;
        }
      } else if (f.geometry.type === "Circle") {
        // Circle - use center point AND capture radius
        const [lon, lat] = f.geometry.center;
        const elev = f.geometry.z || getDefaultZ();
        obstaclePoints.push({ lon, lat, elev });
        // Capture radius from Circle geometry
        radius = f.geometry.radius || 0;
      } else if (f.geometry.type === "LineString") {
        // LineString - interpolate the line for more accurate obstacle representation
        const coords = f.geometry.coordinates;
        const length = getLineLengthMeters(coords);
        const stepSize = getAdaptiveStepSize(length);
        const interpolated = interpolateLine3D(coords, stepSize);
        
        for (const coord of interpolated) {
          const [lon, lat, elev] = coord;
          obstaclePoints.push({
            lon,
            lat,
            elev: elev || getDefaultZ()
          });
        }
      } else if (f.geometry.type === "Polygon") {
        // Polygon - interpolate each edge of the outer ring
        let ring = f.geometry.coordinates[0];
        
        // Ensure ring is closed
        if (ring.length > 0 && 
            (ring[0][0] !== ring[ring.length - 1][0] || 
             ring[0][1] !== ring[ring.length - 1][1])) {
          ring = [...ring, ring[0]];
        }
        
        const length = getLineLengthMeters(ring);
        const stepSize = getAdaptiveStepSize(length);
        const interpolated = interpolateLine3D(ring, stepSize);
        
        for (const coord of interpolated) {
          const [lon, lat, elev] = coord;
          obstaclePoints.push({
            lon,
            lat,
            elev: elev || getDefaultZ()
          });
        }
        
        // Check if this Polygon is actually a Circle (with radius in properties)
        if (f.properties?.radius && f.properties?.radius > 0 && f.properties?._originalType === "Circle") {
          radius = f.properties.radius;
        }
      }
      
      // Create obstacle objects from points
      for (let i = 0; i < obstaclePoints.length; i++) {
        const point = obstaclePoints[i];
        const name = f.properties?.name || `${f.geometry.type} ${i + 1}`;
        const type = f.properties?.type || "Building";
        
        const obstacle = {
          x: point.lon,
          y: point.lat,
          z: point.elev,
          name: name,
          type: type
        };
        
        // GNSS overview counts source features once across sampled points/segments.
        if (includeIdentifiers) obstacle.obstacleId = `feature:${featureIndex}`;

        // Include radius if this is a circular obstacle
        if (radius !== undefined && radius > 0) {
          obstacle.radius = radius;
        }
        
        obstacles.push(obstacle);
      }
    }
    
    return obstacles;
  }

  const penetrationTable = renderPenetrationTable();
  const noPenetrationTable = renderNoPenetrationTable();
  const hasAnyTable = !!penetrationTable || !!noPenetrationTable;

  // Hjälpfunktion för att slå upp adress på namn
  //function getAddressForAirport(name) {
  //  return airportAddresses.find(
  //    a => a.airport_name.toLowerCase().replace(/\s+/g, "") === name.toLowerCase().replace(/\s+/g, "")
  //  );
 // }

  // --- PDF-exportfunktion --- (översätt texter via t())
  // (OBS: här måste du även använda t() för texter i rapporten!)

  // ... PDF-exportfunktion och tabellrendering med t('nyckel') för rubriker och texter ...
  // (Se tidigare block för exakta implementationer, här utelämnas för platsbesparing)

  function renderPenetrationTable() {
    if (!analysis || !Array.isArray(analysis) || !analysis.length) return null;
    let rows = [];
    analysis.forEach((geoResult, i) => {
      (geoResult.matches || [])
        .filter(row => row.highlight)
        .forEach((row, idx2) => {
          const objectZs = (row.penetrations || [])
            .map(p => typeof p.z === "number" ? p.z.toFixed(2) : "-")
            .join(", ");
          const surfaceZs = (row.penetrations || [])
            .map(p => typeof p.surface_z === "number" ? p.surface_z.toFixed(2) : "-")
            .join(", ");
          const penetrationDiffs = (row.penetrations || [])
            .map(p => typeof p.penetration_delta === "number" ? p.penetration_delta.toFixed(2) : "-")
            .join(", ");

          rows.push(
            <tr key={(row.id || idx2) + "_" + i} style={{ background: "#ffd8d8" }}>
              <td>{geoResult.properties?.name || t('geometry') + ` #${i + 1}`}</td>
              <td>{row.airport_name || "-"}</td>
              <td>{row.surface_name || "-"}</td>
              <td>{row.runway || "-"}</td>
              <td>{row.regulation || "-"}</td>
              <td>{objectZs}</td>
              <td>{surfaceZs}</td>
              <td>{t('yes')}</td>
              <td>{penetrationDiffs}</td>
              <td>{row.horizontal_tolerance > 0 ? `${row.horizontal_tolerance} m` : "-"}</td>
            </tr>
          );
        });
    });
    return rows.length ? (
      <div>
        <h4 style={{color:"#b30000"}}>{t('penetration')}</h4>
        <table id="penetration-table" border="1" cellPadding="6" style={{ borderCollapse: "collapse", marginTop: 8, minWidth: 900 }}>
          <thead>
            <tr>
              <th>{t('object')}</th>
              <th>{t('airport')}</th>
              <th>{t('surface')}</th>
              <th>{t('runway')}</th>
              <th>{t('regulation')}</th>
              <th>{t('objectHeightM')}</th>
              <th>{t('surfaceHeightM')}</th>
              <th>{t('penetratesQ')}</th>
              <th>{t('penetrationMAll')}</th>
              <th>{t('horizontalTolerance') || 'Horiz. Tolerance'}</th>
            </tr>
          </thead>
          <tbody>
            {rows}
          </tbody>
        </table>
      </div>
    ) : null;
  }

  function renderNoPenetrationTable() {
    if (!analysis || !Array.isArray(analysis) || !analysis.length) return null;
    let rows = [];
    analysis.forEach((geoResult, i) => {
      (geoResult.matches || [])
        .filter(row => !row.highlight)
        .forEach((row, idx2) => {
          const objectZs = (row.penetrations || [])
            .map(p => typeof p.z === "number" ? p.z.toFixed(2) : "-")
            .join(", ");
          const surfaceZs = (row.penetrations || [])
            .map(p => typeof p.surface_z === "number" ? p.surface_z.toFixed(2) : "-")
            .join(", ");
          const penetrationDiffs = (row.penetrations || [])
            .map(p => typeof p.penetration_delta === "number" ? p.penetration_delta.toFixed(2) : "-")
            .join(", ");

          if (
            row.surface_name ||
            row.airport_name ||
            row.runway ||
            row.regulation ||
            (row.penetrations && row.penetrations.length)
          ) {
            rows.push(
              <tr key={`nopen_${i}_${idx2}_${row.id ?? 'noid'}`} style={{ background: "#d8ffd8" }}>
                <td>{geoResult.properties?.name || t('geometry') + ` #${i + 1}`}</td>
                <td>{row.airport_name || "-"}</td>
                <td>{row.surface_name || "-"}</td>
                <td>{row.runway || "-"}</td>
                <td>{row.regulation || "-"}</td>
                <td>{objectZs}</td>
                <td>{surfaceZs}</td>
                <td>{t('no')}</td>
                <td>{penetrationDiffs}</td>
                <td>{row.horizontal_tolerance > 0 ? `${row.horizontal_tolerance} m` : "-"}</td>
              </tr>
            );
          }
        });
    });
    return rows.length ? (
      <div>
        <h4 style={{color:"#228B22"}}>{t('noPenetration')}</h4>
        <table id="no-penetration-table" border="1" cellPadding="6" style={{ borderCollapse: "collapse", marginTop: 8, minWidth: 900 }}>
          <thead>
            <tr>
              <th>{t('object')}</th>
              <th>{t('airport')}</th>
              <th>{t('surface')}</th>
              <th>{t('runway')}</th>
              <th>{t('regulation')}</th>
              <th>{t('objectHeightM')}</th>
              <th>{t('surfaceHeightM')}</th>
              <th>{t('penetratesQ')}</th>
              <th>{t('penetrationMAll')}</th>
              <th>{t('horizontalTolerance') || 'Horiz. Tolerance'}</th>
            </tr>
          </thead>
          <tbody>
            {rows}
          </tbody>
        </table>
      </div>
    ) : null;
  }
  
    // Förkortad förklaring: surfaceStyle och highlightedSurfaceIds kan använda t() om du visar ytnamn, annars ej nödvändigt.

 
 function surfaceStyle(feature, highlightIds = []) {
    const id = feature.properties && feature.properties.id;
    if (highlightIds && highlightIds.includes(id)) {
      return {
        color: "#d32f2f",
        weight: 3,
        fillColor: "#d32f2f",
        fillOpacity: 0.35
      };
    }
    return {
      color: "#0078A8",
      weight: 2,
      fillColor: "#0078A8",
      fillOpacity: 0.2
    };
  }

 const highlightedSurfaceIds = (analysis || []).flatMap(geoResult =>
    (geoResult.matches || [])
      .filter(m => m.highlight)
      .map(m => m.id)
  );

  const handleDbSurfaceLayers = useCallback(layers => setDbSurfaceLayers(layers), []);

  // Build a map from feat.id → true (has impact) | false (no impact)
  // Only populated after analysis runs. Used to color markers/shapes red or green.
  const featureImpactMap = useMemo(() => {
    const map = new Map();
    if (!analysis) return map;
    const analyzable = features.filter(f => !(f.properties && f.properties.isSurfaceFromDb));
    analyzable.forEach((feat, i) => {
      const result = analysis[i];
      if (!result) return;
      const featName = feat.properties?.name;

      // Surface penetration impact
      const hasSurface = (result.matches || []).some(m =>
        m.penetrations && m.penetrations.some(p => p.penetrates)
      );

      // OMNI impact
      const hasOmni = (omniAnalysis?.results || []).some(r =>
        (r.obstacles || []).some(o => o.obstacleName === featName && o.isCritical && !o.isCloseIn)
      );

      // OAS impact
      const hasOas = (oasAnalysis?.results || []).some(r =>
        (r.categoryResults || []).some(c =>
          (c.obstacles || []).some(o => o.obstacleName === featName && o.isCritical)
        )
      );

      // BARO/VNAV impact
      const hasBaro = (baroVnavAnalysis?.results || []).some(r =>
        (r.categoryResults || []).some(c =>
          (c.obstacleRows || []).some(o => o.obstacleName === featName && o.isCritical)
        )
      );

      // VSS impact
      const hasVss = (vssAnalysis?.results || []).some(r =>
        [...(r.vssRows || []), ...(r.ocsRows || [])].some(o =>
          o.obstacleName === featName && o.isCritical && (o.surface === 'OCS' || !o.isDisregardable)
        )
      );

      // HoldingBase impact
      const hasHoldingBase = (holdingBaseAnalysis?.results || []).some(r =>
        (r.obsRows || []).some(o => o.obstacleName === featName && o.isCritical)
      );

      // HoldingRacetrack impact
      const hasHoldingRacetrack = (holdingRacetrackAnalysis?.results || []).some(r =>
        (r.obsRows || []).some(o => o.obstacleName === featName && o.isCritical)
      );

      // RNAVHolding impact
      const hasRnavHolding = (rnavHoldingAnalysis?.results || []).some(r =>
        (r.obsRows || []).some(o => o.obstacleName === featName && o.isCritical)
      );

      // RNP Holding impact
      const hasRnpHolding = (rnpHoldingAnalysis?.results || []).some(r =>
        (r.obsRows || []).some(o => o.obstacleName === featName && o.isCritical)
      );

      // Shielding impact
      const hasShielding = (shieldingAnalysis?.results || []).some(r =>
        (r.rowsWithinRadius || []).some(o => o.obstacleName === featName && o.isCritical)
      );

      map.set(feat.id, hasSurface || hasOmni || hasOas || hasBaro || hasVss || hasHoldingBase || hasHoldingRacetrack || hasRnavHolding || hasRnpHolding || hasShielding);
    });
    const locCritical = new Set((locAnalysis?.results || []).flatMap(r => r.segments.flatMap(s => s.obsRows || []))
      .filter(o => o.isCritical).map(o => o.obstacleId));
    getAnalyzableFeatures(features).forEach((feature, index) => {
      if (locCritical.has(`source:feature:${index}`)) map.set(feature.id, true);
    });
    return map;
  }, [analysis, omniAnalysis, oasAnalysis, baroVnavAnalysis, vssAnalysis, holdingBaseAnalysis, holdingRacetrackAnalysis, rnavHoldingAnalysis, rnpHoldingAnalysis, shieldingAnalysis, locAnalysis, features]);

  // Build obstacle name → impact map so overlay markers (OMNI/OAS) reflect overall impact
  const featureNameImpactMap = useMemo(() => {
    const map = new Map();
    features.forEach(feat => {
      const name = feat.properties?.name;
      if (name && featureImpactMap.has(feat.id)) {
        map.set(name, featureImpactMap.get(feat.id));
      }
    });
    return map;
  }, [featureImpactMap, features]);

  // Build per-vertex impact map: feat.id → boolean[] indexed by vertex position.
  // The backend computes vertexPenetrations[] for LineString — one entry per original
  // vertex, located in penetrationResult at the cumulative step-count offset for that vertex.
  // Vertex i is red if vp.penetrates is true (it is inside a surface and its z exceeds
  // the surface elevation at that point).
  const featureVertexImpactMap = useMemo(() => {
    const result = new Map();
    if (!analysis) return result;
    const analyzable = features.filter(f => !(f.properties && f.properties.isSurfaceFromDb));
    analyzable.forEach((feat, i) => {
      const geoType = feat.geometry.type;
      if (geoType !== 'LineString') return;
      const analysisResult = analysis[i];
      if (!analysisResult) return;

      const numVerts = feat.geometry.coordinates.length;
      const vertexImpactArr = new Array(numVerts).fill(false);
      (analysisResult.matches || []).forEach(m => {
        (m.vertexPenetrations || []).forEach((vp, vi) => {
          if (vi < numVerts && vp.penetrates) {
            vertexImpactArr[vi] = true;
          }
        });
      });
      result.set(feat.id, vertexImpactArr);
    });
    return result;
  }, [analysis, features]);

  function renderFeatures() {
    return features.flatMap((feat, idx) => {
      // Determine impact color: red = impact, green = no impact, default (blue) if not yet analyzed
      const analyzed = featureImpactMap.has(feat.id);
      const hasImpact = featureImpactMap.get(feat.id);
      const markerColor = analyzed ? (hasImpact ? "red" : "green") : "blue";
      const shapeColor = analyzed ? (hasImpact ? "#d32f2f" : "#2e7d32") : null;

      // Point
      if (feat.geometry.type === "Point") {
        const position = safeGetLatLngFromGeoJSON(feat.geometry.coordinates, `render-point-${feat.id || idx}`);
        if (!position) return null;
        return (
          <Marker
            key={feat.id}
            position={position}
            icon={L.icon({
              iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${markerColor}.png`,
              shadowUrl: "/images/marker-shadow.png",
              iconSize: [25, 41], iconAnchor: [12, 41]
            })}
            featureId={feat.id}
          >
            <Tooltip direction="top" offset={[0, -28]} permanent>
              {feat.properties?.name || ""}
            </Tooltip>
          </Marker>
        );
      }

      // PDF-läge: linjer och polygoner som marker
      if (pdfMode && (feat.geometry.type === "LineString" || feat.geometry.type === "Polygon" || feat.geometry.type === "MultiPolygon")) {
        let coords;
        if (feat.geometry.type === "Polygon") {
          coords = feat.geometry.coordinates[0];
          if (
            coords.length < 2 ||
            coords[0][0] !== coords[coords.length - 1][0] ||
            coords[0][1] !== coords[coords.length - 1][1]
          ) {
            coords = [...coords, coords[0]];
          }
        } else if (feat.geometry.type === "MultiPolygon") {
          // MultiPolygon support in PDF mode: combine all polygon rings
          coords = feat.geometry.coordinates.flatMap(polygon => {
            let ring = polygon[0];
            if (
              ring.length < 2 ||
              ring[0][0] !== ring[ring.length - 1][0] ||
              ring[0][1] !== ring[ring.length - 1][1]
            ) {
              ring = [...ring, ring[0]];
            }
            return ring;
          });
        } else {
          coords = feat.geometry.coordinates;
        }

        const length = getLineLengthMeters(coords);
        const stepSize = getAdaptiveStepSize(length);

        const interpolated = interpolateLine3D(coords, stepSize);
        const pdfMarkerColor = analyzed ? (hasImpact ? "red" : "green") : "blue";

        return interpolated.map((c, i) => (
          <Marker
            key={feat.id + "-pt-" + i}
            position={[c[1], c[0]]}
            icon={L.icon({
              iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${pdfMarkerColor}.png`,
              shadowUrl: "/images/marker-shadow.png",
              iconSize: [19, 31], iconAnchor: [9, 31]
            })}
            featureId={feat.id}
          >
            {feat.properties?.name && i === 0 && (
              <Tooltip direction="top" offset={[0, -22]} permanent>
                {feat.properties.name}
              </Tooltip>
            )}
          </Marker>
        ));
      }

      // PDF-läge: Cirkel som marker längs polygonkant
      if (pdfMode && feat.geometry.type === "Circle") {
        const { center, radius, z } = feat.geometry;
        const circlePoints = circleToPolygon(center[0], center[1], radius, z, 12)[0];
        const pdfMarkerColor = analyzed ? (hasImpact ? "red" : "green") : "blue";
        return circlePoints.map((c, i) => (
          <Marker
            key={feat.id + "-circlept-" + i}
            position={[c[1], c[0]]}
            icon={L.icon({
              iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${pdfMarkerColor}.png`,
              shadowUrl: "/images/marker-shadow.png",
              iconSize: [19, 31], iconAnchor: [9, 31]
            })}
            featureId={feat.id}
          >
            {feat.properties?.name && i === 0 && (
              <Tooltip direction="top" offset={[0, -22]} permanent>
                {feat.properties.name}
              </Tooltip>
            )}
          </Marker>
        ));
      }

      // Vanligt läge: LineString
      if (!pdfMode && feat.geometry.type === "LineString") {
        const lineColor = shapeColor || "#b300b3";
        const vertexImpact = featureVertexImpactMap.get(feat.id); // boolean[] by vertex index
        // Show per-vertex colored markers only after analysis has run.
        // Red = vertex penetrates a surface, green = vertex is safe.
        const vertexMarkers = analyzed
          ? feat.geometry.coordinates.map((c, vi) => {
              const vImpact = vertexImpact ? (vertexImpact[vi] ?? false) : false;
              const vColor = vImpact ? '#d32f2f' : '#2e7d32';
              return (
                <CircleMarker
                  key={`${feat.id}-v-${vi}`}
                  center={[c[1], c[0]]}
                  radius={5}
                  pathOptions={{ color: vColor, fillColor: vColor, fillOpacity: 1, weight: 1 }}
                />
              );
            })
          : [];
        return [
          <Polyline
            key={feat.id}
            positions={feat.geometry.coordinates.map(c => [c[1], c[0]])}
            pathOptions={{ color: lineColor, weight: 5, dashArray: "6 6" }}
            featureId={feat.id}
          >
            <Tooltip direction="top" sticky>
              {feat.properties?.name || ""}
            </Tooltip>
          </Polyline>,
          ...vertexMarkers
        ];
      }
      // Vanligt läge: Polygon
      if (!pdfMode && feat.geometry.type === "Polygon") {
        const polyColor = shapeColor || "#0078A8";
        // For Polygon, use overall feature impact for all vertices (no per-segment data)
        const vColor = analyzed ? (hasImpact ? '#d32f2f' : '#2e7d32') : null;
        const outerRing = feat.geometry.coordinates[0] || [];
        const uniqueVerts = outerRing.length > 1 &&
          outerRing[0][0] === outerRing[outerRing.length - 1][0] &&
          outerRing[0][1] === outerRing[outerRing.length - 1][1]
          ? outerRing.slice(0, -1)
          : outerRing;
        const vertexMarkers = analyzed && vColor
          ? uniqueVerts.map((c, vi) => (
              <CircleMarker
                key={`${feat.id}-v-${vi}`}
                center={[c[1], c[0]]}
                radius={5}
                pathOptions={{ color: vColor, fillColor: vColor, fillOpacity: 1, weight: 1 }}
              />
            ))
          : [];
        return [
          <GeoJsonWithLayers
            key={feat.id}
            data={{
              type: "FeatureCollection",
              features: [{
                type: "Feature",
                geometry: feat.geometry,
                properties: feat.properties
              }]
            }}
            style={() => ({
              color: polyColor,
              weight: 2,
              fillColor: polyColor,
              fillOpacity: 0.2
            })}
            highlightedSurfaceIds={[]}
            onLayersReady={() => { }}
          >
            <Tooltip direction="top" sticky>
              {feat.properties?.name || ""}
            </Tooltip>
          </GeoJsonWithLayers>,
          ...vertexMarkers
        ];
      }
      // Vanligt läge: MultiPolygon
      if (!pdfMode && feat.geometry.type === "MultiPolygon") {
        const polyColor = shapeColor || "#0078A8";
        // For MultiPolygon, use overall feature impact for all vertices (no per-segment data)
        const vColor = analyzed ? (hasImpact ? '#d32f2f' : '#2e7d32') : null;
        const vertexMarkers = analyzed && vColor
          ? feat.geometry.coordinates.flatMap((poly, pi) => {
              const ring = poly[0] || [];
              const uniqueVerts = ring.length > 1 &&
                ring[0][0] === ring[ring.length - 1][0] &&
                ring[0][1] === ring[ring.length - 1][1]
                ? ring.slice(0, -1)
                : ring;
              return uniqueVerts.map((c, vi) => (
                <CircleMarker
                  key={`${feat.id}-p${pi}-v${vi}`}
                  center={[c[1], c[0]]}
                  radius={5}
                  pathOptions={{ color: vColor, fillColor: vColor, fillOpacity: 1, weight: 1 }}
                />
              ));
            })
          : [];
        return [
          <GeoJsonWithLayers
            key={feat.id}
            data={{
              type: "FeatureCollection",
              features: [{
                type: "Feature",
                geometry: feat.geometry,
                properties: feat.properties
              }]
            }}
            style={() => ({
              color: polyColor,
              weight: 2,
              fillColor: polyColor,
              fillOpacity: 0.2
            })}
            highlightedSurfaceIds={[]}
            onLayersReady={() => { }}
          >
            <Tooltip direction="top" sticky>
              {feat.properties?.name || ""}
            </Tooltip>
          </GeoJsonWithLayers>,
          ...vertexMarkers
        ];
      }
      // Vanligt läge: Circle
      if (!pdfMode && feat.geometry.type === "Circle") {
        const circleColor = shapeColor || "#1e88e5";
        return (
          <Circle
            key={feat.id}
            center={[feat.geometry.center[1], feat.geometry.center[0]]}
            radius={feat.geometry.radius}
            pathOptions={{ color: circleColor, weight: 2, fillOpacity: 0.2 }}
            featureId={feat.id}
          >
            <Tooltip direction="top" sticky>
              {feat.properties?.name || ""}
            </Tooltip>
          </Circle>
        );
      }
      return null;
    });
  }
  
   // NYTT: Rendera endast features-kartan (endast input-objekt)
 function renderMapInputOnly() {
  return (
    <div ref={mapInputRef} style={{ height: 400, width: 600, display: "none" }}>
      <MapContainer center={[59.65, 17.95]} zoom={13} style={{ height: "100%", width: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FeatureGroup>
          {features.map((feat, idx) => {
            if (feat.geometry.type === "Point") {
              const position = safeGetLatLngFromGeoJSON(feat.geometry.coordinates, `pdf-point-${feat.id || idx}`);
              if (!position) {
                console.warn(`[Draw3DMap] Skipping Point marker in PDF mode with invalid coordinates:`, feat);
                return null;
              }
              return (
                <Marker
                  key={feat.id || idx}
                  position={position}
                  icon={L.icon({
                    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
                    shadowUrl: "/images/marker-shadow.png",
                    iconSize: [25, 41], iconAnchor: [12, 41]
                  })}
                />
              );
            } else if (feat.geometry.type === "LineString") {
              const coords = feat.geometry.coordinates.filter(c =>
                typeof c[0] === "number" && typeof c[1] === "number"
              );
              if (!coords.length) return null;
              return (
                <Polyline
                  key={feat.id || idx}
                  positions={coords.map(c => [c[1], c[0]])}
                  pathOptions={{ color: "#b300b3", weight: 5, dashArray: "6 6" }}
                />
              );
            } else if (feat.geometry.type === "Polygon") {
              const ring = feat.geometry.coordinates[0].filter(c =>
                typeof c[0] === "number" && typeof c[1] === "number"
              );
              if (!ring.length) return null;
              return (
                <Polyline
                  key={feat.id || idx}
                  positions={ring.map(c => [c[1], c[0]])}
                  pathOptions={{ color: "#0078A8", weight: 2 }}
                />
              );
            } else if (feat.geometry.type === "MultiPolygon") {
              // MultiPolygon support: render each polygon as a separate Polyline.
              // Wrap in a Fragment so the outer .map() receives a single keyed element.
              return (
                <React.Fragment key={feat.id || idx}>
                  {feat.geometry.coordinates.map((polygon, polyIdx) => {
                    const ring = polygon[0].filter(c =>
                      typeof c[0] === "number" && typeof c[1] === "number"
                    );
                    if (!ring.length) return null;
                    return (
                      <Polyline
                        key={`${feat.id || idx}-${polyIdx}`}
                        positions={ring.map(c => [c[1], c[0]])}
                        pathOptions={{ color: "#0078A8", weight: 2 }}
                      />
                    );
                  }).filter(Boolean)}
                </React.Fragment>
              );
            } else if (feat.geometry.type === "Circle") {
              const center = feat.geometry.center;
              if (!center || typeof center[0] !== "number" || typeof center[1] !== "number") return null;
              return (
                <Circle
                  key={feat.id || idx}
                  center={[center[1], center[0]]}
                  radius={feat.geometry.radius}
                  pathOptions={{ color: "#1e88e5", weight: 2, fillOpacity: 0.2 }}
                />
              );
            } else {
              return null;
            }
          })}
        </FeatureGroup>
      </MapContainer>
    </div>
  );
}
  // NYTT: Rendera endast penetrationsmarkörer-kartan (endast penetrationspunkter)
  function renderMapPenetrationOnly() {
    return (
      <div ref={mapPenRef} style={{ height: 400, width: 600, display: "none" }}>
        <MapContainer center={[59.65, 17.95]} zoom={13} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FeatureGroup>
            {analysis && analysis.flatMap((geoResult, i) =>
              (geoResult.matches || [])
                .filter(row => row.highlight && Array.isArray(row.penetrations) && row.penetrations.length > 0)
                .flatMap((row, idx2) =>
                  row.penetrations.map((p, j) => (
                    <Marker
                      key={`${i}_${idx2}_p${j}`}
                      position={[p.y, p.x]}
                      icon={L.icon({
                        iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
                        shadowUrl: "/images/marker-shadow.png",
                        iconSize: [19, 31], iconAnchor: [9, 31]
                      })}
                    />
                  ))
                )
            )}
          </FeatureGroup>
        </MapContainer>
      </div>
    );
  }
	 
  // Helper function to handle OMNI marker clicks on map
  const handleMarkerClick = (runwayIdx, obsIdx) => {
    const obstacleId = `${runwayIdx}-${obsIdx}`;
    setSelectedObstacles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(obstacleId)) {
        newSet.delete(obstacleId);
      } else {
        newSet.add(obstacleId);
      }
      return newSet;
    });
    
    // Scroll table row into view
    setTimeout(() => {
      const tableRow = document.querySelector(`tr[data-obstacle-id="${obstacleId}"]`);
      if (tableRow) {
        tableRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Helper function to handle OAS marker clicks on map
  const handleOasMarkerClick = (resultIdx, catIdx, obsIdx) => {
    const obstacleId = `oas-${resultIdx}-${catIdx}-${obsIdx}`;
    setSelectedOasObstacles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(obstacleId)) {
        newSet.delete(obstacleId);
      } else {
        newSet.add(obstacleId);
      }
      return newSet;
    });
    // Scroll OAS table row into view
    setTimeout(() => {
      const tableRow = document.querySelector(`tr[data-oas-id="${obstacleId}"]`);
      if (tableRow) {
        tableRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Helper: handle BARO marker clicks on map
  const handleBaroMarkerClick = (resultIdx, catIdx, rowIdx) => {
    const id = `baro-${resultIdx}-${catIdx}-${rowIdx}`;
    setSelectedBaroObstacles(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
    setTimeout(() => {
      const el = document.querySelector(`tr[data-baro-id="${id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Helper: handle VSS marker clicks on map
  const handleVssMarkerClick = (resultIdx, surface, rowIdx) => {
    const id = `vss-${resultIdx}-${surface}-${rowIdx}`;
    setSelectedVssObstacles(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
    setTimeout(() => {
      const el = document.querySelector(`tr[data-vss-id="${id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Helper: handle Shielding marker clicks on map
  const handleShieldingMarkerClick = (id) => {
    setSelectedShieldingObstacles(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
    setTimeout(() => {
      const el = document.querySelector(`tr[data-shielding-id="${id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };
  
    // === UI/RENDER ===
  return (
    <div>
      <style>
        {`
          .app-header {
            background: #1e3a8a;
            border-bottom: 1px solid #e0e0e0;
            padding: 8px 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: relative;
            z-index: 1000;
          }
          .header-content {
            display: flex;
            width: 100%;
            justify-content: space-between;
            align-items: center;
          }
          .header-left {
            display: flex;
            align-items: center;
          }
          .header-logo {
            height: 32px;
            margin-right: 12px;
          }
          .header-logo-fallback {
            font-weight: bold;
            font-size: 18px;
            color: #1976d2;
            margin-right: 12px;
          }
          .header-center {
            display: flex;
            flex: 1;
            justify-content: center;
          }
          .header-buttons {
            display: flex;
            gap: 12px;
            align-items: center;
          }
          .header-btn, .airport-dropdown, .language-toggle {
            padding: 6px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: #fff;
            cursor: pointer;
            font-size: 14px;
          }
          .header-btn:hover, .airport-dropdown:hover, .language-toggle:hover {
            background: #f5f5f5;
          }
          .airport-dropdown {
            min-width: 200px;
          }
          .header-right {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .language-toggle {
            font-weight: bold;
            color: #1976d2;
          }
          .auth-buttons {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-right: 12px;
          }
          .user-menu {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-right: 12px;
          }
          .user-info {
            font-size: 14px;
            color: #fff;
          }
          .paid-badge {
            color: #28a745;
            font-weight: bold;
            font-size: 12px;
          }
          .login-btn, .register-btn, .logout-btn {
            background: #007bff;
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            transition: background-color 0.2s;
          }
          .login-btn:hover, .register-btn:hover, .logout-btn:hover {
            background: #0056b3;
          }
          .register-btn {
            background: #28a745;
          }
          .register-btn:hover {
            background: #1e7e34;
          }
          .logout-btn {
            background: #6c757d;
          }
          .logout-btn:hover {
            background: #545b62;
          }
          
          /* OMNI Obstacle Marker Styles */
          .omni-marker-selected img {
            filter: drop-shadow(0 0 8px rgba(33, 150, 243, 0.8));
            animation: pulse-marker 1.5s ease-in-out infinite;
          }
          
          @keyframes pulse-marker {
            0%, 100% {
              filter: drop-shadow(0 0 8px rgba(33, 150, 243, 0.8));
            }
            50% {
              filter: drop-shadow(0 0 16px rgba(33, 150, 243, 1));
            }
          }
          
          .omni-marker:hover img {
            filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.5));
            transform: scale(1.1);
            transition: all 0.2s ease;
          }

          /* OAS Obstacle Marker Styles */
          .oas-marker-selected img {
            filter: drop-shadow(0 0 8px rgba(26, 35, 126, 0.8));
            animation: pulse-oas-marker 1.5s ease-in-out infinite;
          }

          @keyframes pulse-oas-marker {
            0%, 100% {
              filter: drop-shadow(0 0 8px rgba(26, 35, 126, 0.8));
            }
            50% {
              filter: drop-shadow(0 0 16px rgba(26, 35, 126, 1));
            }
          }

          .oas-marker:hover img {
            filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.5));
            transform: scale(1.1);
            transition: all 0.2s ease;
          }

          /* BARO Obstacle Marker Styles */
          .baro-marker-selected img {
            filter: drop-shadow(0 0 8px rgba(27, 94, 32, 0.8));
            animation: pulse-baro-marker 1.5s ease-in-out infinite;
          }
          @keyframes pulse-baro-marker {
            0%, 100% { filter: drop-shadow(0 0 8px rgba(27, 94, 32, 0.8)); }
            50% { filter: drop-shadow(0 0 16px rgba(27, 94, 32, 1)); }
          }
          .baro-marker:hover img {
            filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.5));
            transform: scale(1.1);
            transition: all 0.2s ease;
          }

          /* VSS Obstacle Marker Styles */
          .vss-marker-selected img {
            filter: drop-shadow(0 0 8px rgba(13, 71, 161, 0.8));
            animation: pulse-vss-marker 1.5s ease-in-out infinite;
          }
          @keyframes pulse-vss-marker {
            0%, 100% { filter: drop-shadow(0 0 8px rgba(13, 71, 161, 0.8)); }
            50% { filter: drop-shadow(0 0 16px rgba(13, 71, 161, 1)); }
          }
          .vss-marker:hover img {
            filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.5));
            transform: scale(1.1);
            transition: all 0.2s ease;
          }

          /* Shielding Obstacle Marker Styles */
          .shielding-marker-selected img {
            filter: drop-shadow(0 0 8px rgba(230, 81, 0, 0.8));
            animation: pulse-shielding-marker 1.5s ease-in-out infinite;
          }
          @keyframes pulse-shielding-marker {
            0%, 100% { filter: drop-shadow(0 0 8px rgba(230, 81, 0, 0.8)); }
            50% { filter: drop-shadow(0 0 16px rgba(230, 81, 0, 1)); }
          }
          .shielding-marker:hover img {
            filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.5));
            transform: scale(1.1);
            transition: all 0.2s ease;
          }

          /* Mobile responsiveness */
          @media (max-width: 768px) {
            .app-header {
              padding: 6px 8px;
            }
            .header-content {
              flex-wrap: wrap;
              gap: 8px;
            }
            .header-center {
              order: 3;
              width: 100%;
              justify-content: center;
              margin-top: 8px;
            }
            .header-buttons {
              flex-wrap: wrap;
              gap: 6px;
              justify-content: center;
            }
            .header-btn, .airport-dropdown {
              font-size: 12px;
              padding: 4px 8px;
            }
            .auth-buttons {
              gap: 6px;
              margin-right: 8px;
            }
            .login-btn, .register-btn, .logout-btn {
              font-size: 12px;
              padding: 6px 12px;
            }
            .language-toggle {
              font-size: 12px;
              padding: 4px 8px;
            }
            .user-info {
              font-size: 12px;
            }
          }

          @media (max-width: 480px) {
            .header-content {
              flex-direction: column;
              align-items: stretch;
            }
            .header-left, .header-right {
              justify-content: center;
            }
            .header-center {
              order: 2;
              margin-top: 4px;
            }
            .auth-buttons, .user-menu {
              justify-content: center;
              margin-right: 0;
            }
          }
        `}
      </style>

      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <img 
              src="/images/logo.svg" 
              alt="OLS Logo" 
              className="header-logo"
              onError={e => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'inline-block';
              }}
            />
            <div className="header-logo-fallback" style={{display: 'none'}}>
              OLS
            </div>
          </div>
          <div className="header-center">
            <div className="header-buttons">
              <input
                type="file"
                accept=".geojson,.json,application/json"
                onChange={e => handleGeoJsonUpload(e.target.files[0])}
                style={{ display: 'none' }}
                id="geojson-upload"
              />
              <button 
                className="header-btn"
                onClick={() => document.getElementById('geojson-upload').click()}
              >
                {t('importGeoJSON')}
              </button>
              <button 
                className="header-btn"
                onClick={handleGeoJsonDownload}
                disabled={!features || features.length === 0}
              >
                {t('exportGeoJSON')}
              </button>
              <select 
                value={selectedAirport}
                onChange={handleAirportChange}
                className="airport-dropdown"
                disabled={!airports}
              >
                <option value="">
                  {!airports ? t('loadingAirports') : t('selectAirport')}
                </option>
                {airports && airports.map(airport => (
                  <option key={airport.airport_name} value={airport.airport_name}>
                    {airport.airport_name}
                  </option>
                ))}
              </select>
              <button 
                className="header-btn"
                onClick={() => setShowInformation(true)}
              >
                {t('information')}
              </button>
              <button 
                className="header-btn"
                onClick={() => setShowHelp(true)}
              >
                {t('help')}
              </button>
              <button 
                className="header-btn"
                onClick={() => setShowAboutUs(true)}
              >
                {t('aboutUs')}
              </button>
            </div>
          </div>
          <div className="header-right">
            {isAuthenticated ? (
              <div className="user-menu">
                <span className="user-info">
                  {t('currentUser')} {user?.contact_person}
                  {user?.betald === 'JA' && <span className="paid-badge">({t('paid')})</span>}
                </span>
                <button 
                  className="header-btn logout-btn"
                  onClick={handleLogout}
                >
                  {t('logout')}
                </button>
              </div>
            ) : (
              <div className="auth-buttons">
                <button 
                  className="header-btn login-btn"
                  onClick={() => setShowLoginModal(true)}
                >
                  {t('login')}
                </button>
                <button 
                  className="header-btn register-btn"
                  onClick={() => setShowRegisterModal(true)}
                >
                  {t('register')}
                </button>
              </div>
            )}
            <button 
              className="language-toggle"
              onClick={toggleLanguage}
              title={language === 'sv' ? 'Switch to English' : 'Byt till svenska'}
            >
              {language === 'sv' ? 'EN' : 'SV'}
            </button>
          </div>
        </div>
      </header>

      {/* Popups */}
      {showInformation && (
        <InformationPopup 
          onClose={() => setShowInformation(false)} 
        />
      )}
      {showAboutUs && (
        <AboutUsModal 
          open={showAboutUs}
          onClose={() => setShowAboutUs(false)} 
        />
      )}
      {showHelp && (
        <HelpPopup 
          onClose={() => setShowHelp(false)} 
        />
      )}
      {pendingImportedFeatures && (
        <div className="import-dialog" style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000, background: "#0007"
        }}>
          <div style={{
            background: "#fff", borderRadius: 8, margin: "10% auto", maxWidth: 400, padding: 24, boxShadow: "0 4px 16px #0004"
          }}>
            <p>{t('importReplacePrompt')}</p>
            <button onClick={() => {
              setFeatures(pendingImportedFeatures);
              setPendingImportedFeatures(null);
            }}>{t('replace')}</button>
            <button style={{marginLeft: 16}} onClick={() => {
              setFeatures(prev => [...prev, ...pendingImportedFeatures]);
              setPendingImportedFeatures(null);
            }}>{t('add')}</button>
            <button style={{marginLeft: 16}} onClick={() => setPendingImportedFeatures(null)}>{t('cancel')}</button>
          </div>
        </div>
      )}

     <div ref={mapDivRef} style={{ position: "relative", height: 500, marginTop: 4 }}>
        {/* Loading indicator for surfaces */}
        <LoadingIndicator loading={surfacesLoading} message={t('loadingLayers')} />
        
        {/* Loading indicator for analysis operation */}
        <LoadingIndicator loading={isAnalyzing} message={t('analyzingData')} blocking={true} />
        
        {/* Loading indicator for PDF generation operation */}
        <LoadingIndicator loading={isGeneratingPdf} message={t('generatingPdfReport')} blocking={true} />
        
        <LayerPanelWithHover
          basemap={basemap}
          setBasemap={setBasemap}
          showSwedishAirports={showSwedishAirports}
          setShowSwedishAirports={setShowSwedishAirports}
          showNavaids={showNavaids}
          setShowNavaids={setShowNavaids}
          showNav={showNav}
          setShowNav={setShowNav}
          showWaypoints={showWaypoints}
          setShowWaypoints={setShowWaypoints}
          showTmaTia={showTmaTia}
          setShowTmaTia={setShowTmaTia}
          showCtrTizRmz={showCtrTizRmz}
          setShowCtrTizRmz={setShowCtrTizRmz}
          showNavSurfaces={showNavSurfaces}
          setShowNavSurfaces={setShowNavSurfaces}
          shieldingAnalysis={shieldingAnalysis}
          showEasa={showEasa}
          setShowEasa={setShowEasa}
          showPansOps={showPansOps}
          setShowPansOps={setShowPansOps}
          showAnnex14={showAnnex14}
          setShowAnnex14={setShowAnnex14}
          showEtod={showEtod}
          setShowEtod={setShowEtod}
          showGeoJson={showGeoJson}
          setShowGeoJson={setShowGeoJson}
          uploadedGeoJson={uploadedGeoJson}
          showUserData={showUserData}
          setShowUserData={setShowUserData}
          drawnFeatures={features}
          importedFeatures={[]}
          onReloadLayers={reloadSurfaces}
          isAuthenticated={isAuthenticated}
          holdingRacetrackAnalysis={holdingRacetrackAnalysis}
          showHoldingSurfaces={showHoldingSurfaces}
          setShowHoldingSurfaces={setShowHoldingSurfaces}
          showRacetrackSurfaces={showRacetrackSurfaces}
          setShowRacetrackSurfaces={setShowRacetrackSurfaces}
          omniAnalysis={omniAnalysis}
          layerOmni={layerOmni}
          setLayerOmni={setLayerOmni}
          oasAnalysis={oasAnalysis}
          layerOasIls1={layerOasIls1}
          setLayerOasIls1={setLayerOasIls1}
          layerOasIls2={layerOasIls2}
          setLayerOasIls2={setLayerOasIls2}
          layerOasIls2ap={layerOasIls2ap}
          setLayerOasIls2ap={setLayerOasIls2ap}
          layerOasSbasApv1={layerOasSbasApv1}
          setLayerOasSbasApv1={setLayerOasSbasApv1}
          layerOasSbasCat1={layerOasSbasCat1}
          setLayerOasSbasCat1={setLayerOasSbasCat1}
          baroVnavAnalysis={baroVnavAnalysis}
          layerBaro={layerBaro}
          setLayerBaro={setLayerBaro}
          vssAnalysis={vssAnalysis}
          layerVss={layerVss}
          setLayerVss={setLayerVss}
          holdingBaseAnalysis={holdingBaseAnalysis}
          layerBaseTurn={layerBaseTurn}
          setLayerBaseTurn={setLayerBaseTurn}
          rnavHoldingAnalysis={rnavHoldingAnalysis}
          showRnavHoldingSurfaces={showRnavHoldingSurfaces}
          setShowRnavHoldingSurfaces={setShowRnavHoldingSurfaces}
          rnpHoldingAnalysis={rnpHoldingAnalysis}
          showRnpHoldingSurfaces={showRnpHoldingSurfaces}
          setShowRnpHoldingSurfaces={setShowRnpHoldingSurfaces}
          vorDmeHoldingAnalysis={vorDmeHoldingAnalysis}
          showVorDmeHoldingSurfaces={showVorDmeHoldingSurfaces}
          setShowVorDmeHoldingSurfaces={setShowVorDmeHoldingSurfaces}
          showHoldingDistSurfaces={showHoldingDistSurfaces}
          setShowHoldingDistSurfaces={setShowHoldingDistSurfaces}
          msaAnalysis={msaAnalysis}
          showMsaSurfaces={showMsaSurfaces}
          setShowMsaSurfaces={setShowMsaSurfaces}
          taaAnalysis={taaAnalysis}
          showTaaSectors={showTaaSectors}
          setShowTaaSectors={setShowTaaSectors}
          circlingAnalysis={circlingAnalysis}
          showCirclingAreas={showCirclingAreas}
          setShowCirclingAreas={setShowCirclingAreas}
          showCirclingCats={showCirclingCats}
          setShowCirclingCats={setShowCirclingCats}
          locAnalysis={locAnalysis}
          locLayerState={locLayerState}
          setLocLayerState={setLocLayerState}
          gnssApchAnalysis={gnssApchAnalysis}
          showGnssApchSurfaces={showGnssApchSurfaces}
          setShowGnssApchSurfaces={setShowGnssApchSurfaces}
          showGnssApchLayers={showGnssApchLayers}
          setShowGnssApchLayers={setShowGnssApchLayers}
        />

        {/* Koordinatinput */}
        <CoordinateInputPopup onAdd={(feat, opts) => {
          setFeatures(prev => [...prev, { ...feat, id: ++uniqueId }]);
          if (opts.zoomTo && mapRef.current) {
            let coords;
            if (feat.geometry.type === "Point") {
              coords = [feat.geometry.coordinates[1], feat.geometry.coordinates[0]];
            } else if (feat.geometry.type === "Circle") {
              coords = [feat.geometry.center[1], feat.geometry.center[0]];
            } else if (feat.geometry.type === "LineString") {
              coords = [feat.geometry.coordinates[0][1], feat.geometry.coordinates[0][0]];
            } else if (feat.geometry.type === "Polygon") {
              coords = [feat.geometry.coordinates[0][0][1], feat.geometry.coordinates[0][0][0]];
            } else if (feat.geometry.type === "MultiPolygon") {
              // MultiPolygon support: zoom to first polygon's first coordinate
              coords = [feat.geometry.coordinates[0][0][0][1], feat.geometry.coordinates[0][0][0][0]];
            }
            if (coords) {
              mapRef.current.setView(coords, 15);
            }
          }
        }} />
		
		
		
  	        <MapContainer
          center={[59.65, 17.95]}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
          ref={mapRef}
          zoomControl={false}
        >
          <ZoomBarControl position="topleft" />
          <TileLayer url={basemapUrls[basemap]} />
          <ScaleControl position="bottomleft" />
          {!pdfMode && <MeasureTool />}
          {!pdfMode && !modalFeature && <CoordinateInfoBox surfaces={surfaces} />}
          
          {/* Map state tracker for optimized layer fetching */}
          <MapStateTracker onMapStateChange={handleMapStateChange} />
          
          {/* === NYTT: "Användardata" lager visas om togglad i LayerPanel === */}
          {showUserData && (
            <FeatureGroup>
              {/* Drawing tools: EditControl */}
              {!pdfMode && <EditControl
                position="topright"
                draw={{
                  marker: true,
                  polyline: true,
                  polygon: true,
                  rectangle: false,
                  circle: true,
                  circlemarker: false
                }}
                edit={{ edit: false }}
                remove={{ remove: true }}
                onCreated={onCreated}
                onDeleted={handleDeleted}
              />}
              {renderFeatures()}
              {!pdfMode && uploadedGeoJson && <GeoJSON data={uploadedGeoJson} />}
              {!pdfMode && <PenetrationMarkers analysis={analysis} />}
            </FeatureGroup>
          )}

          {/* PDF maps contain the input obstacles on the basemap. All other
              overlays share this guard, including procedure areas and aids. */}
          {!pdfMode && <>
          {/* Flygplatser */}
          {showSwedishAirports && Array.isArray(airports) && (
            <FeatureGroup>
              {airports
                .filter(ap => ap && ap.arp_latitude && ap.arp_longitude)
                .map(ap => (
                  <Marker
                    key={ap.airport_name}
                    position={[parseFloat(ap.arp_latitude), parseFloat(ap.arp_longitude)]}
                    icon={airportIcon}
                  >
                    <Tooltip><b>{ap.airport_name}</b></Tooltip>
                  </Marker>
                ))}
            </FeatureGroup>
          )}

          {/* ENR NAVAIDS */}
          {showNavaids && isAuthenticated && (
            <FeatureGroup>
              {navInputsData
                .filter(feature =>
                  feature &&
                  feature.properties?.nav_enr === true &&
                  feature.geometry &&
                  Array.isArray(feature.geometry.coordinates) &&
                  feature.geometry.coordinates.length === 2
                )
                .map(feature => {
                  const [lon, lat] = feature.geometry.coordinates;
                  const p = feature.properties;
                  return (
                    <Marker key={`ni-enr-${p.id}`} position={[lat, lon]} icon={getNavaidIcon(p.nav_aid_type)}>
                      <Tooltip>
                        {getNavAidTypeLabel(p.nav_aid_type)}{p.nav_ident ? ` (${p.nav_ident})` : ''}<br />
                        {p.airport_code}{p.runway_designation ? ` / ${p.runway_designation}` : ''}{p.nav_freq ? ` – ${p.nav_freq}` : ''}
                      </Tooltip>
                    </Marker>
                  );
                })}
            </FeatureGroup>
          )}

          {/* NAV */}
          {showNav && isAuthenticated && (
            <FeatureGroup>
              {navInputsData
                .filter(feature =>
                  feature &&
                  feature.properties?.nav_enr !== true &&
                  feature.geometry &&
                  Array.isArray(feature.geometry.coordinates) &&
                  feature.geometry.coordinates.length === 2
                )
                .map(feature => {
                  const [lon, lat] = feature.geometry.coordinates;
                  const p = feature.properties;
                  return (
                    <Marker key={`ni-nav-${p.id}`} position={[lat, lon]} icon={getNavaidIcon(p.nav_aid_type)}>
                      <Tooltip>
                        {getNavAidTypeLabel(p.nav_aid_type)}{p.nav_ident ? ` (${p.nav_ident})` : ''}<br />
                        {p.airport_code}{p.runway_designation ? ` / ${p.runway_designation}` : ''}{p.nav_freq ? ` – ${p.nav_freq}` : ''}
                      </Tooltip>
                    </Marker>
                  );
                })}
            </FeatureGroup>
          )}

          {/* Waypoints */}
          {showWaypoints && isAuthenticated && (
            <FeatureGroup>
              {waypointsData
                .filter(f =>
                  f &&
                  f.geometry &&
                  Array.isArray(f.geometry.coordinates) &&
                  f.geometry.coordinates.length === 2
                )
                .map(feature => {
                  const [lon, lat] = feature.geometry.coordinates;
                  const p = feature.properties;
                  const wptIcon = L.divIcon({
                    className: '',
                    html: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14"><polygon points="7,1 13,13 1,13" fill="none" stroke="#7b1fa2" stroke-width="2"/></svg>`,
                    iconSize: [14, 14],
                    iconAnchor: [7, 13],
                  });
                  return (
                    <Marker key={`wpt-${p.id}`} position={[lat, lon]} icon={wptIcon}>
                      <Tooltip>
                        <b>{p.wpt_ident}</b><br />
                        {p.airport_name || p.airport_code || '—'}{p.airport_code ? ` (${p.airport_code})` : ''}
                      </Tooltip>
                    </Marker>
                  );
                })}
            </FeatureGroup>
          )}

          {/* TMA/TIA */}
          {showTmaTia && Array.isArray(tmaTia) && (
            <FeatureGroup>
              {tmaTia
                .filter(f =>
                  f &&
                  f.geometry &&
                  Array.isArray(f.geometry.coordinates) &&
                  f.geometry.coordinates.length > 0
                )
                .map(feature => {
                  const coords = feature.geometry.coordinates[0].map(([lon, lat]) => [lat, lon]);
                  const p = feature.properties;
                  return (
                <Polygon key={p.id} positions={coords} color="orange">
				<Tooltip>
				<b>{stripCoordinatesFromNameAndLimits(p.name_and_limits) || p.airspace_type}</b>
				<div>{p.airspace_type && `Typ: ${p.airspace_type}`}</div>
				<div>{p.service_unit}</div>
				</Tooltip>
				</Polygon>
                  );
                })}
            </FeatureGroup>
          )}

          {/* CTR/TIZ/RMZ */}
          {showCtrTizRmz && Array.isArray(ctrTizRmz) && (
            <FeatureGroup>
              {ctrTizRmz
                .filter(f =>
                  f &&
                  f.geometry &&
                  Array.isArray(f.geometry.coordinates) &&
                  f.geometry.coordinates.length > 0
                )
                .map(feature => {
                  const coords = feature.geometry.coordinates[0].map(([lon, lat]) => [lat, lon]);
                  const p = feature.properties;
                  return (
                    <Polygon key={p.id} positions={coords} color="red">
                      <Tooltip>
                        <b>{p.airspace_name}</b>
                        <div>{p.lower_limit} – {p.upper_limit}</div>
                        <div>{p.airport_code}</div>
                      </Tooltip>
                    </Polygon>
                  );
                })}
            </FeatureGroup>
          )}

          {/* ICAO Doc 015 (Surfaces) */}
          {showSurfaces && Array.isArray(surfaces) && (
            <FeatureGroup>
              {surfaces
                .filter(
                  surface =>
                    surface &&
                    surface.properties &&
                    surface.properties.regulation === "ICAO Doc 015" &&
                    surface.geometry &&
                    surface.geometry.coordinates &&
                    surface.geometry.coordinates.length > 0
                )
                .flatMap(surface => {
                  const geom = surface.geometry;
                  const props = surface.properties;
                  
                  // Hantera både Polygon och MultiPolygon
                  if (geom.type === "Polygon") {
                    return (
                      <Polygon
                        key={props.id}
                        positions={geom.coordinates[0].map(([lon, lat]) => [lat, lon])}
                        color="blue"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    );
                  } else if (geom.type === "MultiPolygon") {
                    return geom.coordinates.map((polygon, idx) => (
                      <Polygon
                        key={`${props.id}-${idx}`}
                        positions={polygon[0].map(([lon, lat]) => [lat, lon])}
                        color="blue"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    ));
                  }
                  return [];
                })}
            </FeatureGroup>
          )}

          {/* EASA */}
          {showEasa && Array.isArray(surfaces) && (
            <FeatureGroup>
              {surfaces
                .filter(
                  surface =>
                    surface &&
                    surface.properties &&
                    surface.properties.regulation === "EASA" &&
                    surface.geometry &&
                    surface.geometry.coordinates &&
                    surface.geometry.coordinates.length > 0
                )
                .flatMap(surface => {
                  const geom = surface.geometry;
                  const props = surface.properties;
                  
                  // Hantera både Polygon och MultiPolygon
                  if (geom.type === "Polygon") {
                    return (
                      <Polygon
                        key={props.id}
                        positions={geom.coordinates[0].map(([lon, lat]) => [lat, lon])}
                        color="purple"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    );
                  } else if (geom.type === "MultiPolygon") {
                    return geom.coordinates.map((polygon, idx) => (
                      <Polygon
                        key={`${props.id}-${idx}`}
                        positions={polygon[0].map(([lon, lat]) => [lat, lon])}
                        color="purple"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    ));
                  }
                  return [];
                })}
            </FeatureGroup>
          )}

          {/* PANS OPS */}
          {showPansOps && Array.isArray(surfaces) && (
            <FeatureGroup>
              {surfaces
                .filter(
                  surface =>
                    surface &&
                    surface.properties &&
                    surface.properties.regulation === "PANS OPS" &&
                    surface.geometry &&
                    surface.geometry.coordinates &&
                    surface.geometry.coordinates.length > 0
                )
                .flatMap(surface => {
                  const geom = surface.geometry;
                  const props = surface.properties;
                  
                  // Hantera både Polygon och MultiPolygon
                  if (geom.type === "Polygon") {
                    return (
                      <Polygon
                        key={props.id}
                        positions={geom.coordinates[0].map(([lon, lat]) => [lat, lon])}
                        color="green"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    );
                  } else if (geom.type === "MultiPolygon") {
                    return geom.coordinates.map((polygon, idx) => (
                      <Polygon
                        key={`${props.id}-${idx}`}
                        positions={polygon[0].map(([lon, lat]) => [lat, lon])}
                        color="green"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    ));
                  }
                  return [];
                })}
            </FeatureGroup>
          )}

          {/* Annex 14 */}
          {showAnnex14 && Array.isArray(surfaces) && (
            <FeatureGroup>
              {surfaces
                .filter(
                  surface =>
                    surface &&
                    surface.properties &&
                    (surface.properties.regulation === "ANNEX 14" || surface.properties.regulation === "Annex 14") &&
                    surface.geometry &&
                    surface.geometry.coordinates &&
                    surface.geometry.coordinates.length > 0
                )
                .flatMap(surface => {
                  const geom = surface.geometry;
                  const props = surface.properties;
                  
                  // Hantera både Polygon och MultiPolygon
                  if (geom.type === "Polygon") {
                    return (
                      <Polygon
                        key={props.id}
                        positions={geom.coordinates[0].map(([lon, lat]) => [lat, lon])}
                        color="orange"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    );
                  } else if (geom.type === "MultiPolygon") {
                    return geom.coordinates.map((polygon, idx) => (
                      <Polygon
                        key={`${props.id}-${idx}`}
                        positions={polygon[0].map(([lon, lat]) => [lat, lon])}
                        color="orange"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    ));
                  }
                  return [];
                })}
            </FeatureGroup>
          )}

          {/* eTOD */}
          {showEtod && Array.isArray(surfaces) && (
            <FeatureGroup>
              {surfaces
                .filter(
                  surface =>
                    surface &&
                    surface.properties &&
                    (surface.properties.regulation === "etod" || surface.properties.regulation === "eTOD") &&
                    surface.geometry &&
                    surface.geometry.coordinates &&
                    surface.geometry.coordinates.length > 0
                )
                .flatMap(surface => {
                  const geom = surface.geometry;
                  const props = surface.properties;
                  
                  // Hantera både Polygon och MultiPolygon
                  if (geom.type === "Polygon") {
                    return (
                      <Polygon
                        key={props.id}
                        positions={geom.coordinates[0].map(([lon, lat]) => [lat, lon])}
                        color="red"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    );
                  } else if (geom.type === "MultiPolygon") {
                    return geom.coordinates.map((polygon, idx) => (
                      <Polygon
                        key={`${props.id}-${idx}`}
                        positions={polygon[0].map(([lon, lat]) => [lat, lon])}
                        color="red"
                      >
                        <Tooltip>
                          <b>{props.surface_name}</b>
                          <div>{props.airport_name}</div>
                          <div>{props.regulation}</div>
                        </Tooltip>
                      </Polygon>
                    ));
                  }
                  return [];
                })}
            </FeatureGroup>
          )}

          {/* GeoJSON-lager */}
          {showGeoJson && uploadedGeoJson && (
            <FeatureGroup>
              <GeoJSON data={uploadedGeoJson} />
            </FeatureGroup>
          )}
          
          {/* OMNI Obstacle Markers */}
          {omniAnalysis && omniAnalysis.results && (
            <FeatureGroup>
              {omniAnalysis.results.map((result, runwayIdx) => (
                <React.Fragment key={runwayIdx}>
                  {result.obstacles && result.obstacles.length > 0 &&
                    result.obstacles.map((obs, obsIdx) => {
                      if (!obs.obstacleLat || !obs.obstacleLon) return null;
                      
                      const obstacleId = `${runwayIdx}-${obsIdx}`;
                      const isSelected = selectedObstacles.has(obstacleId);
                      
                      // Never downgrade to green if the obstacle has any penetration in any analysis
                      // (surface, OAS, VSS, shielding, etc.). featureNameImpactMap captures all of those.
                      // Also respect OMNI's own per-position criticality.
                      const hasAnyPenetration = featureNameImpactMap.get(obs.obstacleName) === true;
                      const markerColor = (obs.isCritical || hasAnyPenetration) ? 'red'
                        : (obs.isCloseIn ? 'orange' : 'green');
                      
                      // Create icon with selection styling
                      const iconUrl = `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${markerColor}.png`;
                      const iconSize = isSelected ? [30, 49] : [25, 41];
                      const iconAnchor = isSelected ? [15, 49] : [12, 41];
                      
                      const obstacleIcon = L.icon({
                        iconUrl,
                        shadowUrl: "/images/marker-shadow.png",
                        iconSize,
                        iconAnchor,
                        popupAnchor: [0, -41],
                        className: isSelected ? 'omni-marker-selected' : 'omni-marker'
                      });
                      
                      return (
                        <Marker
                          key={obstacleId}
                          position={[obs.obstacleLat, obs.obstacleLon]}
                          icon={obstacleIcon}
                          eventHandlers={{
                            click: () => handleMarkerClick(runwayIdx, obsIdx)
                          }}
                        >
                          <Tooltip>
                            <div style={{ fontSize: '12px' }}>
                              <b>{obs.obstacleName}</b><br/>
                              {result.input.airport_code} - Runway {result.input.runway_designation}<br/>
                              Area: {obs.areaType}<br/>
                              Elev: {obs.obstacleElevation.toFixed(2)}m<br/>
                              PDG: {obs.pdgRequired.toFixed(2)}%<br/>
                              {obs.isCritical && <span style={{color: '#d32f2f', fontWeight: 'bold'}}>⚠ CRITICAL</span>}
                              {obs.isCloseIn && !obs.isCritical && <span style={{color: '#ff6f00', fontWeight: 'bold'}}>⚠ CLOSE-IN</span>}
                            </div>
                          </Tooltip>
                        </Marker>
                      );
                    })
                  }
                </React.Fragment>
              ))}
            </FeatureGroup>
          )}

          {/* OAS Obstacle Markers */}
          {oasAnalysis && oasAnalysis.results && (isAuthenticated && user?.betald === 'JA') && (
            <FeatureGroup>
              {oasAnalysis.results.map((result, resultIdx) => (
                <React.Fragment key={resultIdx}>
                  {result.categoryResults && result.categoryResults.map((catRes, catIdx) => (
                    <React.Fragment key={catIdx}>
                      {catRes.obstacles && catRes.obstacles.map((obs, obsIdx) => {
                        if (!obs.obstacleLat || !obs.obstacleLon) return null;
                        const obstacleId = `oas-${resultIdx}-${catIdx}-${obsIdx}`;
                        const isSelected = selectedOasObstacles.has(obstacleId);
                        // Never downgrade to green if the obstacle has any penetration in any analysis.
                        const hasAnyPenetrationOas = featureNameImpactMap.get(obs.obstacleName) === true;
                        const markerColor = (obs.isCritical || hasAnyPenetrationOas) ? 'red' : 'green';
                        const iconUrl = `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${markerColor}.png`;
                        const iconSize = isSelected ? [30, 49] : [25, 41];
                        const iconAnchor = isSelected ? [15, 49] : [12, 41];
                        const oasIcon = L.icon({
                          iconUrl,
                          shadowUrl: '/images/marker-shadow.png',
                          iconSize,
                          iconAnchor,
                          popupAnchor: [0, -41],
                          className: isSelected ? 'oas-marker-selected' : 'oas-marker'
                        });
                        return (
                          <Marker
                            key={obstacleId}
                            position={[obs.obstacleLat, obs.obstacleLon]}
                            icon={oasIcon}
                            eventHandlers={{
                              click: () => handleOasMarkerClick(resultIdx, catIdx, obsIdx)
                            }}
                          >
                            <Tooltip>
                              <div style={{ fontSize: '12px' }}>
                                <b>{obs.obstacleName}</b><br/>
                                {result.input.airport_code} – RWY {result.input.runway_designation}<br/>
                                {result.input.oas_category} – Cat {catRes.acCategory}<br/>
                                Surface: {obs.surface}<br/>
                                OCA: {obs.ocaM != null ? `${obs.ocaM} m` : '—'}<br/>
                                {obs.isCritical && <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠ CRITICAL</span>}
                              </div>
                            </Tooltip>
                          </Marker>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </FeatureGroup>
          )}

          {/* BARO/VNAV Obstacle Markers */}
          {baroVnavAnalysis && baroVnavAnalysis.results && (isAuthenticated && user?.betald === 'JA') && (
            <FeatureGroup>
              {baroVnavAnalysis.results.flatMap((result, resultIdx) =>
                (result.categoryResults || []).flatMap((cat, catIdx) =>
                  (cat.obstacleRows || []).map((row, rowIdx) => {
                    if (!row.obstacleLat || !row.obstacleLon) return null;
                    const id = `baro-${resultIdx}-${catIdx}-${rowIdx}`;
                    const isSelected = selectedBaroObstacles.has(id);
                    const hasAnyPenetration = featureNameImpactMap.get(row.obstacleName) === true;
                    const markerColor = (row.isCritical || hasAnyPenetration) ? 'red' : 'green';
                    const iconUrl = `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${markerColor}.png`;
                    const iconSize = isSelected ? [30, 49] : [25, 41];
                    const iconAnchor = isSelected ? [15, 49] : [12, 41];
                    const baroIcon = L.icon({
                      iconUrl,
                      shadowUrl: '/images/marker-shadow.png',
                      iconSize,
                      iconAnchor,
                      popupAnchor: [0, -41],
                      className: isSelected ? 'baro-marker-selected' : 'baro-marker',
                    });
                    return (
                      <Marker
                        key={id}
                        position={[row.obstacleLat, row.obstacleLon]}
                        icon={baroIcon}
                        eventHandlers={{ click: () => handleBaroMarkerClick(resultIdx, catIdx, rowIdx) }}
                      >
                        <Tooltip>
                          <div style={{ fontSize: '12px' }}>
                            <b>{row.obstacleName}</b><br/>
                            {result.input.airport_code} – RWY {result.input.runway_designation}<br/>
                            Area: {row.area}<br/>
                            OCA: {row.ocaM != null ? `${row.ocaM} m` : '—'}<br/>
                            {row.isCritical && <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠ CRITICAL</span>}
                          </div>
                        </Tooltip>
                      </Marker>
                    );
                  })
                )
              )}
            </FeatureGroup>
          )}

          {/* VSS/OCS Obstacle Markers */}
          {vssAnalysis && vssAnalysis.results && (isAuthenticated && user?.betald === 'JA') && (
            <FeatureGroup>
              {vssAnalysis.results.flatMap((result, resultIdx) => [
                ...(result.vssRows || []).map((row, rowIdx) => {
                  if (!row.obstacleLat || !row.obstacleLon) return null;
                  const id = `vss-${resultIdx}-vss-${rowIdx}`;
                  const isSelected = selectedVssObstacles.has(id);
                  const hasAnyPenetration = featureNameImpactMap.get(row.obstacleName) === true;
                  const markerColor = (row.isCritical || hasAnyPenetration) ? 'red' : 'green';
                  const iconUrl = `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${markerColor}.png`;
                  const iconSize = isSelected ? [30, 49] : [25, 41];
                  const iconAnchor = isSelected ? [15, 49] : [12, 41];
                  const vssIcon = L.icon({
                    iconUrl,
                    shadowUrl: '/images/marker-shadow.png',
                    iconSize,
                    iconAnchor,
                    popupAnchor: [0, -41],
                    className: isSelected ? 'vss-marker-selected' : 'vss-marker',
                  });
                  return (
                    <Marker
                      key={id}
                      position={[row.obstacleLat, row.obstacleLon]}
                      icon={vssIcon}
                      eventHandlers={{ click: () => handleVssMarkerClick(resultIdx, 'vss', rowIdx) }}
                    >
                      <Tooltip>
                        <div style={{ fontSize: '12px' }}>
                          <b>{row.obstacleName}</b><br/>
                          {result.input.airport_code} – RWY {result.input.runway_designation}<br/>
                          Surface: VSS<br/>
                          Diff: {row.differenceM != null ? `${row.differenceM} m` : '—'}<br/>
                          {row.isCritical && <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠ CRITICAL</span>}
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                }),
                ...(result.ocsRows || []).map((row, rowIdx) => {
                  if (!row.obstacleLat || !row.obstacleLon) return null;
                  const id = `vss-${resultIdx}-ocs-${rowIdx}`;
                  const isSelected = selectedVssObstacles.has(id);
                  const hasAnyPenetration = featureNameImpactMap.get(row.obstacleName) === true;
                  const markerColor = (row.isCritical || hasAnyPenetration) ? 'red' : 'green';
                  const iconUrl = `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${markerColor}.png`;
                  const iconSize = isSelected ? [30, 49] : [25, 41];
                  const iconAnchor = isSelected ? [15, 49] : [12, 41];
                  const ocsIcon = L.icon({
                    iconUrl,
                    shadowUrl: '/images/marker-shadow.png',
                    iconSize,
                    iconAnchor,
                    popupAnchor: [0, -41],
                    className: isSelected ? 'vss-marker-selected' : 'vss-marker',
                  });
                  return (
                    <Marker
                      key={id}
                      position={[row.obstacleLat, row.obstacleLon]}
                      icon={ocsIcon}
                      eventHandlers={{ click: () => handleVssMarkerClick(resultIdx, 'ocs', rowIdx) }}
                    >
                      <Tooltip>
                        <div style={{ fontSize: '12px' }}>
                          <b>{row.obstacleName}</b><br/>
                          {result.input.airport_code} – RWY {result.input.runway_designation}<br/>
                          Surface: OCS<br/>
                          Diff: {row.differenceM != null ? `${row.differenceM} m` : '—'}<br/>
                          {row.isCritical && <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠ CRITICAL</span>}
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                }),
              ])}
            </FeatureGroup>
          )}

          {/* Shielding Obstacle Markers */}
          {shieldingAnalysis && shieldingAnalysis.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            // Reproduce same expansion as the shielding table so marker IDs match row IDs
            const shldWithObs = shieldingAnalysis.results.filter(r => r.rowsWithinRadius && r.rowsWithinRadius.length > 0);
            const shldExpanded = shldWithObs.flatMap(result => {
              if (result.input.nav_aid_type !== 'VOR/DME') return [result];
              return ['VOR', 'DME_N'].map(type => ({
                ...result,
                input: { ...result.input, nav_aid_type: type },
                rowsWithinRadius: result.rowsWithinRadius.filter(r => r.navAidType === type),
              })).filter(sub => sub.rowsWithinRadius.length > 0);
            });
            return (
              <FeatureGroup>
                {shldExpanded.flatMap((result, expandedIdx) =>
                  (result.rowsWithinRadius || []).map((row, rowIdx) => {
                    if (!row.obstacleLat || !row.obstacleLon) return null;
                    const id = `shld-${expandedIdx}-${rowIdx}`;
                    const isSelected = selectedShieldingObstacles.has(id);
                    const hasAnyPenetration = featureNameImpactMap.get(row.obstacleName) === true;
                    const markerColor = (row.isCritical || hasAnyPenetration) ? 'red' : 'green';
                    const iconUrl = `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${markerColor}.png`;
                    const iconSize = isSelected ? [30, 49] : [25, 41];
                    const iconAnchor = isSelected ? [15, 49] : [12, 41];
                    const shldIcon = L.icon({
                      iconUrl,
                      shadowUrl: '/images/marker-shadow.png',
                      iconSize,
                      iconAnchor,
                      popupAnchor: [0, -41],
                      className: isSelected ? 'shielding-marker-selected' : 'shielding-marker',
                    });
                    return (
                      <Marker
                        key={id}
                        position={[row.obstacleLat, row.obstacleLon]}
                        icon={shldIcon}
                        eventHandlers={{ click: () => handleShieldingMarkerClick(id) }}
                      >
                        <Tooltip>
                          <div style={{ fontSize: '12px' }}>
                            <b>{row.obstacleName}</b><br/>
                            {row.navAidTypeLabel || row.navAidType}<br/>
                            Diff: {row.heightDeltaM != null ? `${row.heightDeltaM.toFixed(1)} m` : '—'}<br/>
                            {row.isCritical && <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠ CRITICAL</span>}
                          </div>
                        </Tooltip>
                      </Marker>
                    );
                  })
                )}
              </FeatureGroup>
            );
          })()}

          {/* Debug: OAS surface polygons */}
          {(layerOasIls1 || layerOasIls2 || layerOasIls2ap || layerOasSbasApv1 || layerOasSbasCat1) && oasAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            const OAS_SURFACE_COLORS = {
              OFZ: '#ff9800', W: '#2196f3', WS: '#03a9f4',
              X1: '#4caf50', X2: '#4caf50', Y1: '#9c27b0', Y2: '#9c27b0', Z: '#f44336',
            };
            const categoryFilter = new Set();
            if (layerOasIls1)     categoryFilter.add('ILS CAT I');
            if (layerOasIls2)     categoryFilter.add('ILS CAT II');
            if (layerOasIls2ap)   categoryFilter.add('ILS CAT II AP');
            if (layerOasSbasApv1) categoryFilter.add('SBAS APV I');
            if (layerOasSbasCat1) categoryFilter.add('SBAS CAT I');
            return (
              <FeatureGroup>
                {oasAnalysis.results
                  .filter(result => categoryFilter.has(result.input.oas_category))
                  .flatMap((result, rIdx) =>
                  (result.categoryResults || []).flatMap((cat, cIdx) =>
                    cat.surfacePolygons
                      ? Object.entries(cat.surfacePolygons).map(([name, pts]) => (
                          <Polygon
                            key={`oas-dbg-${rIdx}-${cIdx}-${name}`}
                            positions={pts}
                            pathOptions={{
                              color: OAS_SURFACE_COLORS[name] || '#607d8b',
                              fillOpacity: 0.08,
                              weight: 1.5,
                              dashArray: '4 3',
                            }}
                          >
                            <Tooltip sticky>
                              <b>{name}</b> – Cat {cat.acCategory}<br />
                              {result.input.airport_name || result.input.airport_code} RWY {result.input.runway_designation}
                            </Tooltip>
                          </Polygon>
                        ))
                      : []
                  )
                )}
              </FeatureGroup>
            );
          })()}

          {/* Debug: VSS/OCS surface polygons */}
          {(showVssSurfaces || layerVss) && vssAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {vssAnalysis.results.flatMap((result, rIdx) => {
                  const polys = result.debugPolygons;
                  if (!polys) return [];
                  const items = [];
                  if (polys.VSS) {
                    items.push(
                      <Polygon
                        key={`vss-dbg-${rIdx}-vss`}
                        positions={polys.VSS}
                        pathOptions={{ color: '#ff5722', fillOpacity: 0.12, weight: 2, dashArray: '6 3' }}
                      >
                        <Tooltip sticky>
                          <b>VSS</b><br />
                          {result.input.airport_name || result.input.airport_code} RWY {result.input.runway_designation}
                        </Tooltip>
                      </Polygon>
                    );
                  }
                  if (polys.OCS) {
                    items.push(
                      <Polygon
                        key={`vss-dbg-${rIdx}-ocs`}
                        positions={polys.OCS}
                        pathOptions={{ color: '#009688', fillOpacity: 0.12, weight: 2, dashArray: '6 3' }}
                      >
                        <Tooltip sticky>
                          <b>OCS</b><br />
                          {result.input.airport_name || result.input.airport_code} RWY {result.input.runway_designation}
                        </Tooltip>
                      </Polygon>
                    );
                  }
                  return items;
                })}
              </FeatureGroup>
            );
          })()}

          {/* Base Turn protected-area polygons + arrows */}
          {(showHoldingBaseSurfaces || layerBaseTurn) && holdingBaseAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {holdingBaseAnalysis.results.filter(r => r.polygon && r.polygon.length > 0).flatMap((result, rIdx) => {
                  const label = `${result.input.airport_name || result.input.airport_code || ''}${(result.input.nav_type || result.input.nav_ident) ? ' – ' + [result.input.nav_type, result.input.nav_ident].filter(Boolean).join(' ') : ''} (Base Turn)`;
                  const hasCritical = result.criticalCount > 0;
                  const primaryColor = hasCritical ? '#d32f2f' : '#e65100';
                  const items = [];

                  // ── Layer 1: Secondary zone outer boundary (total protection area) ──
                  if (result.secondaryPolygon && result.secondaryPolygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`hb-sec-${rIdx}`}
                        positions={result.secondaryPolygon}
                        pathOptions={{
                          color: hasCritical ? '#e57373' : '#ff8a50',
                          fillColor: hasCritical ? '#e57373' : '#ff8a50',
                          fillOpacity: 0.07,
                          weight: 1.5,
                          dashArray: '4 6',
                        }}
                      >
                        <Tooltip sticky>
                          <b>Secondary zone boundary</b> – {label}<br />
                          MOC = 300→0 m taper (outer ring between primary and this boundary)
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Layer 2: Primary zone (inner protection area) ──
                  // Draw from raw UTM coordinates (locked to the navaid's UTM zone via
                  // result.utmEpsg) so the polygon always uses the same zone that the
                  // backend computation used.  Falls back to the pre-converted lat/lon
                  // from the backend if proj4 is unavailable or UTM data is missing.
                  const primaryPositions =
                    (result.primaryPolygonUTM && result.utmEpsg)
                      ? (utmPolyToLatLon(result.primaryPolygonUTM, result.utmEpsg) || result.primaryPolygon)
                      : result.primaryPolygon;
                  if (primaryPositions && primaryPositions.length > 0) {
                    items.push(
                      <Polygon
                        key={`hb-pri-${rIdx}`}
                        positions={primaryPositions}
                        pathOptions={{
                          color: primaryColor,
                          fillColor: primaryColor,
                          fillOpacity: 0.18,
                          weight: 2,
                        }}
                      >
                        <Tooltip sticky>
                          <b>Base Turn – Primary zone</b><br />
                          {label}<br />
                          Alt: {result.input.altitude_ft} ft | Track: {parseFloat(result.input.inbound_track_deg).toFixed(2)}°{result.input.utm_grid_inbound_deg !== null && result.input.utm_grid_inbound_deg !== undefined ? ` (grid: ${result.input.utm_grid_inbound_deg.toFixed(2)}°)` : ''}<br />
                          Nav fix: {result.input.nav_lat?.toFixed(5)}°N, {result.input.nav_lon?.toFixed(5)}°E<br />
                          {result.input.nav_utm_x != null
                            ? <span style={{fontSize:'0.9em',color:'#555'}}>UTM X={result.input.nav_utm_x} Y={result.input.nav_utm_y}{result.utmEpsg ? ` (${result.utmEpsg})` : ''}</span>
                            : null}<br />
                          MOC = 300 m<br />
                          {hasCritical
                            ? <span style={{ color: '#d32f2f' }}>⚠ {result.criticalCount} critical</span>
                            : <span style={{ color: '#388e3c' }}>✓ No critical obstacles</span>}
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Layer 3: Nominal track (area1 reference, thin dashed) ──
                  // Drawn on top of primary so it is visible as the nominal centreline.
                  // Not used for obstacle evaluation – reference only.
                  if (result.polygon && result.polygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`hb-nom-${rIdx}`}
                        positions={result.polygon}
                        pathOptions={{
                          color: '#1565c0',
                          fillOpacity: 0,
                          weight: 1.5,
                          dashArray: '5 5',
                        }}
                      >
                        <Tooltip sticky>
                          <b>Nominal track (area1)</b> – {label}<br />
                          Reference only – not used for obstacle evaluation
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Layer 4: Arrows ──
                  if (result.arrows) {
                    result.arrows.forEach((arrow, aIdx) => {
                      if (arrow.triangle && arrow.triangle.length === 3) {
                        items.push(
                          <Polygon
                            key={`hb-arr-${rIdx}-${aIdx}`}
                            positions={arrow.triangle}
                            pathOptions={{
                              color: primaryColor,
                              fillColor: primaryColor,
                              fillOpacity: 0.9,
                              weight: 1,
                            }}
                          >
                            <Tooltip sticky>
                              <b>Arrow {aIdx + 1}</b> – heading {arrow.headingDeg?.toFixed(1)}°
                            </Tooltip>
                          </Polygon>
                        );
                      }
                    });
                  }

                  // ── Layer 6: Inbound / Outbound track lines + bearing labels ──
                  const navLat = result.input.nav_lat;
                  const navLon = result.input.nav_lon;
                  if (navLat != null && navLon != null) {
                    const inbDeg  = parseFloat(result.input.inbound_track_deg);
                    const outbDeg = result.geometry?.true_outbound_deg;
                    const lineLen = Math.min(
                      Math.max((result.geometry?.outerDistM || 5000) * 0.8, 4000),
                      18000
                    );

                    // Inbound track label (approach side)
                    const inbFrom = destPoint(navLat, navLon, (inbDeg + 180) % 360, lineLen);
                    items.push(
                      <Marker
                        key={`hb-inb-lbl-${rIdx}`}
                        position={inbFrom}
                        icon={L.divIcon({
                          html: `<div style="background:rgba(2,119,189,0.9);color:#fff;padding:2px 7px;border-radius:3px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.45)">IBD ${inbDeg.toFixed(1)}°</div>`,
                          className: '',
                          iconSize: null,
                          iconAnchor: [30, 10],
                        })}
                      />
                    );

                    // Outbound track label (only when available)
                    if (outbDeg != null) {
                      const outbTo = destPoint(navLat, navLon, outbDeg, lineLen);
                      items.push(
                        <Marker
                          key={`hb-outb-lbl-${rIdx}`}
                          position={outbTo}
                          icon={L.divIcon({
                            html: `<div style="background:rgba(46,125,50,0.9);color:#fff;padding:2px 7px;border-radius:3px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.45)">OBD ${outbDeg.toFixed(1)}°</div>`,
                            className: '',
                            iconSize: null,
                            iconAnchor: [30, 10],
                          })}
                        />
                      );
                    }
                  }

                  return items;
                })}
              </FeatureGroup>
            );
          })()}

          {/* ── Holding / Racetrack by Time surface polygons ── */}
          {(showHoldingRacetrackSurfaces || showHoldingSurfaces || showRacetrackSurfaces) && holdingRacetrackAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {holdingRacetrackAnalysis.results.filter(r => {
                  if (!r.polygon || r.polygon.length === 0) return false;
                  const usedFor = r.input.used_for_type || 'Holding';
                  // The inline "Show Holding/Racetrack surfaces" button shows all types;
                  // the per-type layer-panel toggles filter by type.
                  if (showHoldingRacetrackSurfaces) return true;
                  if (usedFor === 'Holding' && showHoldingSurfaces) return true;
                  if (usedFor === 'Racetrack' && showRacetrackSurfaces) return true;
                  return false;
                }).flatMap((result, rIdx) => {
                  const usedFor = result.input.used_for_type || 'Holding';
                  const label = `${result.input.airport_name || result.input.airport_code || ''}${(result.input.nav_type || result.input.nav_ident) ? ' – ' + [result.input.nav_type, result.input.nav_ident].filter(Boolean).join(' ') : ''} (${usedFor})`;
                  const hasCritical = result.criticalCount > 0;
                  const primaryColor  = hasCritical ? '#6a1b9a' : '#00695c';  // teal/purple for racetrack
                  const secondaryColor = hasCritical ? '#ab47bc' : '#26a69a';
                  const items = [];

                  // ── Secondary zone bands ──
                  {
                    // For Holding: render each 1-NM band with its MOC label.
                    // For Racetrack: falls back to a single band via secondaryPolygon.
                    const isHoldingType = (result.input.used_for_type === 'Holding');
                    const catH = result.input.use_category_h;
                    const bands = (result.holdingSecondaryBands && result.holdingSecondaryBands.length > 0)
                      ? result.holdingSecondaryBands
                      : (result.secondaryPolygon ? [result.secondaryPolygon] : []);

                    // MOC labels per band for standard Holding stepped table
                    const holdingBandMOC    = ['300 m', '150 m', '120 m', '90 m', '60 m'];
                    const catHBandMOC       = ['300→0 m linear', '300→0 m linear'];

                    // Inner boundary for ring rendering: primary/merged polygon forms the
                    // hole of band[0]; each subsequent band uses the previous band as hole.
                    // This ensures secondary bands appear as rings around the primary, not
                    // as solid filled polygons that cover the primary interior (matching the algorithm
                    // GetOffsetCurves rendering of concentric ring outlines).
                    const secInnerBase = result.mergedPrimaryPolygon || result.primaryPolygon;

                    bands.forEach((band, bIdx) => {
                      if (!band || band.length === 0) return;
                      // Opacity decreases with distance from primary.  With ring rendering
                      // there is no accumulation, so use higher per-ring values.
                      const fillOpacity = Math.max(0.05, 0.20 - bIdx * 0.03);
                      let mocLabel;
                      if (!isHoldingType) {
                        mocLabel = catH ? '90→0 m taper' : '300→0 m taper';
                      } else if (catH) {
                        mocLabel = catHBandMOC[bIdx] ?? '0 m';
                      } else {
                        mocLabel = holdingBandMOC[bIdx] ?? '60 m';
                      }
                      // Build ring positions: [outerRing, holeRing] so only the annular
                      // region between this band and the next-inner boundary is filled.
                      const innerHole = bIdx === 0
                        ? secInnerBase
                        : (bands[bIdx - 1] || secInnerBase);
                      const ringPositions = (innerHole && innerHole.length > 2)
                        ? [band, innerHole]
                        : band;
                      items.push(
                        <Polygon
                          key={`rt-sec-${rIdx}-${bIdx}`}
                          positions={ringPositions}
                          pathOptions={{
                            color: secondaryColor,
                            fillColor: secondaryColor,
                            fillOpacity,
                            weight: 1.5,
                            dashArray: '4 6',
                          }}
                        >
                          <Tooltip sticky>
                            <b>Secondary zone {isHoldingType ? `band ${bIdx + 1} (${bIdx}–${bIdx + 1} NM)` : 'boundary'}</b> – {label}<br />
                            MOC = {mocLabel}
                          </Tooltip>
                        </Polygon>
                      );
                    });
                  }

                  // ── Primary zone ──
                  // When entry areas are active, show the merged primary (racetrack + entry sectors
                  // combined as convex hull) as the main filled zone.  The racetrack-only primary is
                  // shown as a thin dashed reference outline in that case.
                  const racetrackPositionsFromBulge =
                    (result.primaryKeyVertices && result.primaryKeyVertices.length > 0)
                      ? expandBulgePolylineToLatLon(result.primaryKeyVertices, result.utmEpsg)
                      : null;
                  const racetrackPositions =
                    (racetrackPositionsFromBulge && racetrackPositionsFromBulge.length > 0)
                      ? racetrackPositionsFromBulge
                      : ((result.primaryPolygonUTM && result.utmEpsg)
                          ? (utmPolyToLatLon(result.primaryPolygonUTM, result.utmEpsg) || result.primaryPolygon)
                          : result.primaryPolygon);
                  const hasEntryAreas = result.mergedPrimaryPolygon &&
                    result.mergedPrimaryPolygon !== result.primaryPolygon;
                  // When entry_intercept=true, the convex-hull-simplified P-points (mergedPolylineAreaVertices)
                  // go directly from P11 to P12; the raw union polygon (mergedPrimaryPolygon) still contains
                  // V11-V14 intermediate points and must not be used for rendering.
                  // Arc segments (bulge ≠ 0) are expanded to interpolated points so the shape
                  // shows smooth curves rather than straight chords.
                  const mergedPositions = result.mergedPolylineAreaVertices && result.mergedPolylineAreaVertices.length > 0
                    ? expandBulgePolylineToLatLon(result.mergedPolylineAreaVertices, result.utmEpsg)
                    : result.mergedPrimaryPolygon;
                  const primaryPositions = hasEntryAreas
                    ? ((mergedPositions && mergedPositions.length > 0) ? mergedPositions : racetrackPositions)
                    : racetrackPositions;
                  if (primaryPositions && primaryPositions.length > 0) {
                    items.push(
                      <Polygon
                        key={`rt-pri-${rIdx}`}
                        positions={primaryPositions}
                        pathOptions={{
                          color: primaryColor,
                          fillColor: primaryColor,
                          fillOpacity: 0.18,
                          weight: 2,
                        }}
                      >
                        <Tooltip sticky>
                          <b>{usedFor} – Primary zone</b>{hasEntryAreas ? ' (incl. entry sectors)' : ''}<br />
                          {label}<br />
                          Alt: {result.input.altitude_ft} ft | Track: {parseFloat(result.input.inbound_track_deg).toFixed(2)}°<br />
                          MOC = {result.input.use_category_h ? '90' : '300'} m<br />
                          {hasCritical
                            ? <span style={{ color: '#d32f2f' }}>⚠ {result.criticalCount} critical</span>
                            : <span style={{ color: '#388e3c' }}>✓ No critical obstacles</span>}
                        </Tooltip>
                      </Polygon>
                    );
                  }
                  // Racetrack-only primary reference outline removed — users see only the
                  // final merged primary zone (basic + any entry sectors).

                  // ── Nominal track (reference, dashed) ──
                  if (result.polygon && result.polygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rt-nom-${rIdx}`}
                        positions={result.polygon}
                        pathOptions={{ color: '#004d40', fillOpacity: 0, weight: 1.5, dashArray: '5 5' }}
                      >
                        <Tooltip sticky>
                          <b>Nominal racetrack</b> – {label}<br />
                          Reference only – not used for obstacle evaluation
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Tolerance polygon ──
                  if (result.tolerancePolygon && result.tolerancePolygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rt-tol-${rIdx}`}
                        positions={result.tolerancePolygon}
                        pathOptions={{ color: '#f57c00', fillColor: '#f57c00', fillOpacity: 0.08, weight: 1.5, dashArray: '3 4' }}
                      >
                        <Tooltip sticky>
                          <b>Fix tolerance area</b> – {label}
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // Entry area polygon overlays removed — intercept, sector1, sector2, and sectors12
                  // are all merged into the primary zone before display. No standalone entry overlays
                  // are shown to the user.

                  // ── Arrows ──
                  if (result.arrows) {
                    result.arrows.forEach((arrow, aIdx) => {
                      if (arrow.triangle && arrow.triangle.length === 3) {
                        items.push(
                          <Polygon
                            key={`rt-arr-${rIdx}-${aIdx}`}
                            positions={arrow.triangle}
                            pathOptions={{ color: primaryColor, fillColor: primaryColor, fillOpacity: 0.9, weight: 1 }}
                          >
                            <Tooltip sticky>
                              <b>Arrow {aIdx + 1}</b> – heading {arrow.headingDeg?.toFixed(1)}°
                            </Tooltip>
                          </Polygon>
                        );
                      }
                    });
                  }

                  return items;
                })}
              </FeatureGroup>
            );
          })()}

          {/* ── Holding / Racetrack by Distance Protected-Area Surfaces ──────── */}
          {showHoldingDistSurfaces && holdingRacetrackAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {holdingRacetrackAnalysis.results.filter(r => {
                  if (!r.polygon || r.polygon.length === 0) return false;
                  return r.input.limiting_dme_nm != null;
                }).flatMap((result, rIdx) => {
                  const usedFor = result.input.used_for_type || 'Holding';
                  const label = `${result.input.airport_name || result.input.airport_code || ''}${(result.input.nav_type || result.input.nav_ident) ? ' – ' + [result.input.nav_type, result.input.nav_ident].filter(Boolean).join(' ') : ''} (${usedFor} by Dist)`;
                  const hasCritical = result.criticalCount > 0;
                  const primaryColor   = hasCritical ? '#6a1b9a' : '#00695c';
                  const secondaryColor = hasCritical ? '#ab47bc' : '#26a69a';
                  const items = [];

                  // ── Secondary zone bands ──
                  const isHoldingType = (result.input.used_for_type === 'Holding');
                  const catH = result.input.use_category_h;
                  const bands = (result.holdingSecondaryBands && result.holdingSecondaryBands.length > 0)
                    ? result.holdingSecondaryBands
                    : (result.secondaryPolygon ? [result.secondaryPolygon] : []);

                  const holdingBandMOC = ['300 m', '150 m', '120 m', '90 m', '60 m'];
                  const catHBandMOC    = ['300→0 m linear', '300→0 m linear'];

                  const secInnerBase = result.mergedPrimaryPolygon || result.primaryPolygon;

                  bands.forEach((band, bIdx) => {
                    if (!band || band.length === 0) return;
                    const fillOpacity = Math.max(0.05, 0.20 - bIdx * 0.03);
                    let mocLabel;
                    if (!isHoldingType) {
                      mocLabel = catH ? '90→0 m taper' : '300→0 m taper';
                    } else if (catH) {
                      mocLabel = catHBandMOC[bIdx] ?? '0 m';
                    } else {
                      mocLabel = holdingBandMOC[bIdx] ?? '60 m';
                    }
                    const innerHole = bIdx === 0
                      ? secInnerBase
                      : (bands[bIdx - 1] || secInnerBase);
                    const ringPositions = (innerHole && innerHole.length > 2)
                      ? [band, innerHole]
                      : band;
                    items.push(
                      <Polygon
                        key={`rtd-sec-${rIdx}-${bIdx}`}
                        positions={ringPositions}
                        pathOptions={{
                          color: secondaryColor,
                          fillColor: secondaryColor,
                          fillOpacity,
                          weight: 1,
                          dashArray: '4 4',
                        }}
                      >
                        <Tooltip sticky>
                          <b>Secondary zone (Band {bIdx + 1})</b> – {label}<br />
                          MOC = {mocLabel}
                        </Tooltip>
                      </Polygon>
                    );
                  });

                  // ── Primary merged zone ──
                  const hasEntryAreas = result.mergedPrimaryPolygon &&
                    result.mergedPrimaryPolygon !== result.primaryPolygon;
                  const mergedPositions = result.mergedPolylineAreaVertices && result.mergedPolylineAreaVertices.length > 0
                    ? expandBulgePolylineToLatLon(result.mergedPolylineAreaVertices, result.utmEpsg)
                    : result.mergedPrimaryPolygon;
                  const primaryPositionsFromBulge =
                    (result.primaryKeyVertices && result.primaryKeyVertices.length > 0)
                      ? expandBulgePolylineToLatLon(result.primaryKeyVertices, result.utmEpsg)
                      : null;
                  const basePrimaryPositions =
                    (primaryPositionsFromBulge && primaryPositionsFromBulge.length > 0)
                      ? primaryPositionsFromBulge
                      : ((result.primaryPolygonUTM && result.utmEpsg)
                          ? (utmPolyToLatLon(result.primaryPolygonUTM, result.utmEpsg) || result.primaryPolygon)
                          : result.primaryPolygon);
                  const primPoly = hasEntryAreas
                    ? ((mergedPositions && mergedPositions.length > 0) ? mergedPositions : basePrimaryPositions)
                    : basePrimaryPositions;
                  if (primPoly && primPoly.length > 0) {
                    items.push(
                      <Polygon
                        key={`rtd-prim-${rIdx}`}
                        positions={primPoly}
                        pathOptions={{
                          color: primaryColor,
                          fillColor: primaryColor,
                          fillOpacity: 0.15,
                          weight: 2,
                        }}
                      >
                        <Tooltip sticky>
                          <b>Primary zone</b> – {label}<br />
                          Alt: {result.input.altitude_ft} ft | Track: {parseFloat(result.input.inbound_track_deg).toFixed(2)}°<br />
                          Outbound dist: {result.input.limiting_dme_nm} NM<br />
                          MOC = {result.input.use_category_h ? '90' : '300'} m<br />
                          {hasCritical
                            ? <span style={{ color: '#d32f2f' }}>⚠ {result.criticalCount} critical</span>
                            : <span style={{ color: '#388e3c' }}>✓ No critical obstacles</span>}
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Nominal track (dashed) ──
                  if (result.polygon && result.polygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rtd-nom-${rIdx}`}
                        positions={result.polygon}
                        pathOptions={{ color: '#004d40', fillOpacity: 0, weight: 1.5, dashArray: '5 5' }}
                      >
                        <Tooltip sticky>
                          <b>Nominal racetrack</b> – {label}<br />
                          Reference only – not used for obstacle evaluation
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Tolerance polygon ──
                  if (result.tolerancePolygon && result.tolerancePolygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rtd-tol-${rIdx}`}
                        positions={result.tolerancePolygon}
                        pathOptions={{ color: '#f57c00', fillColor: '#f57c00', fillOpacity: 0.08, weight: 1.5, dashArray: '3 4' }}
                      >
                        <Tooltip sticky>
                          <b>Fix tolerance area</b> – {label}
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Arrows ──
                  if (result.arrows) {
                    result.arrows.forEach((arrow, aIdx) => {
                      if (arrow.triangle && arrow.triangle.length === 3) {
                        items.push(
                          <Polygon
                            key={`rtd-arr-${rIdx}-${aIdx}`}
                            positions={arrow.triangle}
                            pathOptions={{ color: primaryColor, fillColor: primaryColor, fillOpacity: 0.7, weight: 1 }}
                          />
                        );
                      }
                    });
                  }

                  return items;
                })}
              </FeatureGroup>
            );
          })()}

          {/* ── RNAV Holding Protected-Area Surfaces ──────────────────────── */}
          {showRnavHoldingSurfaces && rnavHoldingAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {rnavHoldingAnalysis.results.filter(r => r.polygon && r.polygon.length > 0).flatMap((result, rIdx) => {
                  const label = `${result.input.airport_name || result.input.airport_code || ''}${result.input.wpt_ident ? ' – WPT: ' + result.input.wpt_ident : ''} (RNAV Holding)`;
                  const hasCritical = result.criticalCount > 0;
                  const primaryColor  = hasCritical ? '#6a1b9a' : '#00695c';
                  const secondaryColor = hasCritical ? '#ab47bc' : '#26a69a';
                  const items = [];

                  // ── Secondary zone bands ──
                  const isHoldingType = true;  // RNAV holding always uses Holding-type secondary
                  const catH = result.input.use_category_h;
                  const bands = (result.holdingSecondaryBands && result.holdingSecondaryBands.length > 0)
                    ? result.holdingSecondaryBands
                    : (result.secondaryPolygon ? [result.secondaryPolygon] : []);

                  const holdingBandMOC = ['300 m', '150 m', '120 m', '90 m', '60 m'];
                  const catHBandMOC   = ['300→0 m linear', '300→0 m linear'];

                  // Inner boundary for ring rendering (same logic as racetrack section above).
                  const secInnerBase = result.mergedPrimaryPolygon || result.primaryPolygon;

                  bands.forEach((band, bIdx) => {
                    if (!band || band.length === 0) return;
                    // Opacity decreases with distance from primary (non-accumulating rings).
                    const fillOpacity = Math.max(0.05, 0.20 - bIdx * 0.03);
                    const mocLabel = catH ? (catHBandMOC[bIdx] ?? '0 m') : (holdingBandMOC[bIdx] ?? '60 m');
                    const innerHole = bIdx === 0
                      ? secInnerBase
                      : (bands[bIdx - 1] || secInnerBase);
                    const ringPositions = (innerHole && innerHole.length > 2)
                      ? [band, innerHole]
                      : band;
                    items.push(
                      <Polygon
                        key={`rnav-sec-${rIdx}-${bIdx}`}
                        positions={ringPositions}
                        pathOptions={{
                          color: secondaryColor,
                          fillColor: secondaryColor,
                          fillOpacity,
                          weight: 1.5,
                          dashArray: '4 6',
                        }}
                      >
                        <Tooltip sticky>
                          <b>Secondary zone band {bIdx + 1} ({bIdx}–{bIdx + 1} NM)</b> – {label}<br />
                          MOC = {mocLabel}
                        </Tooltip>
                      </Polygon>
                    );
                  });

                  // ── Primary zone ──
                  const mergedPositions = result.mergedPolylineAreaVertices && result.mergedPolylineAreaVertices.length > 0
                    ? expandBulgePolylineToLatLon(result.mergedPolylineAreaVertices, result.utmEpsg)
                    : result.mergedPrimaryPolygon;
                  const primaryPositions = mergedPositions || result.primaryPolygon;
                  if (primaryPositions && primaryPositions.length > 0) {
                    items.push(
                      <Polygon
                        key={`rnav-pri-${rIdx}`}
                        positions={primaryPositions}
                        pathOptions={{
                          color: primaryColor,
                          fillColor: primaryColor,
                          fillOpacity: 0.18,
                          weight: 2,
                        }}
                      >
                        <Tooltip sticky>
                          <b>RNAV Holding – Primary zone</b><br />
                          {label}<br />
                          Alt: {result.input.altitude_ft} ft | Track: {parseFloat(result.input.inbound_track_deg).toFixed(2)}°<br />
                          ATT: {result.input.att_nm} NM | XTT: {result.input.xtt_nm} NM<br />
                          MOC = {catH ? '90' : '300'} m<br />
                          {hasCritical
                            ? <span style={{ color: '#d32f2f' }}>⚠ {result.criticalCount} critical</span>
                            : <span style={{ color: '#388e3c' }}>✓ No critical obstacles</span>}
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Nominal track (reference, dashed) ──
                  if (result.polygon && result.polygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rnav-nom-${rIdx}`}
                        positions={result.polygon}
                        pathOptions={{ color: '#004d40', fillOpacity: 0, weight: 1.5, dashArray: '5 5' }}
                      >
                        <Tooltip sticky>
                          <b>Nominal racetrack</b> – {label}<br />
                          Reference only – not used for obstacle evaluation
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── RNAV tolerance rectangle ──
                  if (result.tolerancePolygon && result.tolerancePolygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rnav-tol-${rIdx}`}
                        positions={result.tolerancePolygon}
                        pathOptions={{ color: '#f57c00', fillColor: '#f57c00', fillOpacity: 0.08, weight: 1.5, dashArray: '3 4' }}
                      >
                        <Tooltip sticky>
                          <b>RNAV WPT tolerance (ATT/XTT)</b> – {label}<br />
                          ATT: {result.input.att_nm} NM | XTT: {result.input.xtt_nm} NM
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Arrows ──
                  if (result.arrows) {
                    result.arrows.forEach((arrow, aIdx) => {
                      if (arrow.triangle && arrow.triangle.length === 3) {
                        items.push(
                          <Polygon
                            key={`rnav-arr-${rIdx}-${aIdx}`}
                            positions={arrow.triangle}
                            pathOptions={{ color: primaryColor, fillColor: primaryColor, fillOpacity: 0.9, weight: 1 }}
                          >
                            <Tooltip sticky>
                              <b>Arrow {aIdx + 1}</b> – heading {arrow.headingDeg?.toFixed(1)}°
                            </Tooltip>
                          </Polygon>
                        );
                      }
                    });
                  }

                  return items;
                })}
              </FeatureGroup>
            );
          })()}

          {/* ── RNP Holding Protected-Area Surfaces ──────────────────────── */}
          {showRnpHoldingSurfaces && rnpHoldingAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {rnpHoldingAnalysis.results.filter(r => r.polygon && r.polygon.length > 0).flatMap((result, rIdx) => {
                  const rnpValue = result.input.rnp_value || 1.0;
                  const label = `${result.input.airport_name || result.input.airport_code || ''}${result.input.wpt_ident ? ' – WPT: ' + result.input.wpt_ident : ''} (RNP ${rnpValue} Holding)`;
                  const hasCritical = result.criticalCount > 0;
                  const primaryColor  = hasCritical ? '#c62828' : '#2e7d32';
                  const secondaryColor = hasCritical ? '#ef5350' : '#66bb6a';
                  const items = [];

                  // ── Secondary zone bands ──
                  const isHoldingType = true;  // RNP holding always uses Holding-type secondary
                  const catH = result.input.use_category_h;
                  const bands = (result.secondaryBands && result.secondaryBands.length > 0)
                    ? result.secondaryBands
                    : (result.secondaryPolygon ? [result.secondaryPolygon] : []);

                  const holdingBandMOC = ['300 m', '150 m', '120 m', '90 m', '60 m'];
                  const catHBandMOC   = ['300→0 m linear', '300→0 m linear'];

                  // Inner boundary for ring rendering
                  const secInnerBase = result.polygon;

                  bands.forEach((band, bIdx) => {
                    const bandPolygon = band.outer || band;
                    if (!bandPolygon || bandPolygon.length === 0) return;
                    const fillOpacity = Math.max(0.05, 0.20 - bIdx * 0.03);
                    const mocLabel = catH ? (catHBandMOC[bIdx] ?? '0 m') : (holdingBandMOC[bIdx] ?? '60 m');
                    const innerHole = bIdx === 0
                      ? secInnerBase
                      : ((bands[bIdx - 1]?.outer || bands[bIdx - 1]) || secInnerBase);
                    const ringPositions = (innerHole && innerHole.length > 2)
                      ? [bandPolygon, innerHole]
                      : bandPolygon;

                    items.push(
                      <Polygon
                        key={`rnp-sec-${rIdx}-${bIdx}`}
                        positions={ringPositions}
                        pathOptions={{ color: secondaryColor, fillColor: secondaryColor, fillOpacity, weight: 1 }}
                      >
                        <Tooltip sticky>
                          <b>Secondary zone {bIdx + 1}</b> – {label}<br />
                          MOC: {mocLabel}
                        </Tooltip>
                      </Polygon>
                    );
                  });

                  // ── Primary area ──
                  if (result.polygon && result.polygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rnp-pri-${rIdx}`}
                        positions={result.polygon}
                        pathOptions={{ color: primaryColor, fillColor: primaryColor, fillOpacity: 0.12, weight: 2 }}
                      >
                        <Tooltip sticky>
                          <b>Primary zone (300 m MOC)</b> – {label}<br />
                          {hasCritical ? '⚠️ Critical obstacles detected!' : 'No critical obstacles'}
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Nominal racetrack ──
                  if (result.nominalPolygon && result.nominalPolygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rnp-nom-${rIdx}`}
                        positions={result.nominalPolygon}
                        pathOptions={{ color: '#616161', fillColor: '#616161', fillOpacity: 0.0, weight: 1.5, dashArray: '4 5' }}
                      >
                        <Tooltip sticky>
                          <b>Nominal racetrack</b> – {label}<br />
                          Reference only – not used for obstacle evaluation
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── RNP tolerance square ──
                  if (result.tolerancePolygon && result.tolerancePolygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`rnp-tol-${rIdx}`}
                        positions={result.tolerancePolygon}
                        pathOptions={{ color: '#ff9800', fillColor: '#ff9800', fillOpacity: 0.08, weight: 1.5, dashArray: '3 4' }}
                      >
                        <Tooltip sticky>
                          <b>RNP WPT tolerance (RNP {rnpValue})</b> – {label}<br />
                          Tolerance: ±{rnpValue} NM
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Arrows ──
                  if (result.nominalArrows) {
                    result.nominalArrows.forEach((arrow, aIdx) => {
                      if (arrow && arrow.length === 3) {
                        items.push(
                          <Polygon
                            key={`rnp-arr-${rIdx}-${aIdx}`}
                            positions={arrow}
                            pathOptions={{ color: primaryColor, fillColor: primaryColor, fillOpacity: 0.9, weight: 1 }}
                          >
                            <Tooltip sticky>
                              <b>Direction arrow {aIdx + 1}</b>
                            </Tooltip>
                          </Polygon>
                        );
                      }
                    });
                  }

                  return items;
                })}
              </FeatureGroup>
            );
          })()}

          {/* ── VOR/DME Holding Protected-Area Surfaces ──────────────────────── */}
          {showVorDmeHoldingSurfaces && vorDmeHoldingAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {vorDmeHoldingAnalysis.results.filter(r => r.polygon && r.polygon.length > 0).flatMap((result, rIdx) => {
                  const usedFor = result.input.used_for_type || 'Holding';
                  const holdingType = result.input.holding_type || 'towards';
                  const label = `${result.input.airport_name || result.input.airport_code || ''}${result.input.nav_ident ? ' – ' + result.input.nav_ident : ''} (VOR/DME ${usedFor} – ${holdingType})`;
                  const hasCritical = result.criticalCount > 0;
                  const primaryColor  = hasCritical ? '#6a1b9a' : '#1565c0';
                  const secondaryColor = hasCritical ? '#ab47bc' : '#42a5f5';
                  const items = [];

                  // ── Secondary zone bands ──
                  const isHolding = usedFor === 'Holding';
                  const bands = (result.holdingSecondaryBands && result.holdingSecondaryBands.length > 0)
                    ? result.holdingSecondaryBands
                    : (result.secondaryPolygon ? [result.secondaryPolygon] : []);

                  // MOC labels for secondary bands
                  const holdingBandMOC = ['300 m', '150 m', '120 m', '90 m', '60 m'];
                  const racetrackMOC = ['300→0 m linear'];

                  // Inner boundary for ring rendering
                  const secInnerBase = result.mergedPrimaryPolygon || result.primaryPolygon || result.polygon;

                  bands.forEach((band, bIdx) => {
                    const bandPolygon = band.outer || band;
                    if (!bandPolygon || bandPolygon.length === 0) return;
                    const fillOpacity = Math.max(0.05, 0.20 - bIdx * 0.03);
                    const mocLabel = isHolding ? (holdingBandMOC[bIdx] ?? '60 m') : (racetrackMOC[bIdx] ?? '300→0 m');
                    const innerHole = bIdx === 0
                      ? secInnerBase
                      : ((bands[bIdx - 1]?.outer || bands[bIdx - 1]) || secInnerBase);
                    const ringPositions = (innerHole && innerHole.length > 2)
                      ? [bandPolygon, innerHole]
                      : bandPolygon;

                    items.push(
                      <Polygon
                        key={`vordme-sec-${rIdx}-${bIdx}`}
                        positions={ringPositions}
                        pathOptions={{ color: secondaryColor, fillColor: secondaryColor, fillOpacity, weight: 1.5, dashArray: '4 6' }}
                      >
                        <Tooltip sticky>
                          <b>Secondary zone band {bIdx + 1}</b> – {label}<br />
                          MOC: {mocLabel}
                        </Tooltip>
                      </Polygon>
                    );
                  });

                  // ── Primary area ──
                  const primaryPositions = result.mergedPrimaryPolygon || result.primaryPolygon;
                  if (primaryPositions && primaryPositions.length > 0) {
                    items.push(
                      <Polygon
                        key={`vordme-pri-${rIdx}`}
                        positions={primaryPositions}
                        pathOptions={{ color: primaryColor, fillColor: primaryColor, fillOpacity: 0.18, weight: 2 }}
                      >
                        <Tooltip sticky>
                          <b>VOR/DME {usedFor} – Primary zone</b><br />
                          {label}<br />
                          Alt: {result.input.altitude_ft} ft | Track: {parseFloat(result.input.inbound_track_deg).toFixed(2)}°<br />
                          Holding DME: {result.input.holding_dme_nm} NM | Limiting DME: {result.input.limiting_dme_nm} NM<br />
                          MOC: {result.input.moc_m || 300} m<br />
                          {hasCritical
                            ? <span style={{ color: '#d32f2f' }}>⚠ {result.criticalCount} critical</span>
                            : <span style={{ color: '#388e3c' }}>✓ No critical obstacles</span>}
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Nominal racetrack ──
                  const nominalPositions = result.nominalPolygon || result.polygon;
                  if (nominalPositions && nominalPositions.length > 0) {
                    items.push(
                      <Polygon
                        key={`vordme-nom-${rIdx}`}
                        positions={nominalPositions}
                        pathOptions={{ color: '#004d40', fillOpacity: 0, weight: 1.5, dashArray: '5 5' }}
                      >
                        <Tooltip sticky>
                          <b>Nominal racetrack</b> – {label}<br />
                          Reference only – not used for obstacle evaluation
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── DME tolerance area (butterfly) ──
                  if (result.tolerancePolygon && result.tolerancePolygon.length > 0) {
                    items.push(
                      <Polygon
                        key={`vordme-tol-${rIdx}`}
                        positions={result.tolerancePolygon}
                        pathOptions={{ color: '#ff9800', fillColor: '#ff9800', fillOpacity: 0.08, weight: 1.5, dashArray: '3 4' }}
                      >
                        <Tooltip sticky>
                          <b>DME fix tolerance</b> – {label}<br />
                          ±5.2° azimuth cone + DME tolerance
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // ── Entry sector polygons ──
                  if (result.entryPolygons) {
                    if (result.entryPolygons.sector1 && result.entryPolygons.sector1.length > 0) {
                      items.push(
                        <Polygon
                          key={`vordme-entry1-${rIdx}`}
                          positions={result.entryPolygons.sector1}
                          pathOptions={{ color: '#00acc1', fillColor: '#00acc1', fillOpacity: 0.06, weight: 1, dashArray: '2 3' }}
                        >
                          <Tooltip sticky><b>Entry Sector 1</b> – {label}</Tooltip>
                        </Polygon>
                      );
                    }
                    if (result.entryPolygons.sector2 && result.entryPolygons.sector2.length > 0) {
                      items.push(
                        <Polygon
                          key={`vordme-entry2-${rIdx}`}
                          positions={result.entryPolygons.sector2}
                          pathOptions={{ color: '#8e24aa', fillColor: '#8e24aa', fillOpacity: 0.06, weight: 1, dashArray: '2 3' }}
                        >
                          <Tooltip sticky><b>Entry Sector 2</b> – {label}</Tooltip>
                        </Polygon>
                      );
                    }
                  }

                  // ── Arrows ──
                  if (result.nominalArrows) {
                    result.nominalArrows.forEach((arrow, aIdx) => {
                      if (arrow && arrow.length === 3) {
                        items.push(
                          <Polygon
                            key={`vordme-arr-${rIdx}-${aIdx}`}
                            positions={arrow}
                            pathOptions={{ color: primaryColor, fillColor: primaryColor, fillOpacity: 0.9, weight: 1 }}
                          >
                            <Tooltip sticky>
                              <b>Direction arrow {aIdx + 1}</b>
                            </Tooltip>
                          </Polygon>
                        );
                      }
                    });
                  }

                  return items;
                })}
              </FeatureGroup>
            );
          })()}

          {/* Debug: OMNI departure surface polygons */}
          {(showOmniSurfaces || layerOmni) && omniAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {omniAnalysis.results.flatMap((result, rIdx) => {
                  const polys = result.debugPolygons;
                  if (!polys) return [];
                  const label = `${result.input.airport_name || result.input.airport_code} RWY ${result.input.runway_designation}`;
                  const items = [];
                  if (polys.area1) {
                    items.push(
                      <Polygon key={`omni-dbg-${rIdx}-area1`} positions={polys.area1}
                        pathOptions={{ color: '#1976d2', fillOpacity: 0.10, weight: 2, dashArray: '6 3' }}>
                        <Tooltip sticky><b>Area 1</b><br />{label}</Tooltip>
                      </Polygon>
                    );
                  }
                  if (polys.area2) {
                    items.push(
                      <Polygon key={`omni-dbg-${rIdx}-area2`} positions={polys.area2}
                        pathOptions={{ color: '#388e3c', fillOpacity: 0.10, weight: 2, dashArray: '6 3' }}>
                        <Tooltip sticky><b>Area 2</b><br />{label}</Tooltip>
                      </Polygon>
                    );
                  }
                  if (polys.tia) {
                    items.push(
                      <Polygon key={`omni-dbg-${rIdx}-tia`} positions={polys.tia}
                        pathOptions={{ color: '#f57c00', fillOpacity: 0.10, weight: 2, dashArray: '4 4' }}>
                        <Tooltip sticky><b>TIA</b><br />{label}</Tooltip>
                      </Polygon>
                    );
                  }
                  if (polys.area3) {
                    items.push(
                      <Circle key={`omni-dbg-${rIdx}-area3`}
                        center={polys.area3.center}
                        radius={polys.area3.radius}
                        pathOptions={{ color: '#7b1fa2', fillOpacity: 0.08, weight: 2, dashArray: '6 3' }}>
                        <Tooltip sticky><b>Area 3</b><br />{label}</Tooltip>
                      </Circle>
                    );
                  }
                  return items;
                })}
              </FeatureGroup>
            );
          })()}

          {/* Debug: BARO/VNAV surface polygons (FAS, H, Z) */}
          {(showBaroSurfaces || layerBaro) && baroVnavAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            const BARO_COLORS = { fas: '#d32f2f', h: '#f57c00', z: '#7b1fa2' };
            const BARO_LABELS = { fas: 'FAS', h: 'H', z: 'Z' };
            return (
              <FeatureGroup>
                {baroVnavAnalysis.results.flatMap((result, rIdx) =>
                  (result.categoryResults || []).flatMap((cat, cIdx) => {
                    const polys = cat.debugPolygons;
                    if (!polys) return [];
                    const label = `${result.input.airport_name || result.input.airport_code} RWY ${result.input.runway_designation} Cat ${cat.acCategory}`;
                    return Object.entries(polys).map(([name, pts]) => (
                      <Polygon
                        key={`baro-dbg-${rIdx}-${cIdx}-${name}`}
                        positions={pts}
                        pathOptions={{
                          color: BARO_COLORS[name] || '#607d8b',
                          fillOpacity: 0.08,
                          weight: 1.5,
                          dashArray: '5 3',
                        }}
                      >
                        <Tooltip sticky>
                          <b>{BARO_LABELS[name] || name}</b> – {label}
                        </Tooltip>
                      </Polygon>
                    ));
                  })
                )}
              </FeatureGroup>
            );
          })()}

          {/* ── NAV Aid Protected-Radius Surfaces ─────────────────────────────── */}
          {showNavSurfaces && shieldingAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            const NAV_SURFACE_COLORS = {
              VOR:     '#1976d2',
              DME:     '#d32f2f',
              DME_N:   '#d32f2f',
              NDB:     '#ff9800',
              ILS_LOC: '#9c27b0',
              ILS_LOC_SingleFreq: '#7b1fa2',
              ILS_GP_MType: '#6a1b9a',
              PSR:     '#f44336',
              SSR:     '#e91e63',
              MLS_AZ:  '#00796b',
              MLS_EL:  '#009688',
            };
            const DIRECTIONAL_TYPES = new Set(['DME','ILS_LOC','ILS_LOC_SingleFreq','ILS_GP_MType','MLS_AZ','MLS_EL']);
            return (
              <FeatureGroup>
                {shieldingAnalysis.results.flatMap((result, rIdx) => {
                  const type  = result.input?.nav_aid_type;
                  const color = NAV_SURFACE_COLORS[type] || '#607d8b';
                  const label = `${result.input?.nav_aid_name || type}${result.input?.nav_ident ? ` (${result.input.nav_ident})` : ''}`;
                  const elevStr = result.input?.nav_elev_ft != null
                    ? `${(result.input.nav_elev_ft * 0.3048).toFixed(1)} m` : '—';

                  // Directional nav aids: draw ground / lateral / sloping polygons
                  if (DIRECTIONAL_TYPES.has(type) && result.debugPolygons) {
                    const polys = result.debugPolygons;
                    const ZONE_COLORS = {
                      ground:        { color: '#2e7d32', label: 'Ground' },
                      lateral_left:  { color: color,     label: 'Lateral (left)' },
                      lateral_right: { color: color,     label: 'Lateral (right)' },
                      sloping:       { color: '#f57c00', label: 'Sloping' },
                    };
                    return Object.entries(ZONE_COLORS).flatMap(([zone, cfg]) =>
                      polys[zone]
                        ? [(
                            <Polygon
                              key={`nav-dir-${rIdx}-${zone}`}
                              positions={polys[zone]}
                              pathOptions={{ color: cfg.color, fillOpacity: 0.08, weight: 1.5, dashArray: '5 3' }}
                            >
                              <Tooltip sticky>
                                <b>{label}</b> – {cfg.label}<br />
                                Elev: {elevStr}
                              </Tooltip>
                            </Polygon>
                          )]
                        : []
                    );
                  }

                  // Omnidirectional nav aids: one circle per unique radius
                  const seen = new Map();
                  (result.rows || []).forEach(row => {
                    const key = `${row.navAidType}-${row.radiusM}`;
                    if (!seen.has(key)) seen.set(key, row);
                  });
                  return Array.from(seen.values()).map((row, cIdx) => {
                    const c = NAV_SURFACE_COLORS[row.navAidType] || '#607d8b';
                    const lbl = `${row.navAidTypeLabel || row.navAidType}${row.navAidName ? ` – ${row.navAidName}` : ''}${row.navAidIdent ? ` (${row.navAidIdent})` : ''}`;
                    return (
                      <Circle
                        key={`nav-omni-${rIdx}-${cIdx}`}
                        center={[row.navAidLat, row.navAidLon]}
                        radius={row.radiusM}
                        pathOptions={{ color: c, fillOpacity: 0.06, weight: 2, dashArray: '6 3' }}
                      >
                        <Tooltip sticky>
                          <b>{lbl}</b><br />
                          Protected radius: {(row.radiusM / 1000).toFixed(1)} km<br />
                          Elev: {row.navAidElevM != null ? `${row.navAidElevM.toFixed(1)} m` : '—'}
                        </Tooltip>
                      </Circle>
                    );
                  });
                })}
              </FeatureGroup>
            );
          })()}

          {/* MSA sector polygon overlay */}
          {showMsaSurfaces && msaAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            return (
              <FeatureGroup>
                {msaAnalysis.results.flatMap((result, rIdx) => {
                  const elements = [];

                  // 5 NM true geometric buffer — wraps around all edges of the
                  // sector (outer arc, both radials, centre point with rounded caps)
                  if (result.bufferPolygon && result.bufferPolygon.length > 0) {
                    elements.push(
                      <Polygon
                        key={`msa-buffer-${rIdx}`}
                        positions={result.bufferPolygon}
                        pathOptions={{ color: '#1565c0', fillColor: '#90caf9', fillOpacity: 0.25, weight: 1.5, dashArray: '4 6' }}
                      >
                        <Tooltip sticky>
                          <b>{result.input.airport_name || result.input.airport_code || '—'}</b><br />
                          Sector: {result.input.sector_from_deg}° – {result.input.sector_to_deg}°<br />
                          5 NM protection buffer
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  // Nominal sector polygon
                  if (result.polygon && result.polygon.length > 0) {
                    elements.push(
                      <Polygon
                        key={`msa-sector-${rIdx}`}
                        positions={result.polygon}
                        pathOptions={{ color: '#2e7d32', fillColor: '#66bb6a', fillOpacity: 0.12, weight: 2, dashArray: '6 3' }}
                      >
                        <Tooltip sticky>
                          <b>{result.input.airport_name || result.input.airport_code || '—'}</b><br />
                          Sector: {result.input.sector_from_deg}° – {result.input.sector_to_deg}°<br />
                          Radius: {result.input.radius_nm} NM<br />
                          {result.msa?.formatted ? <>MSA: <b>{result.msa.formatted}</b></> : null}
                        </Tooltip>
                      </Polygon>
                    );
                  }

                  return elements;
                })}
              </FeatureGroup>
            );
          })()}

          {/* TAA sector polygon overlay – uses precomputed polygons from /api/taa-calculation */}
          {showTaaSectors && taaData?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            // taaData.results come from /api/taa-calculation which returns results
            // with { input, sectorPolygon, bufferPolygon, ... }
            const validResults = taaData.results.filter(r => !r.input?.error && r.sectorPolygon?.length > 0);
            return (
              <FeatureGroup>
                {validResults.map((r, rIdx) => {
                  const inp = r.input;
                  return (
                    <React.Fragment key={`taa-${rIdx}`}>
                      {/* 5 NM buffer polygon */}
                      {r.bufferPolygon && r.bufferPolygon.length > 0 && (
                        <Polygon
                          positions={r.bufferPolygon}
                          pathOptions={{ color: '#154360', fillColor: '#5dade2', fillOpacity: 0.08, weight: 1.5, dashArray: '4 4' }}
                        >
                          <Tooltip sticky>
                            <b>{inp.airport_name || inp.airport_code || '—'}</b><br />
                            5 NM protection buffer
                          </Tooltip>
                        </Polygon>
                      )}
                      {/* Sector polygon */}
                      <Polygon
                        positions={r.sectorPolygon}
                        pathOptions={{ color: '#154360', fillColor: '#2980b9', fillOpacity: 0.15, weight: 2, dashArray: '6 3' }}
                      >
                        <Tooltip sticky>
                          <b>{inp.airport_name || inp.airport_code || '—'}</b><br />
                          Procedure: {inp.procedure_id || '—'} / {inp.sector_type || '—'}<br />
                          Sector: {inp.sector_from_deg != null ? `${inp.sector_from_deg}°` : '—'} – {inp.sector_to_deg != null ? `${inp.sector_to_deg}°` : '—'}<br />
                          Radius: {inp.radius_nm} NM<br />
                          {inp.min_altitude != null ? <>Min Alt: <b>{inp.min_altitude} {inp.altitude_unit || ''}</b></> : null}
                          {r.taa?.formatted ? <><br />TAA: <b>{r.taa.formatted}</b></> : null}
                        </Tooltip>
                      </Polygon>
                    </React.Fragment>
                  );
                })}
              </FeatureGroup>
            );
          })()}

          {/* Circling protected area polygon overlay */}
          {showCirclingAreas && circlingAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            const validResults = circlingAnalysis.results.filter(r => {
              if (r.input?.error) return false;
              const cat = (r.input?.category || '').toUpperCase();
              return showCirclingCats[cat] !== false;
            });
            if (validResults.length === 0) return null;
            return (
              <FeatureGroup>
                {validResults.flatMap((r, rIdx) => {
                  const inp = r.input;
                  const catLabel = inp.category ? `Cat ${inp.category === 'custom' ? 'Custom' : inp.category.toUpperCase()}` : '';
                  const rwys = inp.runway_designations?.length > 0 ? inp.runway_designations.join(', ') : (inp.runway_designation || '—');
                  if (!r.polygon?.coordinates?.length) return [];
                  const positions = r.polygon.coordinates[0].map(([lon, lat]) => [lat, lon]);
                  return (
                    <Polygon
                      key={`circ-${rIdx}`}
                      positions={positions}
                      pathOptions={{ color: '#0d4f3c', fillColor: '#27ae60', fillOpacity: 0.15, weight: 2, dashArray: '6 3' }}
                    >
                      <Tooltip sticky>
                        <b>{inp.airport_code || '—'}</b>{inp.procedure_id ? ` / ${inp.procedure_id}` : ''}<br />
                        {catLabel} – RWY {rwys}{(inp.circling_restriction === 'L' || inp.circling_restriction === 'R') ? ` (${inp.circling_restriction})` : ''}<br />
                        Radius: {inp.radius_nm != null ? `${inp.radius_nm} NM` : '—'}<br />
                        {r.minimum?.formatted ? <>Min Alt: <b>{r.minimum.formatted}</b></> : null}
                      </Tooltip>
                    </Polygon>
                  );
                })}
              </FeatureGroup>
            );
          })()}

          {isAuthenticated && user?.betald === 'JA' && <LocApproachOverlay analysis={locAnalysis} state={locLayerState} />}

          {/* GNSS Approach protection area overlay */}
          {showGnssApchSurfaces && gnssApchAnalysis?.results && (isAuthenticated && user?.betald === 'JA') && (() => {
            const visibleResults = gnssApchAnalysis.results.filter(proc => isGnssProcedureVisible(showGnssApchLayers, proc));
            const validResults = visibleResults.filter(proc => !proc.input?.error);

            const polygonToPositions = (feature) => {
              const geometryType = feature?.geometry?.type;
              const coordinates = feature?.geometry?.coordinates;
              if (!coordinates?.[0]?.length) return null;
              if (geometryType === 'MultiPolygon') {
                return coordinates.map(poly =>
                  poly.map(ring => ring.map(([lon, lat]) => [lat, lon]))
                );
              }
              // Support multi-ring GeoJSON (polygon with hole) for Leaflet:
              // Leaflet <Polygon> accepts [[outerRing], [holeRing]] when rings are nested.
              if (coordinates.length > 1) {
                return coordinates.map(ring => ring.map(([lon, lat]) => [lat, lon]));
              }
              return coordinates[0].map(([lon, lat]) => [lat, lon]);
            };

            return (
              <FeatureGroup>
                {validResults.flatMap((proc, procIdx) =>
                  (proc.segments || []).flatMap((seg, segIdx) => {
                    if (!isGnssSegmentVisible(showGnssApchLayers, proc, seg)) return [];
                    const categoryColor = gnssCategoryColor(proc);

                    const secondaryFeatures = (
                      Array.isArray(seg.secondaryPolygons) && seg.secondaryPolygons.length > 0
                    )
                      ? seg.secondaryPolygons
                      : [seg.secondaryPolygon || seg.polygon].filter(Boolean);
                    const primaryPositions = polygonToPositions(seg.primaryPolygon);
                    // Draw protection parts independently: overlapping phases
                    // or a local DF end join must be a union, not an even-odd hole.
                    const primaryPositionParts = seg.primaryPolygon?.geometry?.type === 'MultiPolygon'
                      ? primaryPositions : [primaryPositions];
                    const segName = seg.segLabel || seg.segType || 'Segment';
                    const secondaryLayers = isGnssDisplayEnabled(showGnssApchLayers, proc, 'secondary')
                      ? secondaryFeatures.map((feature, featureIdx) => {
                          const secondaryPositions = polygonToPositions(feature);
                          if (!secondaryPositions) return null;
                          return (
                            <Polygon
                              key={`gnss-sec-${procIdx}-${segIdx}-${featureIdx}`}
                              positions={secondaryPositions}
                              pathOptions={{ color: categoryColor, fillColor: categoryColor, fillOpacity: 0.10, weight: 2, dashArray: '6 4' }}
                            >
                              <Tooltip sticky>
                                <b>{proc.input.airport_code || '—'}</b>{proc.input.approach_id ? ` / ${proc.input.approach_id}` : ''}<br />
                                RWY {proc.input.runway_designator || '—'} – {proc.input.approach_type || 'GNSS'}<br />
                                Aircraft cat: <b>{proc.input.aircraft_category}</b><br />
                                Segment: <b>{segName}</b> ({seg.fromPhase} → {seg.toPhase})<br />
                                Area: <b>{seg.manualReviewRequired ? 'Outer area / conservative review' : 'Secondary'}</b><br />
                                {seg.manualReviewRequired && <strong style={{ color: seg.criticalCount > 0 ? '#b71c1c' : '#555' }}>Preliminary – manual PANS-OPS review required; full MOC in review area.<br /></strong>}
                                {secondaryFeatures.length > 1 ? <>Part: <b>{featureIdx + 1}/{secondaryFeatures.length}</b><br /></> : null}
                                {seg.criticalCount > 0
                                  ? <span style={{ color: '#d32f2f' }}>⚠ {seg.criticalCount} critical</span>
                                  : <span style={{ color: seg.manualReviewRequired ? '#555' : '#2e7d32' }}>{seg.manualReviewRequired ? 'Preliminary – no critical in review assessment' : '✓ No critical'}</span>}
                              </Tooltip>
                            </Polygon>
                          );
                        }).filter(Boolean)
                      : [];

                    const primaryLayers = (isGnssDisplayEnabled(showGnssApchLayers, proc, 'primary') && primaryPositions) ? primaryPositionParts.map((positions, partIdx) => (
                      <Polygon
                        key={`gnss-pri-${procIdx}-${segIdx}-${partIdx}`}
                        positions={positions}
                        pathOptions={{ color: categoryColor, fillColor: categoryColor, fillOpacity: 0.22, weight: 2 }}
                      >
                        <Tooltip sticky>
                          <b>{proc.input.airport_code || '—'}</b>{proc.input.approach_id ? ` / ${proc.input.approach_id}` : ''}<br />
                          RWY {proc.input.runway_designator || '—'} – {proc.input.approach_type || 'GNSS'}<br />
                          Aircraft cat: <b>{proc.input.aircraft_category}</b><br />
                          Segment: <b>{segName}</b> ({seg.fromPhase} → {seg.toPhase})<br />
                          Area: <b>Primary</b><br />
                          {seg.manualReviewRequired && <strong style={{ color: seg.criticalCount > 0 ? '#b71c1c' : '#555' }}>Preliminary – manual PANS-OPS review required.<br /></strong>}
                          {seg.criticalCount > 0
                            ? <span style={{ color: '#d32f2f' }}>⚠ {seg.criticalCount} critical</span>
                            : <span style={{ color: seg.manualReviewRequired ? '#555' : '#2e7d32' }}>{seg.manualReviewRequired ? 'Preliminary – no critical in review assessment' : '✓ No critical'}</span>}
                        </Tooltip>
                      </Polygon>
                    )) : [];

                    return [...secondaryLayers, ...primaryLayers].filter(Boolean);
                  })
                )}
                {/* Waypoint fix tolerance boxes */}
                {validResults.filter(proc => isGnssDisplayEnabled(showGnssApchLayers, proc, 'fixTolerance')).flatMap((proc, procIdx) =>
                  (proc.waypointTolerances || []).map((wt, wtIdx) => {
                    const positions = polygonToPositions(wt.polygon);
                    if (!positions) return null;
                    return (
                      <Polygon
                        key={`gnss-wpt-tol-${procIdx}-${wtIdx}`}
                        positions={positions}
                        pathOptions={{ color: '#f57c00', fillColor: '#ffe082', fillOpacity: 0.25, weight: 1.5, dashArray: '4 3' }}
                      >
                        <Tooltip sticky>
                          <b>Fix Tolerance – {wt.ident || wt.key?.toUpperCase()}</b><br />
                          {wt.legNo != null && <>Missed approach – Leg {wt.legNo} ({wt.trackRole === 'departure' ? 'outbound' : 'inbound'})<br /></>}
                          Phase: {wt.phase}<br />
                          {wt.attM != null && wt.xttM != null && <>ATT: ±{wt.attM.toFixed(1)} m / XTT: ±{wt.xttM.toFixed(1)} m<br /></>}
                          {wt.trackSource === 'nominal_df_tangent' && <>Direction: nominal DF tangent<br /></>}
                          {proc.input.airport_code || '—'} / RWY {proc.input.runway_designator || '—'}
                        </Tooltip>
                      </Polygon>
                    );
                  }).filter(Boolean)
                )}
                <Pane name="gnss-nominal-track" style={{ zIndex: 450 }}>
                  {validResults.filter(proc => isGnssDisplayEnabled(showGnssApchLayers, proc, 'waypointProjection'))
                    .flatMap((proc, procIdx) => {
                      const endpoint = proc.missedEndpoint;
                      if (endpoint?.source !== 'waypoint' || !endpoint.waypoint || !endpoint.projected) return [];
                      const original = [endpoint.waypoint.lat, endpoint.waypoint.lon];
                      const projected = [endpoint.projected.lat, endpoint.projected.lon];
                      if (![...original, ...projected].every(Number.isFinite)) return [];
                      return [
                        <Polyline key={`gnss-projection-${procIdx}`} positions={[original, projected]}
                          pathOptions={{ color: gnssCategoryColor(proc), weight: 2, dashArray: '3 4' }}>
                          <Tooltip sticky>{endpoint.ident} → final axis · Cat {proc.input.aircraft_category}<br />
                            Cross-track offset: {endpoint.crossTrackM.toFixed(2)} m</Tooltip>
                        </Polyline>,
                        <CircleMarker key={`gnss-endpoint-original-${procIdx}`} center={original} radius={5}
                          pathOptions={{ color: gnssCategoryColor(proc), fillOpacity: 0.8 }}>
                          <Tooltip sticky>{endpoint.ident} · original waypoint · Cat {proc.input.aircraft_category}</Tooltip>
                        </CircleMarker>,
                        <CircleMarker key={`gnss-endpoint-projected-${procIdx}`} center={projected} radius={4}
                          pathOptions={{ color: gnssCategoryColor(proc), fillColor: '#fff', fillOpacity: 1 }}>
                          <Tooltip sticky>{endpoint.ident} · projected endpoint · {endpoint.distanceNm.toFixed(3)} NM from THR</Tooltip>
                        </CircleMarker>,
                      ];
                    })}
                  {validResults.filter(proc => isGnssDisplayEnabled(showGnssApchLayers, proc, 'references')).flatMap((proc, procIdx) =>
                    (proc.lpv || proc.xls) ? [['FAP', (proc.lpv || proc.xls).fap], ['SOC', (proc.lpv || proc.xls).soc],
                      ...(proc.input.missed_approach_turn_type === 'WPT'
                        ? [['ETP', (proc.lpv || proc.xls).etp], ['TP', (proc.lpv || proc.xls).termination]]
                        : [[proc.input.missed_straight_to_altitude ? 'End (altitude)' : proc.input.missed_approach_turn_type === 'Alt' ? 'TP (altitude)' : 'MAHWP', (proc.lpv || proc.xls).etp]])]
                      .filter(([, position]) => Number.isFinite(position?.lat) && Number.isFinite(position?.lon))
                      .map(([label, position]) => <CircleMarker key={`gnss-lpv-${procIdx}-${label}`}
                        center={[position.lat, position.lon]} radius={5}
                        pathOptions={{ color: '#7b1fa2', fillColor: '#fff', fillOpacity: 1, weight: 2 }}>
                        <Tooltip permanent direction="top">{proc.input.approach_type} {label} – Cat {proc.input.aircraft_category}</Tooltip>
                      </CircleMarker>) : []
                  )}
                  {validResults.filter(proc => isGnssDisplayEnabled(showGnssApchLayers, proc, 'references')).flatMap((proc, procIdx) =>
                    proc.baro ? [['FAP', proc.baro.fap], ['SOC', proc.baro.soc],
                      [proc.input.missed_straight_to_altitude ? 'End (altitude)' : proc.input.missed_approach_turn_type === 'Straight' ? 'MAHWP' : 'TP', proc.baro.termination]]
                      .filter(([, position]) => Number.isFinite(position?.lat) && Number.isFinite(position?.lon))
                      .map(([label, position]) => <CircleMarker key={`gnss-baro-${procIdx}-${label}`}
                        center={[position.lat, position.lon]} radius={5}
                        pathOptions={{ color: '#7b1fa2', fillColor: '#fff', fillOpacity: 1, weight: 2 }}>
                        <Tooltip permanent direction="top">BARO {label} – Cat {proc.input.aircraft_category}</Tooltip>
                      </CircleMarker>) : []
                  )}
                  {visibleResults.filter(proc => isGnssDisplayEnabled(showGnssApchLayers, proc, 'nominal')).flatMap((proc, procIdx) =>
                    (proc.nominalTracks || []).flatMap((track, trackIdx) => {
                      if (!isGnssSegmentVisible(showGnssApchLayers, proc, track) || track.line?.geometry?.type !== 'LineString') return [];
                      const positions = track.line.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
                      const turn = track.turn;
                      return [
                        <Polyline key={`gnss-nominal-halo-${procIdx}-${trackIdx}`} positions={positions}
                          pathOptions={{ color: '#fff', weight: 5, opacity: 0.9 }} interactive={false} />,
                        <Polyline key={`gnss-nominal-${procIdx}-${trackIdx}`} positions={positions}
                          pathOptions={{ color: '#ad1457', weight: 2.5, dashArray: '10 5' }}>
                          <Tooltip sticky>
                            <b>Nominal track – {track.segLabel}</b><br />
                            {proc.input.airport_code} / RWY {proc.input.runway_designator}<br />
                            Aircraft cat: {proc.input.aircraft_category}<br />
                            {track.from && track.to ? <>{track.from} → {track.to}<br /></> : null}
                            {turn && <>
                              {turn.legType} / {turn.waypointType}<br />
                              {turn.legType === 'DF' && <>Turn direction: {turn.turnDirection || 'Straight'} ({turn.requestedTurnDirection ? 'input' : 'automatic'})<br /></>}
                              TAS: {turn.tasKt?.toFixed(1)} kt | Bank nominal: {turn.bankNomDeg}°<br />
                              Altitude: {turn.altitudeFt} ft | Radius: {turn.radiusM?.toFixed(1)} m
                            </>}
                          </Tooltip>
                        </Polyline>,
                      ];
                    })
                  )}
                  {visibleResults.flatMap((proc, procIdx) => {
                    const construction = proc.constructionDiagnostics;
                    if (!construction) return [];
                    return (construction.legs || [construction]).flatMap((diagnostic, diagnosticIdx) => {
                      const constructionType = diagnostic.constructionType || (diagnostic.legType === 'DF' ? 'DF' : diagnostic.legType ? 'TF' : 'DF');
                      const visibilityKey = constructionType === 'DF' ? 'dfDiagnostics' : 'tfDiagnostics';
                      const showConstruction = isGnssSegmentVisible(showGnssApchLayers, proc, { segFamily: 'missed' })
                        && isGnssDisplayEnabled(showGnssApchLayers, proc, visibilityKey);
                      const hasExcludedSpiral = (diagnostic.paths || []).some(path => path.kind === 'wind_spiral'
                        && path.role === 'excluded_non_controlling');
                      const showExcluded = hasExcludedSpiral
                        && isGnssDisplayEnabled(showGnssApchLayers, proc, 'excludedSpiralDiagnostics');
                      const showReview = !!(diagnostic.conservativeReview || diagnostic.endJoinReview)
                        && isGnssDisplayEnabled(showGnssApchLayers, proc, 'conservativeDfDiagnostics');
                      if (!showConstruction && !showExcluded && !showReview) return [];
                      const excludedContextKinds = ['selected_envelope', 'used_spiral', 'selected_tangent', 'earliest_track',
                        'turn_section', 'turn_initiation_area', 'exit_line', 'end_section', 'end_cap'];
                      const diagnosticKey = `gnss-${constructionType.toLowerCase()}-${procIdx}-${diagnosticIdx}`;
                      const diagnosticStyles = {
                        end_join_bridge: { color: '#c62828', weight: 4, dashArray: '8 4' },
                        conservative_primary: { color: '#c62828', weight: 4 },
                        conservative_outer: { color: '#c62828', weight: 4, dashArray: '8 5' },
                        endpoint_protection: { color: '#00838f', weight: 3, dashArray: '5 4' },
                        nominal_turn_circle: { color: '#ad1457', dashArray: '3 6' },
                        wind_spiral: { color: '#e65100', dashArray: '3 6' },
                        tangent_candidate: { color: '#00838f', dashArray: '4 6', opacity: 0.65 },
                        selected_tangent: { color: '#1565c0', weight: 3, dashArray: '10 4' },
                        earliest_track: { color: '#00838f', weight: 3, dashArray: '10 4' },
                        selected_envelope: { color: '#d84315', weight: 4 },
                        used_spiral: { color: '#d84315', weight: 5 },
                        secondary_envelope: { color: '#ef6c00', weight: 3, dashArray: '5 4' },
                        turn_section: { color: '#616161', dashArray: '6 4' },
                        turn_initiation_area: { color: '#455a64', weight: 3, dashArray: '8 4' },
                        exit_line: { color: '#6a1b9a', dashArray: '8 4' },
                        start_cap: { color: '#212121', weight: 3, dashArray: '6 4' },
                        end_section: { color: '#212121', dashArray: '8 4' },
                        end_cap: { color: '#212121', weight: 3 },
                      };
                      const paths = (diagnostic.paths || []).filter(path => path.role === 'excluded_non_controlling'
                        ? showExcluded : showConstruction || showReview || (showExcluded && excludedContextKinds.includes(path.kind))).map((path, pathIdx) => {
                        if (path.line?.geometry?.type !== 'LineString') return null;
                        return <Polyline key={`${diagnosticKey}-path-${pathIdx}`}
                          positions={path.line.geometry.coordinates.map(([lon, lat]) => [lat, lon])}
                          pathOptions={{ weight: 2, ...diagnosticStyles[path.kind],
                            ...(path.role === 'excluded_non_controlling' ? { color: '#c2185b', weight: 4, opacity: 1, dashArray: '10 5' } : {}),
                            ...(path.role === 'blocking' ? { color: '#c62828', weight: 4 } : {}),
                            ...(path.ordinaryCandidate ? { opacity: 0.45, weight: 1, color: '#757575' } : {}) }}>
                          <Tooltip sticky>
                            <b>{constructionType} construction diagnostic – {path.label}</b><br />
                            {proc.input.approach_id} · {proc.input.airport_code} / {proc.input.runway_designator}<br />
                            {(diagnostic.conservativeReview || diagnostic.endJoinReview || path.role === 'excluded_non_controlling' || Number(diagnostic.spiralQuarterCount) > 3) && <>
                              <strong style={{ color: proc.input?.error || gnssObstacleCounts(proc).criticalObstacles > 0 ? '#b71c1c' : '#555' }}>{language === 'sv'
                                ? 'Avvikelse från ordinarie PANS OPS-konstruktion; manuell kontroll mot PANS-OPS krävs.'
                                : 'Departure from the standard PANS-OPS construction; manual review against PANS-OPS is required.'}</strong><br />
                              {path.role === 'excluded_non_controlling' && <>{language === 'sv' ? 'Utesluten spiral.' : 'Excluded spiral.'}<br /></>}
                              {Number(diagnostic.spiralQuarterCount) > 3 && <>{language === 'sv'
                                ? 'Hjälpspiralen har utökats med en fjärde kvartsbåge.'
                                : 'The helper spiral has been extended with a fourth quarter arc.'}<br /></>}
                            </>}
                            Aircraft cat: {proc.input.aircraft_category}<br />
                            Leg {diagnostic.legNo} → {diagnostic.to}<br />
                            {diagnostic.waypointType && <>{diagnostic.waypointType}<br /></>}
                            {diagnostic.turnContext && <>
                              Turn application: {diagnostic.turnContext.application} | {diagnostic.turnContext.approachType}<br />
                              Earliest reference: {diagnostic.turnContext.earliestReference}<br />
                              Turns before MAPt: {diagnostic.turnContext.allowTurnsBeforeMapt ? 'Allowed' : 'Not enabled for this turn'}<br />
                            </>}
                            {diagnostic.constructionType === 'DF' && <>
                              Turn direction: {diagnostic.turnDirection || 'Straight'} | Input: {diagnostic.requestedTurnDirection || 'automatic'}<br />
                              Course change: {diagnostic.courseChangeDeg?.toFixed(2) ?? '—'}°<br />
                            </>}
                            {Number.isFinite(diagnostic.courseChangeDeg) && <>Course change: {diagnostic.courseChangeDeg.toFixed(1)}°<br /></>}
                            {Number.isFinite(diagnostic.etpDistanceM) && <>ETP distance: {diagnostic.etpDistanceM.toFixed(1)} m<br /></>}
                            {Number.isFinite(diagnostic.ltpDistanceM) && <>LTP distance: {diagnostic.ltpDistanceM.toFixed(1)} m<br /></>}
                            {Number.isFinite(path.turnAngleDeg) && <>Turn progression: {path.turnAngleDeg.toFixed(1)}°<br /></>}
                            {Number.isFinite(path.joinAngleDeg) && <>Join angle to outbound: {path.joinAngleDeg.toFixed(1)}°<br /></>}
                            {path.kind === 'wind_spiral' && constructionType === 'DF' ? <>
                              Destination inside spiral: {path.containsDestination ? 'Yes' : 'No'}<br />
                              Tangent available: {path.tangentAvailable ? 'Yes' : 'No'}<br />
                              Role: {path.role || 'candidate'}<br />
                              {path.exposedRangesDeg?.length > 0 && <>Potential exposure of full helper: {path.exposedRangesDeg.map(range =>
                                `${range.fromDeg.toFixed(1)}°–${range.toDeg.toFixed(1)}°`).join(', ')}<br /></>}
                              {path.decisionReason && <>{path.decisionReason}<br /></>}
                            </> : path.kind === 'nominal_turn_circle' ? <>
                              Radius: {path.radiusM?.toFixed(1)} m<br />
                              Centre → destination: {path.destinationDistanceM?.toFixed(1)} m<br />
                            </> : null}
                            {diagnostic.selectedSpiral && <>Selected tangent: {diagnostic.selectedSpiral}<br /></>}
                            {diagnostic.blockingSpiral && <>Blocking spiral: {diagnostic.blockingSpiral}<br /></>}
                            {Number.isFinite(diagnostic.exitTurnAngleDeg) && <>15° exit at turn progression: {diagnostic.exitTurnAngleDeg.toFixed(1)}°<br /></>}
                            IAS: {diagnostic.turnConfig?.iasKt} kt | Altitude: {diagnostic.turnConfig?.altitudeFt} ft<br />
                            TAS: {diagnostic.tasKt?.toFixed(1)} kt | Area radius: {diagnostic.radiusM?.toFixed(1)} m<br />
                            Bank nominal / area: {diagnostic.turnConfig?.bankNomDeg}° / {diagnostic.turnConfig?.bankDeg}°<br />
                            Wind: {diagnostic.turnConfig?.windKt} kt<br />
                            {diagnostic.endJoinReview && !proc.input?.error ? (language === 'sv'
                              ? 'DF med lokal tilläggsyta vid slutfixen. Full MOC. Ingen ytterligare höjdmarginal från stiggradienten tillgodoräknas inom tillägget; manuell granskning krävs.'
                              : 'DF with a local addition at the end fix. Full MOC and no additional climb credit within the addition; manual review required.')
                              : diagnostic.conservativeReview && !proc.input?.error ? (language === 'sv'
                              ? 'Konservativ granskningsyta. Full MOC i hela ytterytan; ingen ytterligare höjdmarginal från stiggradienten tillgodoräknas. Ingen verifierad DF-tangent.'
                              : 'Conservative review area. Full MOC throughout outer area; no climb credit. No verified DF tangent.')
                              : diagnostic.status !== 'success' || proc.input?.error
                              ? `Construction preview (${diagnostic.stage || 'failed'}) – procedure not evaluated`
                              : diagnostic.hasProtectionAreas === false ? 'Construction absorbed by following turn'
                              : hasExcludedSpiral ? (language === 'sv'
                                ? 'Manuell granskning krävs – beräknad med utesluten spiral'
                                : 'Manual review required – calculated with an excluded spiral')
                              : `Construction of calculated ${constructionType} protection area`}
                          </Tooltip>
                        </Polyline>;
                      });
                      const markers = [
                        { label: 'Turn start', position: diagnostic.turnPosition, permanent: true },
                        { label: diagnostic.to || `${constructionType} fix`, position: diagnostic.destination, permanent: true },
                        ...(diagnostic.points || []),
                      ].map(({ label, position, permanent = false }, markerIdx) => {
                        if (!Number.isFinite(position?.lat) || !Number.isFinite(position?.lon)) return null;
                        return <CircleMarker key={`${diagnosticKey}-point-${markerIdx}`}
                          center={[position.lat, position.lon]} radius={5}
                          pathOptions={{ color: '#ad1457', fillColor: '#fff', fillOpacity: 1, weight: 2 }}>
                          <Tooltip permanent={permanent} direction="top">{label}</Tooltip>
                        </CircleMarker>;
                      });
                      return [...paths, ...markers];
                    });
                  })}
                </Pane>
              </FeatureGroup>
            );
          })()}

          </>}
        </MapContainer>
      </div>
      <FooterHeader
        onAnalyze={analyzeGeometries}
        onExportPdf={handleExportPdf}
        t={t}
        disabledAnalyze={getAnalyzableFeatures(features).length === 0 || isAnalyzing || isGeneratingPdf}
        disabledExport={getAnalyzableFeatures(features).length === 0 || !analysis || isAnalyzing || isGeneratingPdf}
        versionInfo={versionInfo}
        isAuthenticated={isAuthenticated}
        isPaid={isPaid}
      />
  {/*onAnalyze={analyzeGeometries}
  onExportPdf={() =>
    exportPenetrationTablesToPDF({
      t,
      showSurfaces, setShowSurfaces,
      showEasa, setShowEasa,
      showPansOps, setShowPansOps,
      showAnnex14, setShowAnnex14,
      showEtod, setShowEtod,
      showGeoJson, setShowGeoJson,
      showUserData, setShowUserData,
      showSwedishAirports, setShowSwedishAirports,
      showNavaids, setShowNavaids,
      showNav, setShowNav,
      showTmaTia, setShowTmaTia,
      showCtrTizRmz, setShowCtrTizRmz,
      setPdfMode,
      analysis,
      features,
      mapDivRef,
      fitMapToFeaturesAndWait,
      airportAddresses
    })
  }
  t={t}
  disabledAnalyze={getAnalyzableFeatures(features).length === 0}
  disabledExport={getAnalyzableFeatures(features).length === 0 || !analysis}
/>
*/}

      <PropertyModal
        feature={modalFeature}
        onSave={handleSaveProperties}
        onCancel={handleCancelProperties}
      /> 

      <FlyghinderOrderModal
        open={showOrderModal}
        onClose={handleOrderCancel}
        onConfirm={handleOrderConfirm}
      />

      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
      />
      
      <RegisterModal
        open={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={handleRegisterSuccess}
        onSwitchToLogin={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />

      <SubscriptionWarningModal
        open={showSubscriptionWarning}
        onClose={() => setShowSubscriptionWarning(false)}
        onLogin={handleSubscriptionLogin}
        onRegister={handleSubscriptionRegister}
        onContinueWithoutRegistration={handleContinueWithoutRegistration}
        onContactUs={handleContactUs}
      />

      <LoginRequiredModal
        open={showLoginRequired}
        onClose={() => setShowLoginRequired(false)}
        onLogin={handleLoginRequiredLogin}
        onRegister={handleLoginRequiredRegister}
      />

      <FreeExportsExhaustedModal
        open={showFreeExportsExhausted}
        onClose={() => setShowFreeExportsExhausted(false)}
      />

      {analysis && (
        <div style={{ marginTop: 10 }}>
		
	   {/* NY TABELL – Sammanställning */}
     <ResultatTable geometries={getAnalyzableFeatures(features)} analysis={analysis} omniAnalysis={omniAnalysis} oasAnalysis={oasAnalysis} baroVnavAnalysis={baroVnavAnalysis} vssAnalysis={vssAnalysis} shieldingAnalysis={shieldingAnalysis} holdingBaseAnalysis={holdingBaseAnalysis} holdingRacetrackAnalysis={holdingRacetrackAnalysis} rnavHoldingAnalysis={rnavHoldingAnalysis} rnpHoldingAnalysis={rnpHoldingAnalysis} msaAnalysis={msaAnalysis} taaAnalysis={taaAnalysis} circlingAnalysis={circlingAnalysis} gnssApchAnalysis={gnssApchAnalysis} locAnalysis={locAnalysis} />
          
		  <h3>{t('result')}</h3>
	
		  
		  {(isAuthenticated && user?.betald === 'JA') && (
		    <>
		      {penetrationTable}
              {noPenetrationTable}
            </>
          )}

	   </div>
      )}

      {/* OMNI Analysis Results - Only for authorized users */}
      {omniAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!omniAnalysis.results?.some(r => r.obstacles?.length > 0)) return null;
        // Helper function to sort obstacles
        const sortObstacles = (obstacles, sortKey, sortDirection) => {
          const sorted = [...obstacles].sort((a, b) => {
            let aVal = a[sortKey];
            let bVal = b[sortKey];
            
            // Handle numeric comparisons
            if (typeof aVal === 'number' && typeof bVal === 'number') {
              return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }
            
            // Handle boolean comparisons (Critical, Close-in)
            if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
              return sortDirection === 'asc' 
                ? (aVal === bVal ? 0 : aVal ? 1 : -1)
                : (aVal === bVal ? 0 : aVal ? -1 : 1);
            }
            
            // Handle string comparisons
            aVal = String(aVal || '');
            bVal = String(bVal || '');
            return sortDirection === 'asc' 
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          });
          return sorted;
        };
        
        // Helper function to handle column header click
        const handleSort = (key) => {
          setOmniSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
          }));
        };
        
        // Helper function to handle row selection
        const handleRowClick = (runwayIdx, obsIdx, event) => {
          const obstacleId = `${runwayIdx}-${obsIdx}`;
          setSelectedObstacles(prev => {
            const newSet = new Set(prev);
            if (event.ctrlKey || event.metaKey) {
              // Multi-select: toggle
              if (newSet.has(obstacleId)) {
                newSet.delete(obstacleId);
              } else {
                newSet.add(obstacleId);
              }
            } else {
              // Single select: clear others and select this one
              newSet.clear();
              newSet.add(obstacleId);
            }
            return newSet;
          });
        };
        
        // Helper function to render sort indicator
        const renderSortIndicator = (columnKey) => {
          if (omniSortConfig.key !== columnKey) {
            return <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>;
          }
          return (
            <span style={{ marginLeft: 4 }}>
              {omniSortConfig.direction === 'asc' ? '▲' : '▼'}
            </span>
          );
        };

        const omniTdStyle = { padding: 4, border: '1px solid #ddd', whiteSpace: 'nowrap' };
        
        return (
        <div style={{ marginTop: 10, background: "#e8f5e9", padding: 16, borderRadius: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <h3 style={{ marginTop: 0, marginBottom: 0, color: "#2e7d32" }}>
              {t('omniResults') || 'OMNI Departure Analysis Results'}
            </h3>
            <button
              onClick={() => setShowOmniSurfaces(v => !v)}
              title="Toggle OMNI surface polygons on the map"
              style={{
                padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                background: showOmniSurfaces ? '#2e7d32' : '#c8e6c9',
                color: showOmniSurfaces ? '#fff' : '#2e7d32',
                border: '1px solid #2e7d32', whiteSpace: 'nowrap',
              }}
            >
              🗺 {showOmniSurfaces ? 'Hide OMNI surfaces' : 'Show OMNI surfaces'}
            </button>
          </div>
          
          {/* Results by Runway - Filter to show only runways with obstacles */}
          {omniAnalysis.results && omniAnalysis.results.filter(result => result.obstacles && result.obstacles.length > 0).length > 0 && (
            <div>
              {omniAnalysis.results
                .filter(result => result.obstacles && result.obstacles.length > 0)
                .map((result, idx) => (
                <details key={idx} style={{ marginBottom: 12, background: "white", padding: 12, borderRadius: 4 }}>
                  <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: 8 }}>
                    {result.input.airport_code} - {result.input.airport_name} - Runway {result.input.runway_designation}
                    {result.criticalObstacles > 0
                      ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalObstacles} {t('critical') || 'critical'})</span>
                      : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                    }
                  </summary>
                  
                  {result.input.error ? (
                    <div style={{ color: "#d32f2f", padding: 8 }}>
                      <strong>{t('error')}:</strong> {result.input.error}
                    </div>
                  ) : (
                    <div style={{ marginTop: 8 }}>
                      {/* Input Parameters */}
                      <div style={{ marginBottom: 8, fontSize: '12px', background: '#f1f8e9', padding: 8, borderRadius: 4 }}>
                        <b>{t('omniInputParams') || 'Input Parameters'}</b>
                        <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                          <tbody>
                            <tr>
                              <td style={omniTdStyle}>Runway Start</td>
                              <td style={omniTdStyle} colSpan={3}><b>{result.input.runway_start_lat} / {result.input.runway_start_lon}</b></td>
                              <td style={omniTdStyle}>Runway Start Elev</td>
                              <td style={omniTdStyle}><b>{result.input.runway_start_elev_m != null ? `${parseFloat(result.input.runway_start_elev_m).toFixed(1)} m / ${result.input.runway_start_elev_ft} ft` : '—'}</b></td>
                            </tr>
                            <tr>
                              <td style={omniTdStyle}>DER Position</td>
                              <td style={omniTdStyle} colSpan={3}><b>{result.input.der_position_lat} / {result.input.der_position_lon}</b></td>
                              <td style={omniTdStyle}>DER Elev</td>
                              <td style={omniTdStyle}><b>{result.input.der_position_elev_m != null ? `${parseFloat(result.input.der_position_elev_m).toFixed(1)} m / ${result.input.der_position_elev_ft} ft` : '—'}</b></td>
                            </tr>
                            <tr>
                              <td style={omniTdStyle}>Turning Altitude</td>
                              <td style={omniTdStyle}><b>{result.input.turning_altitude_ft != null ? `${result.input.turning_altitude_ft} ft / ${result.input.turning_altitude_m != null ? parseFloat(result.input.turning_altitude_m).toFixed(1) : '—'} m` : '—'}</b></td>
                              <td style={omniTdStyle}>Next Segment Altitude</td>
                              <td style={omniTdStyle}><b>{result.input.next_segment_altitude_ft != null ? `${result.input.next_segment_altitude_ft} ft / ${result.input.next_segment_altitude_m != null ? parseFloat(result.input.next_segment_altitude_m).toFixed(1) : '—'} m` : '—'}</b></td>
                              <td style={omniTdStyle}>Min Turn Height</td>
                              <td style={omniTdStyle}><b>{result.input.min_turn_height_m != null ? `${result.input.min_turn_height_m} m` : '—'}</b></td>
                            </tr>
                            <tr>
                              <td style={omniTdStyle}>Min MOC</td>
                              <td style={omniTdStyle}><b>{result.input.min_moc_m != null ? `${result.input.min_moc_m} m` : '—'}</b></td>
                              <td style={omniTdStyle}>MOC %</td>
                              <td style={omniTdStyle}><b>{result.input.moc_percentage != null ? `${result.input.moc_percentage}%` : '—'}</b></td>
                              <td style={omniTdStyle}>PDG %</td>
                              <td style={omniTdStyle}><b>{result.input.pdg_percentage != null ? `${result.input.pdg_percentage}%` : '—'}</b></td>
                            </tr>
                            <tr>
                              <td style={omniTdStyle}>Category H</td>
                              <td style={omniTdStyle}><b>{result.input.category_h ? t('yes') || 'Yes' : t('no') || 'No'}</b></td>
                              <td style={omniTdStyle}>Allow Turns Before DER</td>
                              <td style={omniTdStyle} colSpan={3}><b>{result.input.allow_turns_before_der ? t('yes') || 'Yes' : t('no') || 'No'}</b></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p><strong>{t('totalObstaclesAssessed') || 'Total Obstacles Assessed'}:</strong> {result.obstaclesChecked}</p>
                      <p><strong>{t('criticalObstacle') || 'Critical Obstacle'}:</strong> {result.criticalObstacles}</p>
                      
                      {result.obstacles && result.obstacles.length > 0 && (() => {
                        // Sort obstacles based on current sort configuration
                        const sortedObstacles = sortObstacles(result.obstacles, omniSortConfig.key, omniSortConfig.direction);
                        
                        return (
                        <div style={{ marginTop: 12 }}>
                          <h5>{t('calculationDetails') || 'Calculation Details'}:</h5>
                          <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", minWidth: "1200px" }}>
                              <thead>
                                <tr style={{ background: "#f5f5f5" }}>
                                  <th 
                                    onClick={() => handleSort('obstacleName')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('name') || 'Name'}{renderSortIndicator('obstacleName')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('areaType')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('area') || 'Area'}{renderSortIndicator('areaType')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('obstacleElevation')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('elevation') || 'Elev'} (m){renderSortIndicator('obstacleElevation')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('distanceDr')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('drDistance') || 'Dr distance'} (m){renderSortIndicator('distanceDr')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('distanceDo')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('doDistance') || 'Do distance'} (m){renderSortIndicator('distanceDo')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('requiredMocFt')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('requiredMocFt') || 'Req MOC (ft)'}{renderSortIndicator('requiredMocFt')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('actualAltitudeFt')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('actualAircraftAltitudeFt') || 'Act Alt (ft)'}{renderSortIndicator('actualAltitudeFt')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('requiredAltitudeFt')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('requiredCrossingAltitudeFt') || 'Req Alt (ft)'}{renderSortIndicator('requiredAltitudeFt')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('pdgRequired')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none", background: omniSortConfig.key === 'pdgRequired' ? '#e3f2fd' : '#f5f5f5' }}
                                  >
                                    {t('requiredPdg') || 'PDG %'}{renderSortIndicator('pdgRequired')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('isCritical')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('criticalObstacleCol') || 'Critical'}{renderSortIndicator('isCritical')}
                                  </th>
                                  <th 
                                    onClick={() => handleSort('isCloseIn')} 
                                    style={{ padding: 6, border: "1px solid #ddd", textAlign: "left", cursor: "pointer", userSelect: "none" }}
                                  >
                                    {t('closeInObstacleCol') || 'Close-in'}{renderSortIndicator('isCloseIn')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedObstacles.map((obs, obsIdx) => {
                                  const obstacleId = `${idx}-${obsIdx}`;
                                  const isSelected = selectedObstacles.has(obstacleId);
                                  const bgColor = isSelected 
                                    ? "#bbdefb" 
                                    : obs.isCritical 
                                      ? "#ffebee" 
                                      : "white";
                                  
                                  return (
                                  <tr 
                                    key={obsIdx}
                                    data-obstacle-id={obstacleId}
                                    onClick={(e) => handleRowClick(idx, obsIdx, e)}
                                    style={{ 
                                      background: bgColor,
                                      cursor: "pointer",
                                      transition: "background 0.2s"
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected) {
                                        e.currentTarget.style.background = obs.isCritical ? "#ffcdd2" : "#f5f5f5";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSelected) {
                                        e.currentTarget.style.background = obs.isCritical ? "#ffebee" : "white";
                                      }
                                    }}
                                  >
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.obstacleName}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.areaType}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.obstacleElevation.toFixed(2)}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.distanceDr ? obs.distanceDr.toFixed(2) : 'N/A'}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.distanceDo.toFixed(2)}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.requiredMocFt.toFixed(2)}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.actualAltitudeFt.toFixed(2)}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.requiredAltitudeFt.toFixed(2)}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>{obs.pdgRequired.toFixed(2)}</td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>
                                      {obs.isCritical ? (
                                        <span style={{ color: "#d32f2f", fontWeight: "bold" }}>YES</span>
                                      ) : (
                                        <span style={{ color: "#388e3c" }}>NO</span>
                                      )}
                                    </td>
                                    <td style={{ padding: 6, border: "1px solid #ddd" }}>
                                      {obs.isCloseIn ? (
                                        <span style={{ color: "#ff6f00", fontWeight: "bold" }}>YES</span>
                                      ) : (
                                        <span>NO</span>
                                      )}
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                  )}
                </details>
              ))}
            </div>
          )}
        </div>
        );
      })()}

      {omniError && (
        <div style={{ color: "#b30000", marginTop: 8, background: "#ffebee", padding: 12, borderRadius: 4 }}>
          <b>{t('omniError') || 'OMNI Analysis Error'}:</b> {omniError}
        </div>
      )}

      {/* OAS Analysis Results - Only for authorized users */}
      {oasAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!oasAnalysis.results?.some(r => r.categoryResults?.some(c => c.obstacles?.length > 0)
          || r.paoasResults?.length > 0)) return null;
        // Helper: sort OAS obstacles
        const sortOasObstacles = (obstacles, sortKey, sortDirection) => {
          const sorted = [...obstacles].sort((a, b) => {
            let aVal = a[sortKey];
            let bVal = b[sortKey];
            if (typeof aVal === 'number' && typeof bVal === 'number') {
              // Treat null/undefined as -Infinity so they sort last
              const aNum = aVal ?? -Infinity;
              const bNum = bVal ?? -Infinity;
              return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
            }
            aVal = String(aVal ?? '');
            bVal = String(bVal ?? '');
            return sortDirection === 'asc'
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          });
          return sorted;
        };

        // Helper: handle column header click
        const handleOasSort = (key) => {
          setOasSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
          }));
        };

        // Helper: render sort indicator arrow
        const renderOasSortIndicator = (columnKey) => {
          if (oasSortConfig.key !== columnKey) {
            return <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>;
          }
          return (
            <span style={{ marginLeft: 4 }}>
              {oasSortConfig.direction === 'asc' ? '▲' : '▼'}
            </span>
          );
        };

        // Helper: render OAS obstacle table
        const renderOasTable = (obstacles, resultIdx, catIdx) => {
          if (!obstacles || obstacles.length === 0) return null;
          // Attach unique IDs before sorting so we can track selections
          const indexedObstacles = obstacles.map((row, origIdx) => ({
            ...row,
            _oasId: `oas-${resultIdx}-${catIdx}-${origIdx}`,
          }));
          const sortedObstacles = sortOasObstacles(indexedObstacles, oasSortConfig.key, oasSortConfig.direction);
          const thSortStyle = (key) => ({
            padding: 6,
            border: '1px solid #90caf9',
            textAlign: 'left',
            background: oasSortConfig.key === key ? '#bbdefb' : '#e3f2fd',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            userSelect: 'none',
          });
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '1100px' }}>
                <thead>
                  <tr style={{ background: '#e3f2fd' }}>
                    <th style={thSortStyle('obstacleName')} onClick={() => handleOasSort('obstacleName')}>
                      {t('oasObstacleName') || 'Obstacle Name'}{renderOasSortIndicator('obstacleName')}
                    </th>
                    <th style={thSortStyle('obstacleAltM')} onClick={() => handleOasSort('obstacleAltM')}>
                      {t('oasObstacleAltM') || 'Alt (m)'}{renderOasSortIndicator('obstacleAltM')}
                    </th>
                    <th style={thSortStyle('ilsX')} onClick={() => handleOasSort('ilsX')}>
                      {t('oasIlsX') || 'ILS X (m)'}{renderOasSortIndicator('ilsX')}
                    </th>
                    <th style={thSortStyle('ilsY')} onClick={() => handleOasSort('ilsY')}>
                      {t('oasIlsY') || 'ILS Y (m)'}{renderOasSortIndicator('ilsY')}
                    </th>
                    <th style={thSortStyle('eqAltM')} onClick={() => handleOasSort('eqAltM')}>
                      {t('oasEqAlt') || 'Equiv. Alt (m)'}{renderOasSortIndicator('eqAltM')}
                    </th>
                    <th style={thSortStyle('surface')} onClick={() => handleOasSort('surface')}>
                      {t('oasSurface') || 'Surface'}{renderOasSortIndicator('surface')}
                    </th>
                    <th style={thSortStyle('surfaceAltM')} onClick={() => handleOasSort('surfaceAltM')}>
                      {t('oasSurfaceAltM') || 'Surf. Alt (m)'}{renderOasSortIndicator('surfaceAltM')}
                    </th>
                    <th style={thSortStyle('surfaceAltFt')} onClick={() => handleOasSort('surfaceAltFt')}>
                      {t('oasSurfaceAltFt') || 'Surf. Alt (ft)'}{renderOasSortIndicator('surfaceAltFt')}
                    </th>
                    <th style={thSortStyle('differenceM')} onClick={() => handleOasSort('differenceM')}>
                      {t('oasDiffM') || 'Diff (m)'}{renderOasSortIndicator('differenceM')}
                    </th>
                    <th style={thSortStyle('differenceFt')} onClick={() => handleOasSort('differenceFt')}>
                      {t('oasDiffFt') || 'Diff (ft)'}{renderOasSortIndicator('differenceFt')}
                    </th>
                    <th style={thSortStyle('ocaM')} onClick={() => handleOasSort('ocaM')}>
                      {t('oasOcaM') || 'OCA (m)'}{renderOasSortIndicator('ocaM')}
                    </th>
                    <th style={thSortStyle('ocaFt')} onClick={() => handleOasSort('ocaFt')}>
                      {t('oasOcaFt') || 'OCA (ft)'}{renderOasSortIndicator('ocaFt')}
                    </th>
                    <th style={thSortStyle('isCritical')} onClick={() => handleOasSort('isCritical')}>
                      {t('oasCritical') || 'Critical'}{renderOasSortIndicator('isCritical')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedObstacles.map((row, ri) => {
                    const isPenetrating = row.differenceM > 0;
                    const isCrit = row.isCritical;
                    const bg = isCrit ? '#ffcdd2' : isPenetrating ? '#ffebee' : 'white';
                    const obstacleId = row._oasId || `oas-obs-${ri}`;
                    const isSelected = selectedOasObstacles.has(obstacleId);
                    return (
                      <tr
                        key={ri}
                        data-oas-id={obstacleId}
                        style={{ background: isSelected ? '#bbdefb' : bg, cursor: 'pointer' }}
                        onClick={(e) => {
                          setSelectedOasObstacles(prev => {
                            const newSet = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (newSet.has(obstacleId)) newSet.delete(obstacleId);
                              else newSet.add(obstacleId);
                            } else {
                              newSet.clear();
                              newSet.add(obstacleId);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM.toFixed(2) : '—'}</td>
                        <td style={tdStyle}>{row.ilsX != null ? row.ilsX.toFixed(1) : '—'}</td>
                        <td style={tdStyle}>{row.ilsY != null ? row.ilsY.toFixed(1) : '—'}</td>
                        <td style={tdStyle}>{row.eqAltM != null ? row.eqAltM.toFixed(2) : '—'}</td>
                        <td style={tdStyle}>{row.surface}</td>
                        <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM.toFixed(2) : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt.toFixed(1) : '—'}</td>
                        <td style={{ ...tdStyle, color: isPenetrating ? '#d32f2f' : '#388e3c', fontWeight: isPenetrating ? 'bold' : 'normal' }}>
                          {row.differenceM != null ? row.differenceM.toFixed(2) : '—'}
                        </td>
                        <td style={{ ...tdStyle, color: isPenetrating ? '#d32f2f' : '#388e3c', fontWeight: isPenetrating ? 'bold' : 'normal' }}>
                          {row.differenceFt != null ? row.differenceFt.toFixed(1) : '—'}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: isCrit ? 'bold' : 'normal' }}>{row.ocaM != null ? row.ocaM : '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: isCrit ? 'bold' : 'normal' }}>{row.ocaFt != null ? row.ocaFt : '—'}</td>
                        <td style={tdStyle}>
                          {isCrit
                            ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                            : <span style={{ color: '#666' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        const thStyle = { padding: 6, border: '1px solid #90caf9', textAlign: 'left', background: '#e3f2fd', whiteSpace: 'nowrap' };
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        return (
          <div style={{ marginTop: 10, background: '#e8eaf6', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#1a237e' }}>
                {t('oasResults') || 'OAS Calculation Results'}
              </h3>
              <button
                onClick={() => {
                  const allOn = layerOasIls1 && layerOasIls2 && layerOasIls2ap && layerOasSbasApv1 && layerOasSbasCat1;
                  const newVal = !allOn;
                  setLayerOasIls1(newVal);
                  setLayerOasIls2(newVal);
                  setLayerOasIls2ap(newVal);
                  setLayerOasSbasApv1(newVal);
                  setLayerOasSbasCat1(newVal);
                }}
                title="Toggle OAS surface polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: (layerOasIls1 && layerOasIls2 && layerOasIls2ap && layerOasSbasApv1 && layerOasSbasCat1) ? '#1565c0' : '#e3f2fd',
                  color: (layerOasIls1 && layerOasIls2 && layerOasIls2ap && layerOasSbasApv1 && layerOasSbasCat1) ? '#fff' : '#1565c0',
                  border: '1px solid #1565c0', whiteSpace: 'nowrap',
                }}
              >
                🗺 {(layerOasIls1 && layerOasIls2 && layerOasIls2ap && layerOasSbasApv1 && layerOasSbasCat1) ? 'Hide OAS surfaces' : 'Show OAS surfaces'}
              </button>
            </div>

            {oasAnalysis.results && oasAnalysis.results.length > 0 ? (() => {
              // Only show runway entries that have at least one obstacle in any category OR in PAOAS
              const resultsWithObstacles = oasAnalysis.results.filter(result =>
                (result.categoryResults || []).some(c => c.obstacles && c.obstacles.length > 0) ||
                (result.paoasResults && result.paoasResults.length > 0)
              );
              if (resultsWithObstacles.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No obstacles found within OAS surfaces for any runway in range.
                  </p>
                );
              }
              return resultsWithObstacles.map((result, idx) => {
                const totalCritical = (result.categoryResults || []).reduce(
                  (sum, c) => sum + (c.criticalObstacles || 0), 0
                );
                return (
                <details key={idx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: 8 }}>
                    {result.input.airport_code} – {result.input.airport_name} – RWY {result.input.runway_designation}
                    {' '}({result.input.oas_category})
                    {totalCritical > 0
                      ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({totalCritical} {t('critical') || 'critical'})</span>
                      : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                    }
                    {result.input.error && (
                      <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                    )}
                  </summary>

                  {/* Input Parameters */}
                  <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#e8eaf6', padding: 8, borderRadius: 4 }}>
                    <b>{t('oasInputParams') || 'Input Parameters'}</b>
                    <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                      <tbody>
                        <tr>
                          <td style={tdStyle}>GPA (°)</td>
                          <td style={tdStyle}><b>{result.input.gpa_deg}</b></td>
                          <td style={tdStyle}>RDH</td>
                          <td style={tdStyle}><b>{result.input.rdh_ft != null ? `${(result.input.rdh_ft * 0.3048).toFixed(1)} m / ${result.input.rdh_ft} ft` : '—'}</b></td>
                          <td style={tdStyle}>DLT (m)</td>
                          <td style={tdStyle}><b>{result.input.dlt_m != null ? `${result.input.dlt_m} m` : '—'}</b></td>
                        </tr>
                        <tr>
                          <td style={tdStyle}>{t('oasMacg') || 'MA climb gradient'}</td>
                          <td style={tdStyle}><b>{result.input.macg_pct != null ? `${result.input.macg_pct}%` : '—'}</b></td>
                          <td style={tdStyle}>MA Evaluation Method</td>
                          <td style={tdStyle}><b>{result.input.ma_evaluation_method === 'STANDARD' ? 'Xz standard' : result.input.ma_evaluation_method === 'ALTERNATIVE' ? "VPA'" : result.input.ma_evaluation_method}</b></td>
                          <td style={tdStyle}>OAS Category</td>
                          <td style={tdStyle}><b>{result.input.oas_category}</b></td>
                        </tr>
                        <tr>
                          <td style={tdStyle}>THR Position</td>
                          <td style={tdStyle}><b>{result.input.thr_lat != null ? `${toDMS(result.input.thr_lat, true)} / ${toDMS(result.input.thr_lon, false)}` : '—'}</b></td>
                          <td style={tdStyle}>THR Elevation</td>
                          <td style={tdStyle}><b>{result.input.thr_elev_m != null ? `${result.input.thr_elev_m.toFixed(1)} m` : '—'}</b></td>
                          <td style={tdStyle}>Inbound Track (°)</td>
                          <td style={tdStyle}><b>{result.input.inbound_track_deg != null ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (UTM grid: ${result.input.utm_grid_bearing_deg?.toFixed(2)}°)` : '—'}</b></td>
                        </tr>
                        {result.input.paoas_designator && (
                          <tr>
                            <td style={tdStyle}>PAOAS Designator</td>
                            <td style={tdStyle}><b>{result.input.paoas_designator}</b></td>
                            {result.input.mrva_ft != null ? (
                              <>
                                <td style={tdStyle}>MRVA (ft)</td>
                                <td style={tdStyle}><b>{result.input.mrva_ft}</b></td>
                              </>
                            ) : <td style={tdStyle} colSpan={2} />}
                            <td style={tdStyle} colSpan={2} />
                          </tr>
                        )}
                        {result.input.preceding_segment_alt_ft != null && (
                          <tr>
                            <td style={tdStyle}>Intermediate Segment Alt (ft)</td>
                            <td style={tdStyle}><b>{result.input.preceding_segment_alt_ft}</b></td>
                            {result.input.preceding_segment_moc_m != null ? (
                              <>
                                <td style={tdStyle}>Intermediate Segment MOC (m)</td>
                                <td style={tdStyle}><b>{result.input.preceding_segment_moc_m}</b></td>
                              </>
                            ) : <td style={tdStyle} colSpan={2} />}
                            <td style={tdStyle} colSpan={2} />
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Per aircraft category – shown directly (no dropdown) */}
                  {result.categoryResults && result.categoryResults.map((catRes, catIdx) => (
                    catRes.obstacles?.length > 0 ? <div key={catIdx} style={{ marginTop: 8, border: '1px solid #c5cae9', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {t('oasAircraftCategory') || 'Aircraft Category'}: {catRes.acCategory}
                        {catRes.error && <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {catRes.error}</span>}
                        {catRes.obstacleOcaM != null && (
                          <span style={{ marginLeft: 16, color: '#1a237e' }}>
                            OCA = {catRes.obstacleOcaM} m / {catRes.obstacleOcaFt} ft
                          </span>
                        )}
                        {catRes.publishedOcaFt != null && (
                          <span style={{ marginLeft: 8, color: '#1a237e' }}>
                            | {t('oasPublishedMinima') || 'Published minima'}: {catRes.publishedOcaFt} ft
                          </span>
                        )}
                        {catRes.criticalObstacles > 0 && (
                          <span style={{ color: '#d32f2f', marginLeft: 8 }}>
                            ({catRes.criticalObstacles} {t('critical') || 'critical'})
                          </span>
                        )}
                      </div>

                      {/* Total / Critical summary */}
                      {catRes.obstaclesChecked != null && (
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ marginRight: 16 }}><strong>{t('totalObstaclesAssessed') || 'Total Obstacles Assessed'}:</strong> {catRes.obstaclesChecked}</span>
                          <strong>{t('oasCritical') || 'Critical'}:</strong>{' '}
                          <span style={{ color: catRes.criticalObstacles > 0 ? '#d32f2f' : 'inherit', fontWeight: catRes.criticalObstacles > 0 ? 'bold' : 'normal' }}>
                            {catRes.criticalObstacles || 0}
                          </span>
                        </div>
                      )}

                      {/* Constants debug panel */}
                      {catRes.constants && (
                        <div style={{ fontSize: '11px', fontFamily: 'monospace', background: '#f8f9fa', padding: 8, marginTop: 4, borderRadius: 3 }}>
                          <strong>{t('oasConstants') || 'OAS Constants'}:</strong>
                          {' '}WA={catRes.constants.WA} WC={catRes.constants.WC}
                          {catRes.constants.WSA != null && <span> | WSA={catRes.constants.WSA} WSC={catRes.constants.WSC}</span>}
                          {' '}| XA={catRes.constants.XA} XB={catRes.constants.XB} XC={catRes.constants.XC}
                          {' '}| YA={catRes.constants.YA} YB={catRes.constants.YB} YC={catRes.constants.YC}
                          {' '}| ZA={catRes.constants.ZA} ZC={catRes.constants.ZC}
                          {' '}| HL={catRes.constants.HL_m} m
                          {' '}| WSS={catRes.constants.wssM} m, WH={catRes.constants.whM} m, RDH={catRes.constants.rdhM?.toFixed(2)} m
                        </div>
                      )}

                      {/* Results table – shown directly without a dropdown */}
                      {catRes.obstacles && catRes.obstacles.length > 0
                        ? renderOasTable(catRes.obstacles, idx, catIdx)
                        : (
                          <p style={{ color: '#666', fontStyle: 'italic', marginTop: 8 }}>
                            No obstacles within OAS surfaces for category {catRes.acCategory}.
                          </p>
                        )}
                    </div> : null
                  ))}

                  {/* PAOAS P1/P2 results (aircraft-category independent) */}
                  {result.paoasResults && result.paoasResults.length > 0 && (
                    <div style={{ marginTop: 12, border: '1px solid #b39ddb', borderRadius: 4, padding: 8, background: '#f3e5f5' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 6, color: '#4a148c' }}>
                        PAOAS Surfaces (P1 / P2) – designator: {result.input.paoas_designator}
                        {result.input.mrva_ft !== null && result.input.mrva_ft !== undefined && (
                          <span style={{ marginLeft: 8, fontWeight: 'normal', color: '#666' }}>
                            MRVA: {result.input.mrva_ft} ft
                          </span>
                        )}
                        {result.paoasResults.some(r => r.penetrated) && (
                          <span style={{ color: '#d32f2f', marginLeft: 8 }}>
                            ⚠ {result.paoasResults.filter(r => r.penetrated).length} penetrated
                          </span>
                        )}
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr style={{ background: '#e1bee7' }}>
                              <th style={thStyle}>{t('oasObstacleName') || 'Obstacle Name'}</th>
                              <th style={thStyle}>{t('oasObstacleAltM') || 'Alt (m)'}</th>
                              <th style={thStyle}>{t('oasIlsX') || 'ILS X (m)'}</th>
                              <th style={thStyle}>{t('oasIlsY') || 'ILS Y (m)'}</th>
                              <th style={thStyle}>{t('oasSurface') || 'Surface'}</th>
                              <th style={thStyle}>{t('oasSurfaceAltM') || 'Surf. Alt (m)'}</th>
                              <th style={thStyle}>{t('oasSurfaceAltFt') || 'Surf. Alt (ft)'}</th>
                              <th style={thStyle}>{t('oasDiffM') || 'Diff (m)'}</th>
                              <th style={thStyle}>{t('oasDiffFt') || 'Diff (ft)'}</th>
                              <th style={thStyle}>{t('oasPenetrated') || 'Penetrated'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.paoasResults.map((row, ri) => {
                              const bg = row.penetrated ? '#ffebee' : 'white';
                              return (
                                <tr key={ri} style={{ background: bg }}>
                                  <td style={tdStyle}>{row.obstacleName}</td>
                                  <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM.toFixed(2) : '—'}</td>
                                  <td style={tdStyle}>{row.ilsX != null ? row.ilsX.toFixed(1) : '—'}</td>
                                  <td style={tdStyle}>{row.ilsY != null ? row.ilsY.toFixed(1) : '—'}</td>
                                  <td style={tdStyle}>{row.surface}</td>
                                  <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM.toFixed(2) : '—'}</td>
                                  <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt.toFixed(1) : '—'}</td>
                                  <td style={{ ...tdStyle, color: row.penetrated ? '#d32f2f' : '#388e3c', fontWeight: row.penetrated ? 'bold' : 'normal' }}>
                                    {row.differenceM != null ? row.differenceM.toFixed(2) : '—'}
                                  </td>
                                  <td style={{ ...tdStyle, color: row.penetrated ? '#d32f2f' : '#388e3c', fontWeight: row.penetrated ? 'bold' : 'normal' }}>
                                    {row.differenceFt != null ? row.differenceFt.toFixed(1) : '—'}
                                  </td>
                                  <td style={tdStyle}>
                                    {row.penetrated
                                      ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                                      : <span style={{ color: '#388e3c' }}>No</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {oasAnalysis.message || 'No OAS configurations found for airports in range.'}
              </p>
            )}
          </div>
        );
      })()}

      {oasError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>{t('oasError') || 'OAS Calculation Error'}:</b> {oasError}
        </div>
      )}

      {/* BARO/VNAV OAS Results - Only for authorized users */}
      {baroVnavAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!baroVnavAnalysis.results?.some(r => r.categoryResults?.some(c => !c.error && c.obstacleRows?.length > 0))) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };
        // thStyle used in parameter/summary tables that don't need sorting
        const thStyle = { padding: 6, border: '1px solid #a5d6a7', textAlign: 'left', background: '#e8f5e9', whiteSpace: 'nowrap' };

        // Sort helper (generic, reused across analyses)
        const sortRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleBaroSort = (key) => setBaroSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderBaroSortIndicator = (key) => (
          baroSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{baroSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          padding: 6, border: '1px solid #a5d6a7', textAlign: 'left', whiteSpace: 'nowrap',
          background: baroSortConfig.key === key ? '#b9f6ca' : '#e8f5e9',
          cursor: 'pointer', userSelect: 'none',
        });

        const thPlain = { padding: 6, border: '1px solid #a5d6a7', textAlign: 'left', background: '#e8f5e9', whiteSpace: 'nowrap' };

        return (
          <div style={{ marginTop: 10, background: '#f1f8e9', padding: 16, borderRadius: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <h3 style={{ marginTop: 0, marginBottom: 0, color: '#1b5e20' }}>
              {t('baroVnavResults') || 'BARO/VNAV OAS Calculation Results'}
            </h3>
            <button
              onClick={() => setShowBaroSurfaces(v => !v)}
              title="Toggle BARO/VNAV surface polygons on the map"
              style={{
                padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                background: showBaroSurfaces ? '#1b5e20' : '#dcedc8',
                color: showBaroSurfaces ? '#fff' : '#1b5e20',
                border: '1px solid #1b5e20', whiteSpace: 'nowrap',
              }}
            >
              🗺 {showBaroSurfaces ? 'Hide BaroVNAV surfaces' : 'Show BaroVNAV surfaces'}
            </button>
          </div>

            {baroVnavAnalysis.results && baroVnavAnalysis.results.length > 0 ? (() => {
              const resultsWithCats = baroVnavAnalysis.results.filter(r =>
                r.categoryResults && r.categoryResults.some(c => !c.error && c.obstacleRows && c.obstacleRows.length > 0)
              );
              if (resultsWithCats.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No BARO/VNAV configurations found for airports in range.
                  </p>
                );
              }
              return resultsWithCats.map((result) => {
                // Track original result index for stable marker IDs
                const resultIdx = baroVnavAnalysis.results.indexOf(result);
                const fafDistanceNm = result.input.effective_thr_faf_distance_nm ?? result.input.thr_faf_distance_nm;
                const fafDistanceSource = result.input.thr_faf_distance_source === 'calculated'
                  ? (t('language') === 'Svenska' ? 'beräknat' : 'calculated')
                  : (t('language') === 'Svenska' ? 'angivet' : 'entered');
                const totalBaroCritical = (result.categoryResults || []).reduce(
                  (sum, c) => sum + (c.obstacleRows ? c.obstacleRows.filter(r => r.isCritical).length : 0), 0
                );
                return (
                <details key={resultIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#1b5e20' }}>
                    {result.input.airport_name || result.input.airport_code} – RWY {result.input.runway_designation}
                    {totalBaroCritical > 0
                      ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({totalBaroCritical} {t('critical') || 'critical'})</span>
                      : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                    }
                    {result.input.error && (
                      <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                    )}
                  </summary>

                  {/* Input Parameters */}
                  <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#f9fbe7', padding: 8, borderRadius: 4 }}>
                    <b>{t('baroVnavInputParams') || 'Input Parameters'}</b>
                    <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                      <tbody>
                        <tr>
                          <td style={tdStyle}>{t('baroVnavVpa') || 'VPA (°)'}</td>
                          <td style={tdStyle}><b>{result.input.vertical_path_angle_deg}</b></td>
                          <td style={tdStyle}>{t('baroVnavRdh') || 'RDH'}</td>
                          <td style={tdStyle}><b>{result.input.rdh_ft != null ? `${(result.input.rdh_ft * 0.3048).toFixed(1)} m / ${result.input.rdh_ft} ft` : '—'}</b></td>
                          <td style={tdStyle}>{t('baroVnavMinAlt') || 'Min Altitude (ft)'}</td>
                          <td style={tdStyle}><b>{result.input.min_altitude_ft}</b></td>
                        </tr>
                        <tr>
                          <td style={tdStyle}>{t('baroVnavMinTemp') || 'Min Temperature (°C)'}</td>
                          <td style={tdStyle}><b>{result.input.minimum_temperature_c}</b></td>
                          <td style={tdStyle}>{t('baroVnavIsaDev') || 'ISA Deviation (°C)'}</td>
                          <td style={tdStyle}><b>{result.input.isa_deviation_c}</b></td>
                          <td style={tdStyle}>{t('baroVnavMacg') || 'MACG (%)'}</td>
                          <td style={tdStyle}><b>{result.input.macg_percent}</b></td>
                        </tr>
                        <tr>
                          <td style={tdStyle}>{t('baroVnavEvalMethod') || 'MA Evaluation Method'}</td>
                          <td style={tdStyle}><b>{result.input.eval_method_ma === 'STANDARD' ? 'Xz standard' : result.input.eval_method_ma === 'ALTERNATIVE' ? "VPA'" : result.input.eval_method_ma}</b></td>
                          <td style={tdStyle}>{t('baroVnavArpElevFt') || 'ARP Elev'}</td>
                          <td style={tdStyle}><b>{result.input.arp_elev_m != null && result.input.arp_elev_ft != null ? `${result.input.arp_elev_m} m / ${result.input.arp_elev_ft} ft` : '—'}</b></td>
                          <td style={tdStyle}>THR Elev</td>
                          <td style={tdStyle}><b>{result.input.thr_elev_m != null ? `${result.input.thr_elev_m.toFixed(1)} m / ${Math.round(result.input.thr_elev_m / 0.3048)} ft` : '—'}</b></td>
                        </tr>
                        <tr>
                          <td style={tdStyle}>THR Position</td>
                          <td style={tdStyle}><b>{result.input.thr_lat != null ? `${toDMS(result.input.thr_lat, true)} / ${toDMS(result.input.thr_lon, false)}` : '—'}</b></td>
                          <td style={tdStyle}>{t('baroVnavInboundTrack') || 'Inbound Track (°)'}</td>
                          <td style={tdStyle} colSpan={3}><b>{result.input.inbound_track_deg != null ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (UTM grid: ${result.input.utm_grid_bearing_deg?.toFixed(2)}°)` : '—'}</b></td>
                        </tr>
                        <tr>
                          <td style={tdStyle}>THR - FAF/FAP</td>
                          <td style={tdStyle} colSpan={5}><b>{fafDistanceNm != null
                            ? `${Number(fafDistanceNm).toFixed(3)} NM (${fafDistanceSource})` : '—'}</b></td>
                        </tr>
                        {result.input.offset_final_approach_track && (
                          <tr>
                            <td style={tdStyle}>{t('baroVnavOffsetAngle') || 'Offset Angle (°)'}</td>
                            <td style={tdStyle} colSpan={5}><b>{result.input.offset_angle_deg}</b></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Per-category results table */}
                  {result.categoryResults && result.categoryResults.length > 0 && (
                    <div style={{ overflowX: 'auto', marginTop: 8 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>{t('baroVnavAircraftCategory') || 'Cat'}</th>
                            <th style={thStyle}>{t('baroVnavIas') || 'Max IAS (kts)'}</th>
                            <th style={thStyle}>{t('baroVnavIasAtThr') || 'Max IAS at LTP/FTP (kts)'}</th>
                            <th style={thStyle} title="Height of intermediate segment minimum altitude above THR">{t('baroVnavOch') || 'OCH (m)'}</th>
                            <th style={thStyle}>{t('baroVnavHeightLoss') || 'HL (m)'}</th>
                            <th style={thStyle} title="VPA/altitude correction added on top of the base Height Loss (0 when VPA ≤ 3.2° and ARP ≤ 900 m)">{t('baroVnavHlCorrection') || 'HL Correction (m)'}</th>
                            <th style={thStyle}>{t('baroVnavTempCorr') || 'TC (m)'}</th>
                            <th style={thStyle}>{t('baroVnavXfap') || 'X FAP (m)'}</th>
                            <th style={thStyle}>{t('baroVnavXfas') || 'X FAS'}</th>
                            <th style={thStyle}>{t('baroVnavFasAngle') || 'FAS Angle'}</th>
                            <th style={thStyle}>{t('baroVnavXz') || 'Xz'}</th>
                            <th style={thStyle}>{t('baroVnavMinVpa') || 'Min VPA (°)'}</th>
                            <th style={thStyle}>{t('baroVnavMaxTemp35') || 'Max Temp@3.5° (°C)'}</th>
                            {result.input.offset_final_approach_track && (
                              <>
                                <th style={thStyle}>{t('baroVnavDMin') || 'Min D (m)'}</th>
                                <th style={thStyle}>{t('baroVnavOchMin') || 'Min OCH (m)'}</th>
                                <th style={thStyle}>{t('baroVnavOchMinFt') || 'Min OCH (ft)'}</th>
                                <th style={thStyle}>{t('baroVnavOcaMin') || 'Min OCA (m)'}</th>
                                <th style={thStyle}>{t('baroVnavOcaMinFt') || 'Min OCA (ft)'}</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {result.categoryResults.map((cat, ci) => {
                            if (cat.error) {
                              return (
                                <tr key={ci} style={{ background: '#ffebee' }}>
                                  <td style={tdStyle}><b>{cat.acCategory}</b></td>
                                  <td colSpan={20} style={{ ...tdStyle, color: '#d32f2f' }}>
                                    Error: {cat.error}
                                  </td>
                                </tr>
                              );
                            }
                            const minVpaWarn = cat.minVPADeg != null && cat.minVPADeg < 2.5;
                            // Warn if max temp at 3.5° VPA is below the actual minimum temperature
                            // (meaning the 3.5° VPA limit is already reached at or above min temp)
                            const actualMinTemp = result.input.minimum_temperature_c;
                            const maxTempWarn = cat.maxTempAt35VPA != null &&
                              actualMinTemp != null &&
                              cat.maxTempAt35VPA < actualMinTemp;
                            return (
                              <tr key={ci} style={{ background: minVpaWarn ? '#fff9c4' : 'white' }}>
                                <td style={{ ...tdStyle, fontWeight: 'bold' }}>{cat.acCategory}</td>
                                <td style={tdStyle}>{cat.iasKts}</td>
                                <td style={tdStyle}>{cat.iasAtThrKts}</td>
                                <td style={tdStyle}>{cat.ochM != null ? cat.ochM.toFixed(1) : '—'}</td>
                                <td style={tdStyle}>{cat.hlM != null ? cat.hlM : '—'}</td>
                                <td style={tdStyle}>{cat.hlCorrectionM != null ? cat.hlCorrectionM : '—'}</td>
                                <td style={tdStyle}>
                                  {cat.tcM != null ? cat.tcM.toFixed(1) : '—'}
                                </td>
                                <td style={tdStyle}>{cat.xfapM != null ? cat.xfapM.toFixed(2) : '—'}</td>
                                <td style={tdStyle} title={cat.mocpFas != null ? `MOC = ${cat.mocpFas} m` : ''}>
                                  {cat.xfasM != null ? cat.xfasM.toFixed(2) : '—'}
                                </td>
                                <td style={tdStyle} title={cat.mocpFas != null ? `MOC = ${cat.mocpFas} m` : ''}>
                                  {cat.fasAngleDeg != null ? cat.fasAngleDeg.toFixed(2) : '—'}
                                </td>
                                <td style={tdStyle}>{cat.xzM != null ? cat.xzM.toFixed(0) : '—'}</td>
                                <td style={{ ...tdStyle, color: minVpaWarn ? '#d32f2f' : 'inherit', fontWeight: minVpaWarn ? 'bold' : 'normal' }}>
                                  {cat.minVPADeg != null ? cat.minVPADeg.toFixed(2) : '—'}
                                  {minVpaWarn && ' ⚠ <2.5°'}
                                </td>
                                <td style={{ ...tdStyle, color: maxTempWarn ? '#d32f2f' : 'inherit' }}>
                                  {cat.maxTempAt35VPA != null ? cat.maxTempAt35VPA.toFixed(1) : '—'}
                                </td>
                                {result.input.offset_final_approach_track && (
                                  <>
                                    <td style={tdStyle}>{cat.offsetCalc ? cat.offsetCalc.dMinM.toFixed(1) : '—'}</td>
                                    <td style={tdStyle}>{cat.offsetCalc ? cat.offsetCalc.ochMinM.toFixed(1) : '—'}</td>
                                    <td style={tdStyle}>{cat.offsetCalc ? cat.offsetCalc.ochMinFt.toFixed(1) : '—'}</td>
                                    <td style={tdStyle}>{cat.offsetCalc ? cat.offsetCalc.ocaMinM.toFixed(1) : '—'}</td>
                                    <td style={tdStyle}>{cat.offsetCalc ? cat.offsetCalc.ocaMinFt.toFixed(1) : '—'}</td>
                                  </>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Obstacle evaluation – one section per aircraft category, shown directly like OAS */}
                  {result.categoryResults && result.categoryResults.map((cat, catIdx) => {
                    if (cat.error || !cat.obstacleRows?.length) return null;
                    const criticalCount = cat.obstacleRows ? cat.obstacleRows.filter(r => r.isCritical).length : 0;
                    return (
                    <div key={catIdx} style={{ marginTop: 12, border: '1px solid #c8e6c9', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 13 }}>
                        {t('baroVnavAircraftCategory') || 'Aircraft Category'}: {cat.acCategory}
                        {cat.maxOcaM != null && cat.maxOcaFt != null && (
                          <span style={{ marginLeft: 16, color: '#1a237e' }}>
                            OCA = {cat.maxOcaM} m / {cat.maxOcaFt} ft
                          </span>
                        )}
                        {cat.publishedOcaFt != null && (
                          <span style={{ marginLeft: 12, color: '#388e3c' }}>
                            | {t('baroVnavPublishedMinima') || 'Published'}: {cat.publishedOcaFt} ft
                          </span>
                        )}
                        {!cat.minimaFound && (
                          <span style={{ marginLeft: 12, color: '#f57c00', fontSize: 11 }}>
                            ({t('baroVnavNoPublishedMinima') || 'no published minima'})
                          </span>
                        )}
                        {criticalCount > 0 && (
                          <span style={{ color: '#d32f2f', marginLeft: 8 }}>
                            ({criticalCount} {t('critical') || 'critical'})
                          </span>
                        )}
                      </div>
                      {cat.obstacleRows != null && (
                        <div style={{ marginBottom: 4 }}>
                          <p><strong>{t('totalObstaclesAssessed') || 'Total Obstacles Assessed'}:</strong> {cat.obstacleRows.length}</p>
                          <p><strong>{t('criticalObstacle') || 'Critical Obstacle'}:</strong> {criticalCount}</p>
                        </div>
                      )}
                      {cat.obstacleRows && cat.obstacleRows.length > 0 ? (
                        <div style={{ marginTop: 12 }}>
                          <h5>{t('calculationDetails') || 'Calculation Details'}:</h5>
                        <div style={{ overflowX: 'auto', marginTop: 4 }}>
                          <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '1100px' }}>
                            <thead>
                              <tr style={{ background: '#c8e6c9' }}>
                                <th style={thSort('obstacleName')} onClick={() => handleBaroSort('obstacleName')}>{t('baroVnavObstName') || 'Obstacle Name'}{renderBaroSortIndicator('obstacleName')}</th>
                                <th style={thSort('obstacleAltM')} onClick={() => handleBaroSort('obstacleAltM')}>{t('baroVnavObstAlt') || 'Obstacle Alt (m)'}{renderBaroSortIndicator('obstacleAltM')}</th>
                                <th style={thSort('area')} onClick={() => handleBaroSort('area')}>{t('baroVnavArea') || 'Area'}{renderBaroSortIndicator('area')}</th>
                                <th style={thPlain} title="Perpendicular distance from the inner edge of the secondary area to the obstacle">{t('baroVnavDistInSec') || 'Dist fr inner edge (m)'}</th>
                                <th style={thSort('alongTrackM')} onClick={() => handleBaroSort('alongTrackM')}>{t('baroVnavAlongTrackM') || 'Along-track dist (m)'}{renderBaroSortIndicator('alongTrackM')}</th>
                                <th style={thPlain}>{t('baroVnavMocM') || 'MOC (m)'}</th>
                                <th style={thPlain}>{t('baroVnavMocFt') || 'MOC (ft)'}</th>
                                <th style={thSort('eqHeightM')} onClick={() => handleBaroSort('eqHeightM')}>{t('baroVnavEqHeightM') || 'Equiv. Height (m)'}{renderBaroSortIndicator('eqHeightM')}</th>
                                <th style={thSort('eqHeightFt')} onClick={() => handleBaroSort('eqHeightFt')}>{t('baroVnavEqHeightFt') || 'Equiv. Height (ft)'}{renderBaroSortIndicator('eqHeightFt')}</th>
                                <th style={thSort('eqAltM')} onClick={() => handleBaroSort('eqAltM')}>{t('baroVnavEqAltM') || 'Equiv. Alt (m)'}{renderBaroSortIndicator('eqAltM')}</th>
                                <th style={thSort('eqAltFt')} onClick={() => handleBaroSort('eqAltFt')}>{t('baroVnavEqAltFt') || 'Equiv. Alt (ft)'}{renderBaroSortIndicator('eqAltFt')}</th>
                                <th style={thSort('surface')} onClick={() => handleBaroSort('surface')}>{t('baroVnavSurface') || 'Surface'}{renderBaroSortIndicator('surface')}</th>
                                <th style={thSort('surfaceAltM')} onClick={() => handleBaroSort('surfaceAltM')}>{t('baroVnavSurfAltM') || 'Surface Alt (m)'}{renderBaroSortIndicator('surfaceAltM')}</th>
                                <th style={thSort('surfaceAltFt')} onClick={() => handleBaroSort('surfaceAltFt')}>{t('baroVnavSurfAltFt') || 'Surface Alt (ft)'}{renderBaroSortIndicator('surfaceAltFt')}</th>
                                <th style={thSort('differenceM')} onClick={() => handleBaroSort('differenceM')}>{t('baroVnavDiffM') || 'Diff (m)'}{renderBaroSortIndicator('differenceM')}</th>
                                <th style={thSort('differenceFt')} onClick={() => handleBaroSort('differenceFt')}>{t('baroVnavDiffFt') || 'Diff (ft)'}{renderBaroSortIndicator('differenceFt')}</th>
                                <th style={thPlain} title="Height Loss applied in the OCA calculation">{t('baroVnavHlM') || 'HL (m)'}</th>
                                <th style={thPlain}>{t('baroVnavHlFt') || 'HL (ft)'}</th>
                                <th style={thSort('ocaM')} onClick={() => handleBaroSort('ocaM')}>{t('baroVnavOcaM') || 'OCA (m)'}{renderBaroSortIndicator('ocaM')}</th>
                                <th style={thSort('ocaFt')} onClick={() => handleBaroSort('ocaFt')}>{t('baroVnavOcaFt') || 'OCA (ft)'}{renderBaroSortIndicator('ocaFt')}</th>
                                <th style={thSort('isCritical')} onClick={() => handleBaroSort('isCritical')}>{t('baroVnavCritical') || 'Critical'}{renderBaroSortIndicator('isCritical')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortRows(
                                cat.obstacleRows.map((row, ri) => ({ ...row, _baroId: `baro-${resultIdx}-${catIdx}-${ri}` })),
                                baroSortConfig.key, baroSortConfig.direction
                              ).map((row) => {
                                const id = row._baroId;
                                const isSelected = selectedBaroObstacles.has(id);
                                const isPenetrating = row.differenceM > 0;
                                const isCrit = row.isCritical;
                                const baseBg = isCrit ? '#ffcdd2' : isPenetrating ? '#ffebee' : 'white';
                                return (
                                  <tr
                                    key={id}
                                    data-baro-id={id}
                                    onClick={(e) => {
                                      setSelectedBaroObstacles(prev => {
                                        const s = new Set(prev);
                                        if (e.ctrlKey || e.metaKey) {
                                          if (s.has(id)) s.delete(id); else s.add(id);
                                        } else { s.clear(); s.add(id); }
                                        return s;
                                      });
                                    }}
                                    style={{ background: isSelected ? '#bbdefb' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = isCrit ? '#ffcdd2' : '#f5f5f5'; }}
                                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                                  >
                                    <td style={tdStyle}>{row.obstacleName}</td>
                                    <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                                    <td style={tdStyle}>{row.area}</td>
                                    <td style={tdStyle}>{row.distInSecM != null ? row.distInSecM : '—'}</td>
                                    <td style={tdStyle}>{row.alongTrackM != null ? row.alongTrackM : '—'}</td>
                                    <td style={tdStyle}>{row.mocM != null ? row.mocM : '—'}</td>
                                    <td style={tdStyle}>{row.mocFt != null ? row.mocFt : '—'}</td>
                                    <td style={tdStyle}>{row.eqHeightM != null ? row.eqHeightM : '—'}</td>
                                    <td style={tdStyle}>{row.eqHeightFt != null ? row.eqHeightFt : '—'}</td>
                                    <td style={tdStyle}>{row.eqAltM != null ? row.eqAltM : '—'}</td>
                                    <td style={tdStyle}>{row.eqAltFt != null ? row.eqAltFt : '—'}</td>
                                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{row.surface}</td>
                                    <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM : '—'}</td>
                                    <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt : '—'}</td>
                                    <td style={{ ...tdStyle, color: isPenetrating ? '#d32f2f' : '#388e3c', fontWeight: isPenetrating ? 'bold' : 'normal' }}>
                                      {row.differenceM != null ? row.differenceM : '—'}
                                    </td>
                                    <td style={{ ...tdStyle, color: isPenetrating ? '#d32f2f' : '#388e3c' }}>
                                      {row.differenceFt != null ? row.differenceFt : '—'}
                                    </td>
                                    <td style={tdStyle}>{row.hlM != null ? row.hlM : '—'}</td>
                                    <td style={tdStyle}>{row.hlFt != null ? row.hlFt : '—'}</td>
                                    <td style={{ ...tdStyle, fontWeight: isCrit ? 'bold' : 'normal' }}>
                                      {row.ocaM != null ? row.ocaM : '—'}
                                    </td>
                                    <td style={{ ...tdStyle, fontWeight: isCrit ? 'bold' : 'normal' }}>
                                      {row.ocaFt != null ? row.ocaFt : '—'}
                                    </td>
                                    <td style={tdStyle}>
                                      {isCrit
                                        ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                                        : <span style={{ color: '#666' }}>—</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        </div>
                      ) : (
                        <p style={{ color: '#666', fontStyle: 'italic', marginTop: 8 }}>
                          {t('baroVnavNoObstacles') || 'No obstacles within protection surfaces'}.
                        </p>
                      )}
                    </div>
                  );
                  })}
                  </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {baroVnavAnalysis.message || 'No BARO/VNAV configurations found for airports in range.'}
              </p>
            )}
          </div>
        );
      })()}

      {baroVnavError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>{t('baroVnavError') || 'BARO/VNAV OAS Calculation Error'}:</b> {baroVnavError}
        </div>
      )}

      {/* VSS (Visual Segment Surface) Results - Only for authorized users */}
      {vssAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!vssAnalysis.results?.some(r => r.vssRows?.length > 0 || r.ocsRows?.length > 0)) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        const sortVssRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleVssSort = (key) => setVssSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderVssSortIndicator = (key) => (
          vssSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{vssSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          padding: 6, border: '1px solid #90caf9', textAlign: 'left', whiteSpace: 'nowrap',
          background: vssSortConfig.key === key ? '#bbdefb' : '#e3f2fd',
          cursor: 'pointer', userSelect: 'none',
        });

        /**
         * Render one obstacle results table (VSS or OCS rows).
         * resultIdx and surface (lowercase) are used to generate stable row IDs.
         */
        const renderVssTable = (rows, surface, resultIdx) => {
          if (!rows || rows.length === 0) return null;
          const surfKey = surface.toLowerCase();
          // Attach stable IDs before sorting
          const indexedRows = rows.map((row, origIdx) => ({ ...row, _vssId: `vss-${resultIdx}-${surfKey}-${origIdx}` }));
          const sortedRows = sortVssRows(indexedRows, vssSortConfig.key, vssSortConfig.direction);
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '900px', width: '100%' }}>
                <thead>
                  <tr style={{ background: surface === 'VSS' ? '#bbdefb' : '#b3e5fc' }}>
                    <th style={thSort('obstacleName')} onClick={() => handleVssSort('obstacleName')}>{t('baroVnavObstName') || 'Obstacle Name'}{renderVssSortIndicator('obstacleName')}</th>
                    <th style={thSort('obstacleAltM')} onClick={() => handleVssSort('obstacleAltM')}>{t('vssObstAltM') || 'Alt (m)'}{renderVssSortIndicator('obstacleAltM')}</th>
                    <th style={thSort('obstacleAltFt')} onClick={() => handleVssSort('obstacleAltFt')}>{t('vssObstAltFt') || 'Alt (ft)'}{renderVssSortIndicator('obstacleAltFt')}</th>
                    <th style={thSort('alongTrackM')} onClick={() => handleVssSort('alongTrackM')}>{t('baroVnavAlongTrackM') || 'Along-track (m)'}{renderVssSortIndicator('alongTrackM')}</th>
                    <th style={thSort('surface')} onClick={() => handleVssSort('surface')}>{t('baroVnavSurface') || 'Surface'}{renderVssSortIndicator('surface')}</th>
                    <th style={thSort('surfaceAltM')} onClick={() => handleVssSort('surfaceAltM')}>{t('baroVnavSurfAltM') || 'Surf. Alt (m)'}{renderVssSortIndicator('surfaceAltM')}</th>
                    <th style={thSort('surfaceAltFt')} onClick={() => handleVssSort('surfaceAltFt')}>{t('baroVnavSurfAltFt') || 'Surf. Alt (ft)'}{renderVssSortIndicator('surfaceAltFt')}</th>
                    <th style={thSort('differenceM')} onClick={() => handleVssSort('differenceM')}>{t('baroVnavDiffM') || 'Diff (m)'}{renderVssSortIndicator('differenceM')}</th>
                    <th style={thSort('differenceFt')} onClick={() => handleVssSort('differenceFt')}>{t('baroVnavDiffFt') || 'Diff (ft)'}{renderVssSortIndicator('differenceFt')}</th>
                    <th style={thSort('isCritical')} onClick={() => handleVssSort('isCritical')}>{t('baroVnavCritical') || 'Critical'}{renderVssSortIndicator('isCritical')}</th>
                    {surface === 'VSS' && <th style={thSort('isDisregardable')} onClick={() => handleVssSort('isDisregardable')}>{t('vssDisregardable') || 'Disregardable'}{renderVssSortIndicator('isDisregardable')}</th>}
                    <th style={{ padding: 6, border: '1px solid #90caf9', textAlign: 'left', background: '#e3f2fd', whiteSpace: 'nowrap' }}>{t('vssImpact') || 'Impact'}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const id = row._vssId;
                    const isSelected = selectedVssObstacles.has(id);
                    const isPenetrating = row.differenceM > 0;
                    const isCrit = row.isCritical;
                    const hasImpact = isCrit && (surface === 'OCS' || !row.isDisregardable);
                    const baseBg = hasImpact ? '#ffcdd2' : isCrit ? '#fff3e0' : isPenetrating ? '#ffebee' : 'white';
                    return (
                      <tr
                        key={id}
                        data-vss-id={id}
                        onClick={(e) => {
                          setSelectedVssObstacles(prev => {
                            const s = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (s.has(id)) s.delete(id); else s.add(id);
                            } else { s.clear(); s.add(id); }
                            return s;
                          });
                        }}
                        style={{ background: isSelected ? '#bbdefb' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = hasImpact ? '#ffcdd2' : '#f5f5f5'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                        <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                        <td style={tdStyle}>{row.alongTrackM != null ? row.alongTrackM : '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 'bold' }}>{row.surface}</td>
                        <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt : '—'}</td>
                        <td style={{ ...tdStyle, color: isPenetrating ? '#d32f2f' : '#388e3c', fontWeight: isPenetrating ? 'bold' : 'normal' }}>
                          {row.differenceM != null ? row.differenceM : '—'}
                        </td>
                        <td style={{ ...tdStyle, color: isPenetrating ? '#d32f2f' : '#388e3c' }}>
                          {row.differenceFt != null ? row.differenceFt : '—'}
                        </td>
                        <td style={tdStyle}>
                          {isCrit
                            ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                            : <span style={{ color: '#666' }}>—</span>}
                        </td>
                        {surface === 'VSS' && (
                          <td style={{ ...tdStyle, color: row.isDisregardable ? '#f57c00' : 'inherit' }}>
                            {row.isDisregardable ? 'Yes' : 'No'}
                          </td>
                        )}
                        <td style={tdStyle}>
                          {hasImpact
                            ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                            : <span style={{ color: '#666' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        return (
          <div style={{ marginTop: 10, background: '#e3f2fd', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#0d47a1' }}>
                {t('vssResults') || 'VSS Calculation Results'}
              </h3>
              <button
                onClick={() => setShowVssSurfaces(v => !v)}
                title="Toggle VSS/OCS surface polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showVssSurfaces ? '#0d47a1' : '#bbdefb',
                  color: showVssSurfaces ? '#fff' : '#0d47a1',
                  border: '1px solid #0d47a1', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showVssSurfaces ? 'Hide VSS surfaces' : 'Show VSS surfaces'}
              </button>
            </div>

            {vssAnalysis.results && vssAnalysis.results.length > 0 ? (() => {
              const resultsWithObs = vssAnalysis.results.filter(r =>
                (r.vssRows && r.vssRows.length > 0) || (r.ocsRows && r.ocsRows.length > 0)
              );
              if (resultsWithObs.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No obstacles found within VSS/OCS surfaces for any runway in range.
                  </p>
                );
              }
              return resultsWithObs.map((result) => {
                // Track original result index for stable marker IDs
                const resultIdx = vssAnalysis.results.indexOf(result);
                const totalCritical = result.vssCriticalCount + result.ocsCriticalCount;
                return (
                  <details key={resultIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#0d47a1' }}>
                      {result.input.airport_name || result.input.airport_code} – RWY {result.input.runway_designation}
                      {result.input.procedure ? ` - ${result.input.procedure}` : ''}
                      {totalCritical > 0
                        ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({totalCritical} {t('critical') || 'critical'})</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                      }
                      {result.input.error && (
                        <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                      )}
                    </summary>

                    {/* Input Parameters */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#e8eaf6', padding: 8, borderRadius: 4 }}>
                      <b>{t('vssInputParams') || 'Input Parameters'}</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>Approach Type</td>
                            <td style={tdStyle}><b>{result.input.apch_type}</b></td>
                            <td style={tdStyle}>GPA (°)</td>
                            <td style={tdStyle}><b>{result.input.gpa_deg}</b></td>
                            <td style={tdStyle}>OCA</td>
                            <td style={tdStyle}><b>{result.input.oca_value_ft != null ? `${(result.input.oca_value_ft * 0.3048).toFixed(1)} m / ${result.input.oca_value_ft} ft` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>RDH</td>
                            <td style={tdStyle}><b>{result.input.rdh_ft != null ? `${(result.input.rdh_ft * 0.3048).toFixed(1)} m / ${result.input.rdh_ft} ft` : '—'}</b></td>
                            <td style={tdStyle}>OCS enabled</td>
                            <td style={tdStyle}><b>{result.input.ocs ? 'Yes' : 'No'}</b></td>
                            <td style={tdStyle}>APV BARO</td>
                            <td style={tdStyle}><b>{result.input.apvbaro ? `Yes (min VPA: ${result.input.min_vpa_deg != null ? result.input.min_vpa_deg + '°' : 'N/A'})` : 'No'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Cat F</td>
                            <td style={tdStyle}><b>{result.input.cat_f ? 'Yes' : 'No'}</b></td>
                            <td style={tdStyle}>RWY Code</td>
                            <td style={tdStyle}><b>{result.input.rwy_code}</b></td>
                            <td style={tdStyle}>RWY Width</td>
                            <td style={tdStyle}><b>{result.input.rwy_width_m != null ? `${result.input.rwy_width_m} m` : '—'}</b></td>
                          </tr>
                          {result.input.apch_type === 'NonPrecision' && (
                            <tr>
                              <td style={tdStyle}>True Inbound (°)</td>
                              <td style={tdStyle}><b>{result.input.true_inbound_deg != null ? `${parseFloat(result.input.true_inbound_deg).toFixed(2)}° (UTM grid: ${result.input.utm_grid_inbound_deg?.toFixed(2)}°)` : '—'}</b></td>
                              <td style={tdStyle}>Strip Width</td>
                              <td style={tdStyle}><b>{result.input.strip_width_m != null ? `${result.input.strip_width_m} m` : '—'}</b></td>
                            </tr>
                          )}
                          <tr>
                            <td style={tdStyle}>THR Position</td>
                            <td style={tdStyle}><b>{result.input.thr_lat != null ? `${toDMS(result.input.thr_lat, true)} / ${toDMS(result.input.thr_lon, false)}` : '—'}</b></td>
                            <td style={tdStyle}>THR Elevation</td>
                            <td style={tdStyle}><b>{result.input.thr_elev_m != null ? `${result.input.thr_elev_m.toFixed(1)} m` : '—'}</b></td>
                            <td style={tdStyle}>Inbound Track (°)</td>
                            <td style={tdStyle}><b>{result.input.inbound_track_deg != null ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (UTM grid: ${result.input.utm_grid_bearing_deg?.toFixed(2)}°)` : '—'}</b></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Geometry / surface parameters */}
                    {result.geometry && (
                      <div style={{ marginTop: 8, marginBottom: 8, fontSize: '11px', fontFamily: 'monospace', background: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                        <strong>Surface Geometry:</strong>
                        {' '}OCH = {result.geometry.ochM} m
                        {' '}| Length = {result.geometry.lengthM} m
                        {' '}| Half-width = {result.geometry.halfWidthM} m
                        {' '}| tan(VSS) = {result.geometry.tangVSS}
                        {result.geometry.tangOCS != null && (
                          <span> | tan(OCS) = {result.geometry.tangOCS}
                          {result.geometry.ocsBaseElevM != null && ` | OCS base elev = ${result.geometry.ocsBaseElevM} m`}
                          </span>
                        )}
                        {result.input.apch_type === 'NonPrecision' && (
                          <span> | Diverge L={result.geometry.leftDiverge} R={result.geometry.rightDiverge}</span>
                        )}
                      </div>
                    )}

                    {/* VSS obstacles */}
                    <div style={{ marginTop: 8, border: '1px solid #90caf9', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        VSS Surface
                        {result.vssCriticalCount > 0 && (
                          <span style={{ color: '#d32f2f', marginLeft: 8 }}>
                            ({result.vssCriticalCount} {t('critical') || 'critical'})
                          </span>
                        )}
                      </div>
                      <div style={{ marginBottom: 4, fontSize: '12px' }}>
                        <strong>Obstacles inside VSS:</strong> {result.vssRows ? result.vssRows.length : 0}
                        {' | '}<strong>Critical:</strong>{' '}
                        <span style={{ color: result.vssCriticalCount > 0 ? '#d32f2f' : 'inherit', fontWeight: result.vssCriticalCount > 0 ? 'bold' : 'normal' }}>
                          {result.vssCriticalCount || 0}
                        </span>
                      </div>
                      {result.vssRows && result.vssRows.length > 0
                        ? renderVssTable(result.vssRows, 'VSS', resultIdx)
                        : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within VSS surface.</p>
                      }
                    </div>

                    {/* OCS obstacles (only shown if ocs=true) */}
                    {result.input.ocs && (
                      <div style={{ marginTop: 8, border: '1px solid #4fc3f7', borderRadius: 4, padding: 8, background: '#f0f9ff' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                          OCS Surface
                          {result.ocsCriticalCount > 0 && (
                            <span style={{ color: '#d32f2f', marginLeft: 8 }}>
                              ({result.ocsCriticalCount} {t('critical') || 'critical'})
                            </span>
                          )}
                        </div>
                        <div style={{ marginBottom: 4, fontSize: '12px' }}>
                          <strong>Obstacles inside OCS:</strong> {result.ocsRows ? result.ocsRows.length : 0}
                          {' | '}<strong>Critical:</strong>{' '}
                          <span style={{ color: result.ocsCriticalCount > 0 ? '#d32f2f' : 'inherit', fontWeight: result.ocsCriticalCount > 0 ? 'bold' : 'normal' }}>
                            {result.ocsCriticalCount || 0}
                          </span>
                        </div>
                        {result.ocsRows && result.ocsRows.length > 0
                          ? renderVssTable(result.ocsRows, 'OCS', resultIdx)
                          : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within OCS surface.</p>
                        }
                      </div>
                    )}
                  </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {vssAnalysis.message || 'No VSS configurations found for airports in range.'}
              </p>
            )}
          </div>
        );
      })()}

      {vssError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>{t('vssError') || 'VSS Calculation Error'}:</b> {vssError}
        </div>
      )}

      {/* ── Base Turn Calculation Results ──────────────────────────────── */}
      {holdingBaseAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!holdingBaseAnalysis.results?.some(r => r.obsRows?.length > 0)) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        const sortHbRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleHbSort = (key) => setHoldingBaseSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderHbSortIndicator = (key) => (
          holdingBaseSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{holdingBaseSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          padding: 6, border: '1px solid #ffb74d', textAlign: 'left', whiteSpace: 'nowrap',
          background: holdingBaseSortConfig.key === key ? '#ffe0b2' : '#fff3e0',
          cursor: 'pointer', userSelect: 'none',
        });

        const renderHbTable = (rows, resultIdx) => {
          if (!rows || rows.length === 0) return null;
          const indexedRows = rows.map((row, origIdx) => ({ ...row, _hbId: `hb-${resultIdx}-${origIdx}` }));
          const sortedRows = sortHbRows(indexedRows, holdingBaseSortConfig.key, holdingBaseSortConfig.direction);
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '1150px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#ffe0b2' }}>
                    <th style={thSort('obstacleName')} onClick={() => handleHbSort('obstacleName')}>Obstacle Name{renderHbSortIndicator('obstacleName')}</th>
                    <th style={thSort('obstacleAltM')} onClick={() => handleHbSort('obstacleAltM')}>Alt (m){renderHbSortIndicator('obstacleAltM')}</th>
                    <th style={thSort('obstacleAltFt')} onClick={() => handleHbSort('obstacleAltFt')}>Alt (ft){renderHbSortIndicator('obstacleAltFt')}</th>
                    <th style={thSort('zone')} onClick={() => handleHbSort('zone')}>Zone{renderHbSortIndicator('zone')}</th>
                    <th style={thSort('distFromPrimaryBoundaryM')} onClick={() => handleHbSort('distFromPrimaryBoundaryM')}>Dist. from primary bdry (m){renderHbSortIndicator('distFromPrimaryBoundaryM')}</th>
                    <th style={thSort('mocM')} onClick={() => handleHbSort('mocM')}>MOC (m){renderHbSortIndicator('mocM')}</th>
                    <th style={thSort('mocFt')} onClick={() => handleHbSort('mocFt')}>MOC (ft){renderHbSortIndicator('mocFt')}</th>
                    <th style={thSort('surfaceAltM')} onClick={() => handleHbSort('surfaceAltM')}>Surf. Alt (m){renderHbSortIndicator('surfaceAltM')}</th>
                    <th style={thSort('surfaceAltFt')} onClick={() => handleHbSort('surfaceAltFt')}>Surf. Alt (ft){renderHbSortIndicator('surfaceAltFt')}</th>
                    <th style={thSort('clearanceM')} onClick={() => handleHbSort('clearanceM')}>Clearance (m){renderHbSortIndicator('clearanceM')}</th>
                    <th style={thSort('clearanceFt')} onClick={() => handleHbSort('clearanceFt')}>Clearance (ft){renderHbSortIndicator('clearanceFt')}</th>
                    <th style={thSort('isCritical')} onClick={() => handleHbSort('isCritical')}>Critical{renderHbSortIndicator('isCritical')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const id = row._hbId;
                    const isSelected = selectedHoldingBaseObstacles.has(id);
                    const isCrit = row.isCritical;
                    const isSecondary = row.zone === 'Secondary';
                    const baseBg = isCrit ? '#ffcdd2' : isSecondary ? '#fff8e1' : 'white';
                    return (
                      <tr
                        key={id}
                        onClick={(e) => {
                          setSelectedHoldingBaseObstacles(prev => {
                            const s = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (s.has(id)) s.delete(id); else s.add(id);
                            } else { s.clear(); s.add(id); }
                            return s;
                          });
                        }}
                        style={{ background: isSelected ? '#ffe0b2' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#fff3e0'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                        <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 'bold', color: isSecondary ? '#f57c00' : '#1b5e20' }}>
                          {row.zone || 'Primary'}
                        </td>
                        <td style={tdStyle}>{row.distFromPrimaryBoundaryM != null ? row.distFromPrimaryBoundaryM : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocM != null ? `${row.mocM} m` : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocFt != null ? `${row.mocFt} ft` : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt : '—'}</td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c', fontWeight: isCrit ? 'bold' : 'normal' }}>
                          {row.clearanceM != null ? row.clearanceM : '—'}
                        </td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c' }}>
                          {row.clearanceFt != null ? row.clearanceFt : '—'}
                        </td>
                        <td style={tdStyle}>
                          {isCrit
                            ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                            : <span style={{ color: '#666' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        return (
          <div style={{ marginTop: 10, background: '#fff3e0', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#e65100' }}>
                Base Turn Calculation Results
              </h3>
              <button
                onClick={() => setShowHoldingBaseSurfaces(v => !v)}
                title="Toggle Base Turn protected-area polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showHoldingBaseSurfaces ? '#e65100' : '#ffe0b2',
                  color: showHoldingBaseSurfaces ? '#fff' : '#e65100',
                  border: '1px solid #e65100', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showHoldingBaseSurfaces ? 'Hide Base Turn surfaces' : 'Show Base Turn surfaces'}
              </button>
            </div>

            {holdingBaseAnalysis.results && holdingBaseAnalysis.results.length > 0 ? (() => {
              const resultsWithObs = holdingBaseAnalysis.results.filter(r => r.obsRows && r.obsRows.length > 0);
              if (resultsWithObs.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No obstacles found within any Base Turn protected area.
                  </p>
                );
              }
              return resultsWithObs.map((result) => {
                const resultIdx = holdingBaseAnalysis.results.indexOf(result);
                return (
                  <details key={resultIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#e65100' }}>
                      {result.input.airport_name || result.input.airport_code || '—'}
                      {(result.input.nav_type || result.input.nav_ident) ? ` – ${[result.input.nav_type, result.input.nav_ident].filter(Boolean).join(' ')}` : ''}
                      {` (Base Turn)`}
                      {result.criticalCount > 0
                        ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                      }
                      {result.input.error && (
                        <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                      )}
                    </summary>

                    {/* Input parameters */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#fff8e1', padding: 8, borderRadius: 4 }}>
                      <b>Input Parameters</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>Nav Ident</td>
                            <td style={tdStyle}><b>{result.input.nav_ident || '—'}</b></td>
                            <td style={tdStyle}>Nav Type</td>
                            <td style={tdStyle}><b>{result.input.nav_type}</b></td>
                            <td style={tdStyle}>Turn Direction</td>
                            <td style={tdStyle}><b>{result.input.turn_direction}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Inbound Track (°)</td>
                            <td style={tdStyle}><b>
                              {result.input.inbound_track_deg != null
                                ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (UTM grid: ${result.input.utm_grid_inbound_deg?.toFixed(2)}°)`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}>Altitude</td>
                            <td style={tdStyle}><b>{result.input.altitude_ft != null ? `${result.input.altitude_ft} ft / ${result.geometry?.holdingAltM} m` : '—'}</b></td>
                            <td style={tdStyle}>IAS</td>
                            <td style={tdStyle}><b>{result.input.ias_kt != null ? `${result.input.ias_kt} kt` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Outbound Limit</td>
                            <td style={tdStyle}><b>
                              {result.input.outbound_limit_type === 'time'
                                ? `${result.input.outbound_time_min} min`
                                : `${result.input.outbound_dme_nm} NM (DME)`}
                            </b></td>
                            <td style={tdStyle}>Nav Fix</td>
                            <td style={tdStyle}><b>
                              {result.input.nav_lat != null
                                ? `${toDMS(result.input.nav_lat, true)} / ${toDMS(result.input.nav_lon, false)}`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}>ISA Δ</td>
                            <td style={tdStyle}><b>{result.input.isa_delta_c != null ? `${result.input.isa_delta_c}°C` : '—'}</b></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Geometry parameters */}
                    {result.geometry && (
                      <div style={{ marginTop: 4, marginBottom: 8, fontSize: '11px', fontFamily: 'monospace', background: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                        <strong>Geometry:</strong>
                        {' '}TAS = {result.geometry.tas_kt} kt
                        {result.geometry.wind_kt != null && ` | Wind = ${result.geometry.wind_kt} kt`}
                        {' '}| Turn radius = {result.geometry.turn_radius_m} m
                        {' '}| Sector angle = {result.geometry.degrees2}°
                        {result.geometry.true_outbound_deg != null && ` | True outbound = ${result.geometry.true_outbound_deg.toFixed(4)}°`}
                        {result.input.offset_entry != null && ` | Offset entry = ${result.input.offset_entry}°`}
                        {result.geometry.outerDistM != null && ` | Outer dist ≈ ${result.geometry.outerDistM} m`}
                        {result.geometry.innerDistM != null && ` | Inner dist = ${result.geometry.innerDistM} m`}
                        {' '}| Primary MOC = {result.geometry.primaryMocM} m
                        {result.geometry.secondaryMocM != null && ` | Secondary MOC = ${result.geometry.secondaryMocM} m`}
                        {' '}| Holding alt = {result.geometry.holdingAltM} m
                      </div>
                    )}

                    {/* Obstacle table */}
                    <div style={{ marginTop: 8, border: '1px solid #ffb74d', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        Obstacles inside protected area
                        {result.criticalCount > 0
                          ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                          : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                        }
                      </div>
                       <div style={{ marginBottom: 4, fontSize: '12px' }}>
                        <strong>Total:</strong> {result.obsRows.length}
                        {' | '}
                        <strong>Primary:</strong> {result.obsRows.filter(r => r.zone === 'Primary' || !r.zone).length}
                        {result.secondaryPolygon && <>
                          {' | '}
                          <strong>Secondary:</strong> {result.obsRows.filter(r => r.zone === 'Secondary').length}
                        </>}
                        {' | '}<strong>Critical:</strong>{' '}
                        <span style={{ color: result.criticalCount > 0 ? '#d32f2f' : 'inherit', fontWeight: result.criticalCount > 0 ? 'bold' : 'normal' }}>
                          {result.criticalCount}
                        </span>
                      </div>
                      {result.obsRows.length > 0
                        ? renderHbTable(result.obsRows, resultIdx)
                        : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                      }
                    </div>
                  </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {holdingBaseAnalysis.message || 'No HoldingBase configurations found within 50 km.'}
              </p>
            )}
          </div>
        );
      })()}

      {holdingBaseError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>Base Turn Calculation Error:</b> {holdingBaseError}
        </div>
      )}

      {/* ── Holding / Racetrack by Time Results ──────────────────────────── */}
      {holdingRacetrackAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!holdingRacetrackAnalysis.results?.some(r => r.input?.limiting_dme_nm == null && r.obsRows?.length > 0)) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        const sortRtRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleRtSort = (key) => setHoldingRacetrackSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderRtSortIndicator = (key) => (
          holdingRacetrackSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{holdingRacetrackSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          padding: 6, border: '1px solid #80cbc4', textAlign: 'left', whiteSpace: 'nowrap',
          background: holdingRacetrackSortConfig.key === key ? '#b2dfdb' : '#e0f2f1',
          cursor: 'pointer', userSelect: 'none',
        });

        const renderRtTable = (rows, resultIdx) => {
          if (!rows || rows.length === 0) return null;
          const indexedRows = rows.map((row, origIdx) => ({ ...row, _rtId: `rt-${resultIdx}-${origIdx}` }));
          const sortedRows = sortRtRows(indexedRows, holdingRacetrackSortConfig.key, holdingRacetrackSortConfig.direction);
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '1150px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#b2dfdb' }}>
                    <th style={thSort('obstacleName')} onClick={() => handleRtSort('obstacleName')}>Obstacle Name{renderRtSortIndicator('obstacleName')}</th>
                    <th style={thSort('obstacleAltM')} onClick={() => handleRtSort('obstacleAltM')}>Alt (m){renderRtSortIndicator('obstacleAltM')}</th>
                    <th style={thSort('obstacleAltFt')} onClick={() => handleRtSort('obstacleAltFt')}>Alt (ft){renderRtSortIndicator('obstacleAltFt')}</th>
                    <th style={thSort('zone')} onClick={() => handleRtSort('zone')}>Zone{renderRtSortIndicator('zone')}</th>
                    <th style={thSort('distFromPrimaryBoundaryM')} onClick={() => handleRtSort('distFromPrimaryBoundaryM')}>Dist. from primary bdry (m){renderRtSortIndicator('distFromPrimaryBoundaryM')}</th>
                    <th style={thSort('mocM')} onClick={() => handleRtSort('mocM')}>MOC (m){renderRtSortIndicator('mocM')}</th>
                    <th style={thSort('mocFt')} onClick={() => handleRtSort('mocFt')}>MOC (ft){renderRtSortIndicator('mocFt')}</th>
                    <th style={thSort('surfaceAltM')} onClick={() => handleRtSort('surfaceAltM')}>Surf. Alt (m){renderRtSortIndicator('surfaceAltM')}</th>
                    <th style={thSort('surfaceAltFt')} onClick={() => handleRtSort('surfaceAltFt')}>Surf. Alt (ft){renderRtSortIndicator('surfaceAltFt')}</th>
                    <th style={thSort('clearanceM')} onClick={() => handleRtSort('clearanceM')}>Clearance (m){renderRtSortIndicator('clearanceM')}</th>
                    <th style={thSort('clearanceFt')} onClick={() => handleRtSort('clearanceFt')}>Clearance (ft){renderRtSortIndicator('clearanceFt')}</th>
                    <th style={thSort('isCritical')} onClick={() => handleRtSort('isCritical')}>Critical{renderRtSortIndicator('isCritical')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const id = row._rtId;
                    const isSelected = selectedHoldingRacetrackObstacles.has(id);
                    const isCrit = row.isCritical;
                    const isSecondary = row.zone === 'Secondary';
                    const baseBg = isCrit ? '#fce4ec' : isSecondary ? '#e0f2f1' : 'white';
                    return (
                      <tr
                        key={id}
                        onClick={(e) => {
                          setSelectedHoldingRacetrackObstacles(prev => {
                            const s = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (s.has(id)) s.delete(id); else s.add(id);
                            } else { s.clear(); s.add(id); }
                            return s;
                          });
                        }}
                        style={{ background: isSelected ? '#b2dfdb' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#e0f2f1'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                        <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 'bold', color: isSecondary ? '#00695c' : '#1b5e20' }}>
                          {row.zone || 'Primary'}
                        </td>
                        <td style={tdStyle}>{row.distFromPrimaryBoundaryM != null ? row.distFromPrimaryBoundaryM : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocM != null ? `${row.mocM} m` : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocFt != null ? `${row.mocFt} ft` : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt : '—'}</td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c', fontWeight: isCrit ? 'bold' : 'normal' }}>
                          {row.clearanceM != null ? row.clearanceM : '—'}
                        </td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c' }}>
                          {row.clearanceFt != null ? row.clearanceFt : '—'}
                        </td>
                        <td style={tdStyle}>
                          {isCrit
                            ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                            : <span style={{ color: '#666' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        return (
          <div style={{ marginTop: 10, background: '#e0f2f1', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#00695c' }}>
                Holding / Racetrack by Time Results
              </h3>
              <button
                onClick={() => setShowHoldingRacetrackSurfaces(v => !v)}
                title="Toggle Holding/Racetrack protected-area polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showHoldingRacetrackSurfaces ? '#00695c' : '#b2dfdb',
                  color: showHoldingRacetrackSurfaces ? '#fff' : '#00695c',
                  border: '1px solid #00695c', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showHoldingRacetrackSurfaces ? 'Hide Holding/Racetrack surfaces' : 'Show Holding/Racetrack surfaces'}
              </button>
            </div>

            {holdingRacetrackAnalysis.results && holdingRacetrackAnalysis.results.length > 0 ? (() => {
              const byTimeResults = holdingRacetrackAnalysis.results.filter(r => r.input.limiting_dme_nm == null);
              const resultsWithObs = byTimeResults.filter(r => r.obsRows && r.obsRows.length > 0);
              if (resultsWithObs.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No obstacles found within any Holding/Racetrack (by Time) protected area.
                  </p>
                );
              }
              return resultsWithObs.map((result) => {
                const resultIdx = holdingRacetrackAnalysis.results.indexOf(result);
                const usedFor = result.input.used_for_type || 'Holding';
                return (
                  <details key={resultIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#00695c' }}>
                      {result.input.airport_name || result.input.airport_code || '—'}
                      {(result.input.nav_type || result.input.nav_ident) ? ` – ${[result.input.nav_type, result.input.nav_ident].filter(Boolean).join(' ')}` : ''}
                      {` (${usedFor})`}
                      {result.criticalCount > 0
                        ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                      }
                      {result.input.error && (
                        <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                      )}
                    </summary>

                    {/* Input parameters */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#e8f5e9', padding: 8, borderRadius: 4 }}>
                      <b>Input Parameters</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>Used for</td>
                            <td style={tdStyle}><b>{usedFor}</b></td>
                            <td style={tdStyle}>Nav Ident</td>
                            <td style={tdStyle}><b>{result.input.nav_ident || '—'}</b></td>
                            <td style={tdStyle}>Nav Type</td>
                            <td style={tdStyle}><b>{result.input.nav_type}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Turn Direction</td>
                            <td style={tdStyle}><b>{result.input.turn_direction}</b></td>
                            <td style={tdStyle}>Inbound Track (°)</td>
                            <td style={tdStyle}><b>
                              {result.input.inbound_track_deg != null
                                ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (grid: ${result.input.utm_grid_inbound_deg?.toFixed(2)}°)`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}>Altitude</td>
                            <td style={tdStyle}><b>{result.input.altitude_ft != null ? `${result.input.altitude_ft} ft / ${result.geometry?.holdingAltM} m` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>IAS</td>
                            <td style={tdStyle}><b>{result.input.ias_kt != null ? `${result.input.ias_kt} kt` : '—'}</b></td>
                            <td style={tdStyle}>Outbound Time</td>
                            <td style={tdStyle}><b>{result.input.outbound_time_min != null ? `${result.input.outbound_time_min} min` : '—'}</b></td>
                            <td style={tdStyle}>Nav Fix</td>
                            <td style={tdStyle}><b>
                              {result.input.nav_lat != null
                                ? `${toDMS(result.input.nav_lat, true)} / ${toDMS(result.input.nav_lon, false)}`
                                : '—'}
                            </b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>ISA Δ</td>
                            <td style={tdStyle}><b>{result.input.isa_delta_c != null ? `${result.input.isa_delta_c}°C` : '—'}</b></td>
                            <td style={tdStyle}>Category H</td>
                            <td style={tdStyle}><b>{result.input.use_category_h ? 'Yes' : 'No'}</b></td>
                            <td style={tdStyle}>Tolerance Type</td>
                            <td style={tdStyle}><b>
                              {result.input.tolerance_type === 'facility'
                                ? 'Over-head a Facility'
                                : result.input.tolerance_type === 'custom'
                                  ? 'Fix'
                                  : result.input.tolerance_type === 'fix'
                                    ? 'Fix'
                                    : result.input.tolerance_type || '—'}
                            </b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>NAV Elevation</td>
                            <td style={tdStyle}><b>
                              {result.input.nav_elev_ft != null
                                ? `${(result.input.nav_elev_ft * 0.3048).toFixed(1)} m / ${result.input.nav_elev_ft} ft`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}></td><td style={tdStyle}></td>
                            <td style={tdStyle}></td><td style={tdStyle}></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Geometry parameters */}
                    {result.geometry && (
                      <div style={{ marginTop: 4, marginBottom: 8, fontSize: '11px', fontFamily: 'monospace', background: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                        <strong>Geometry:</strong>
                        {' '}TAS = {result.geometry.tas_kt} kt
                        {result.geometry.wind_kt != null && ` | Wind = ${result.geometry.wind_kt} kt`}
                        {' '}| Turn radius = {result.geometry.turn_radius_m} m
                        {result.geometry.nominalDistM != null && ` | Nominal leg = ${result.geometry.nominalDistM} m`}
                        {result.geometry.outerDistM != null && ` | Outer leg = ${result.geometry.outerDistM} m`}
                        {result.geometry.innerDistM != null && ` | Inner leg = ${result.geometry.innerDistM} m`}
                        {' '}| Primary MOC = {result.geometry.primaryMocM} m
                        {result.geometry.secondaryMocM != null && ` | Secondary MOC = ${result.geometry.secondaryMocM} m`}
                        {' '}| Holding alt = {result.geometry.holdingAltM} m
                      </div>
                    )}

                    {/* Obstacle table */}
                    <div style={{ marginTop: 8, border: '1px solid #80cbc4', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        Obstacles inside protected area
                        {result.criticalCount > 0
                          ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                          : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                        }
                      </div>
                      <div style={{ marginBottom: 4, fontSize: '12px' }}>
                        <strong>Total:</strong> {result.obsRows.length}
                        {' | '}
                        <strong>Primary:</strong> {result.obsRows.filter(r => r.zone === 'Primary' || !r.zone).length}
                        {result.secondaryPolygon && <>
                          {' | '}
                          <strong>Secondary:</strong> {result.obsRows.filter(r => r.zone === 'Secondary').length}
                        </>}
                        {' | '}<strong>Critical:</strong>{' '}
                        <span style={{ color: result.criticalCount > 0 ? '#d32f2f' : 'inherit', fontWeight: result.criticalCount > 0 ? 'bold' : 'normal' }}>
                          {result.criticalCount}
                        </span>
                      </div>
                      {result.obsRows.length > 0
                        ? renderRtTable(result.obsRows, resultIdx)
                        : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                      }
                    </div>
                  </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {holdingRacetrackAnalysis.message || 'No Holding/Racetrack configurations found within 50 km.'}
              </p>
            )}
          </div>
        );
      })()}

      {holdingRacetrackError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>Holding/Racetrack Calculation Error:</b> {holdingRacetrackError}
        </div>
      )}

      {/* ── Holding / Racetrack by Distance Results ──────────────────────────── */}
      {holdingRacetrackAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        const byDistResults = (holdingRacetrackAnalysis.results || []).filter(r => r.input.limiting_dme_nm != null);
        if (!byDistResults.some(r => r.obsRows?.length > 0)) return null;

        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        const sortDistRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleDistSort = (key) => setHoldingDistSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderDistSortIndicator = (key) => (
          holdingDistSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{holdingDistSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          padding: 6, border: '1px solid #80cbc4', textAlign: 'left', whiteSpace: 'nowrap',
          background: holdingDistSortConfig.key === key ? '#b2dfdb' : '#e0f2f1',
          cursor: 'pointer', userSelect: 'none',
        });

        const renderDistTable = (rows, resultIdx) => {
          if (!rows || rows.length === 0) return null;
          const indexedRows = rows.map((row, origIdx) => ({ ...row, _rdId: `rd-${resultIdx}-${origIdx}` }));
          const sortedRows = sortDistRows(indexedRows, holdingDistSortConfig.key, holdingDistSortConfig.direction);
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '1150px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#b2dfdb' }}>
                    <th style={thSort('obstacleName')} onClick={() => handleDistSort('obstacleName')}>Obstacle Name{renderDistSortIndicator('obstacleName')}</th>
                    <th style={thSort('obstacleAltM')} onClick={() => handleDistSort('obstacleAltM')}>Alt (m){renderDistSortIndicator('obstacleAltM')}</th>
                    <th style={thSort('obstacleAltFt')} onClick={() => handleDistSort('obstacleAltFt')}>Alt (ft){renderDistSortIndicator('obstacleAltFt')}</th>
                    <th style={thSort('zone')} onClick={() => handleDistSort('zone')}>Zone{renderDistSortIndicator('zone')}</th>
                    <th style={thSort('distFromPrimaryBoundaryM')} onClick={() => handleDistSort('distFromPrimaryBoundaryM')}>Dist. from primary bdry (m){renderDistSortIndicator('distFromPrimaryBoundaryM')}</th>
                    <th style={thSort('mocM')} onClick={() => handleDistSort('mocM')}>MOC (m){renderDistSortIndicator('mocM')}</th>
                    <th style={thSort('mocFt')} onClick={() => handleDistSort('mocFt')}>MOC (ft){renderDistSortIndicator('mocFt')}</th>
                    <th style={thSort('surfaceAltM')} onClick={() => handleDistSort('surfaceAltM')}>Surf. Alt (m){renderDistSortIndicator('surfaceAltM')}</th>
                    <th style={thSort('surfaceAltFt')} onClick={() => handleDistSort('surfaceAltFt')}>Surf. Alt (ft){renderDistSortIndicator('surfaceAltFt')}</th>
                    <th style={thSort('clearanceM')} onClick={() => handleDistSort('clearanceM')}>Clearance (m){renderDistSortIndicator('clearanceM')}</th>
                    <th style={thSort('clearanceFt')} onClick={() => handleDistSort('clearanceFt')}>Clearance (ft){renderDistSortIndicator('clearanceFt')}</th>
                    <th style={thSort('isCritical')} onClick={() => handleDistSort('isCritical')}>Critical{renderDistSortIndicator('isCritical')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const id = row._rdId;
                    const isSelected = selectedHoldingDistObstacles.has(id);
                    const isCrit = row.isCritical;
                    const isSecondary = row.zone === 'Secondary';
                    const baseBg = isCrit ? '#fce4ec' : isSecondary ? '#e0f2f1' : 'white';
                    return (
                      <tr
                        key={id}
                        onClick={(e) => {
                          setSelectedHoldingDistObstacles(prev => {
                            const s = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (s.has(id)) s.delete(id); else s.add(id);
                            } else { s.clear(); s.add(id); }
                            return s;
                          });
                        }}
                        style={{ background: isSelected ? '#b2dfdb' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#e0f2f1'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                        <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 'bold', color: isSecondary ? '#00695c' : '#1b5e20' }}>
                          {row.zone || 'Primary'}
                        </td>
                        <td style={tdStyle}>{row.distFromPrimaryBoundaryM != null ? row.distFromPrimaryBoundaryM : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocM != null ? `${row.mocM} m` : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocFt != null ? `${row.mocFt} ft` : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt : '—'}</td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c', fontWeight: isCrit ? 'bold' : 'normal' }}>
                          {row.clearanceM != null ? row.clearanceM : '—'}
                        </td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c' }}>
                          {row.clearanceFt != null ? row.clearanceFt : '—'}
                        </td>
                        <td style={tdStyle}>
                          {isCrit
                            ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                            : <span style={{ color: '#666' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        const resultsWithObs = byDistResults.filter(r => r.obsRows && r.obsRows.length > 0);

        return (
          <div style={{ marginTop: 10, background: '#e0f2f1', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#00695c' }}>
                Holding / Racetrack by Distance Results
              </h3>
              <button
                onClick={() => setShowHoldingDistSurfaces(v => !v)}
                title="Toggle Holding/Racetrack by Distance protected-area polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showHoldingDistSurfaces ? '#00695c' : '#b2dfdb',
                  color: showHoldingDistSurfaces ? '#fff' : '#00695c',
                  border: '1px solid #00695c', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showHoldingDistSurfaces ? 'Hide Holding/Racetrack by Distance surfaces' : 'Show Holding/Racetrack by Distance surfaces'}
              </button>
            </div>

            {resultsWithObs.length === 0 ? (
              <p style={{ color: '#666' }}>
                No obstacles found within any Holding/Racetrack (by Distance) protected area.
              </p>
            ) : (
              resultsWithObs.map((result) => {
                const resultIdx = holdingRacetrackAnalysis.results.indexOf(result);
                const usedFor = result.input.used_for_type || 'Holding';
                return (
                  <details key={resultIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#00695c' }}>
                      {result.input.airport_name || result.input.airport_code || '—'}
                      {(result.input.nav_type || result.input.nav_ident) ? ` – ${[result.input.nav_type, result.input.nav_ident].filter(Boolean).join(' ')}` : ''}
                      {` (${usedFor})`}
                      {result.criticalCount > 0
                        ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                      }
                      {result.input.error && (
                        <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                      )}
                    </summary>

                    {/* Input parameters */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#e8f5e9', padding: 8, borderRadius: 4 }}>
                      <b>Input Parameters</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>Used for</td>
                            <td style={tdStyle}><b>{usedFor}</b></td>
                            <td style={tdStyle}>Nav Ident</td>
                            <td style={tdStyle}><b>{result.input.nav_ident || '—'}</b></td>
                            <td style={tdStyle}>Nav Type</td>
                            <td style={tdStyle}><b>{result.input.nav_type}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Turn Direction</td>
                            <td style={tdStyle}><b>{result.input.turn_direction}</b></td>
                            <td style={tdStyle}>Inbound Track (°)</td>
                            <td style={tdStyle}><b>
                              {result.input.inbound_track_deg != null
                                ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (grid: ${result.input.utm_grid_inbound_deg?.toFixed(2)}°)`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}>Altitude</td>
                            <td style={tdStyle}><b>{result.input.altitude_ft != null ? `${result.input.altitude_ft} ft / ${result.geometry?.holdingAltM} m` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>IAS</td>
                            <td style={tdStyle}><b>{result.input.ias_kt != null ? `${result.input.ias_kt} kt` : '—'}</b></td>
                            <td style={tdStyle}>Outbound Distance</td>
                            <td style={tdStyle}><b>{result.input.limiting_dme_nm != null ? `${result.input.limiting_dme_nm} NM` : '—'}</b></td>
                            <td style={tdStyle}>Nav Fix</td>
                            <td style={tdStyle}><b>
                              {result.input.nav_lat != null
                                ? `${toDMS(result.input.nav_lat, true)} / ${toDMS(result.input.nav_lon, false)}`
                                : '—'}
                            </b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Limiting Nav Ident</td>
                            <td style={tdStyle}><b>{result.input.limiting_nav_ident || '—'}</b></td>
                            <td style={tdStyle}>Limiting Nav Position</td>
                            <td style={tdStyle}><b>
                              {result.input.limiting_nav_lat != null
                                ? `${toDMS(result.input.limiting_nav_lat, true)} / ${toDMS(result.input.limiting_nav_lon, false)}`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}>Eff. Outbound Dist</td>
                            <td style={tdStyle}><b>{result.input.dme_effective_dist_m != null ? `${result.input.dme_effective_dist_m} m` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>ISA Δ</td>
                            <td style={tdStyle}><b>{result.input.isa_delta_c != null ? `${result.input.isa_delta_c}°C` : '—'}</b></td>
                            <td style={tdStyle}>Category H</td>
                            <td style={tdStyle}><b>{result.input.use_category_h ? 'Yes' : 'No'}</b></td>
                            <td style={tdStyle}>Tolerance Type</td>
                            <td style={tdStyle}><b>
                              {result.input.tolerance_type === 'facility'
                                ? 'Over-head a Facility'
                                : result.input.tolerance_type === 'custom'
                                  ? 'Fix'
                                  : result.input.tolerance_type === 'fix'
                                    ? 'Fix'
                                    : result.input.tolerance_type || '—'}
                            </b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>NAV Elevation</td>
                            <td style={tdStyle}><b>
                              {result.input.nav_elev_ft != null
                                ? `${(result.input.nav_elev_ft * 0.3048).toFixed(1)} m / ${result.input.nav_elev_ft} ft`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}></td><td style={tdStyle}></td>
                            <td style={tdStyle}></td><td style={tdStyle}></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Geometry parameters */}
                    {result.geometry && (
                      <div style={{ marginTop: 4, marginBottom: 8, fontSize: '11px', fontFamily: 'monospace', background: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                        <strong>Geometry:</strong>
                        {' '}TAS = {result.geometry.tas_kt} kt
                        {result.geometry.wind_kt != null && ` | Wind = ${result.geometry.wind_kt} kt`}
                        {' '}| Turn radius = {result.geometry.turn_radius_m} m
                        {result.geometry.nominalDistM != null && ` | Nominal leg = ${result.geometry.nominalDistM} m`}
                        {result.geometry.outerDistM != null && ` | Outer leg = ${result.geometry.outerDistM} m`}
                        {result.geometry.innerDistM != null && ` | Inner leg = ${result.geometry.innerDistM} m`}
                        {' '}| Primary MOC = {result.geometry.primaryMocM} m
                        {result.geometry.secondaryMocM != null && ` | Secondary MOC = ${result.geometry.secondaryMocM} m`}
                        {' '}| Holding alt = {result.geometry.holdingAltM} m
                      </div>
                    )}

                    {/* Obstacle table */}
                    <div style={{ marginTop: 8, border: '1px solid #80cbc4', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        Obstacles inside protected area
                        {result.criticalCount > 0
                          ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                          : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                        }
                      </div>
                      <div style={{ marginBottom: 4, fontSize: '12px' }}>
                        <strong>Total:</strong> {result.obsRows.length}
                        {' | '}
                        <strong>Primary:</strong> {result.obsRows.filter(r => r.zone === 'Primary' || !r.zone).length}
                        {result.secondaryPolygon && <>
                          {' | '}
                          <strong>Secondary:</strong> {result.obsRows.filter(r => r.zone === 'Secondary').length}
                        </>}
                        {' | '}<strong>Critical:</strong>{' '}
                        <span style={{ color: result.criticalCount > 0 ? '#d32f2f' : 'inherit', fontWeight: result.criticalCount > 0 ? 'bold' : 'normal' }}>
                          {result.criticalCount}
                        </span>
                      </div>
                      {result.obsRows.length > 0
                        ? renderDistTable(result.obsRows, resultIdx)
                        : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                      }
                    </div>
                  </details>
                );
              })
            )}
          </div>
        );
      })()}

      {/* ── VOR/DME Holding (by Distance) Results ────────────────────────────── */}
      {vorDmeHoldingAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!vorDmeHoldingAnalysis.results?.some(r => r.obsRows?.length > 0)) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        const sortVorDmeRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleVorDmeSort = (key) => setVorDmeHoldingSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderVorDmeSortIndicator = (key) => (
          vorDmeHoldingSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{vorDmeHoldingSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          padding: 6, border: '1px solid #80cbc4', textAlign: 'left', whiteSpace: 'nowrap',
          background: vorDmeHoldingSortConfig.key === key ? '#b2dfdb' : '#e0f2f1',
          cursor: 'pointer', userSelect: 'none',
        });

        const renderVorDmeTable = (rows, resultIdx) => {
          if (!rows || rows.length === 0) return null;
          const indexedRows = rows.map((row, origIdx) => ({ ...row, _vorDmeId: `vordme-${resultIdx}-${origIdx}` }));
          const sortedRows = sortVorDmeRows(indexedRows, vorDmeHoldingSortConfig.key, vorDmeHoldingSortConfig.direction);
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '1150px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#b2dfdb' }}>
                    <th style={thSort('obstacleName')} onClick={() => handleVorDmeSort('obstacleName')}>Obstacle Name{renderVorDmeSortIndicator('obstacleName')}</th>
                    <th style={thSort('obstacleAltM')} onClick={() => handleVorDmeSort('obstacleAltM')}>Alt (m){renderVorDmeSortIndicator('obstacleAltM')}</th>
                    <th style={thSort('obstacleAltFt')} onClick={() => handleVorDmeSort('obstacleAltFt')}>Alt (ft){renderVorDmeSortIndicator('obstacleAltFt')}</th>
                    <th style={thSort('zone')} onClick={() => handleVorDmeSort('zone')}>Zone{renderVorDmeSortIndicator('zone')}</th>
                    <th style={thSort('distFromPrimaryBoundaryM')} onClick={() => handleVorDmeSort('distFromPrimaryBoundaryM')}>Dist. from primary bdry (m){renderVorDmeSortIndicator('distFromPrimaryBoundaryM')}</th>
                    <th style={thSort('mocM')} onClick={() => handleVorDmeSort('mocM')}>MOC (m){renderVorDmeSortIndicator('mocM')}</th>
                    <th style={thSort('mocFt')} onClick={() => handleVorDmeSort('mocFt')}>MOC (ft){renderVorDmeSortIndicator('mocFt')}</th>
                    <th style={thSort('surfaceAltM')} onClick={() => handleVorDmeSort('surfaceAltM')}>Surf. Alt (m){renderVorDmeSortIndicator('surfaceAltM')}</th>
                    <th style={thSort('surfaceAltFt')} onClick={() => handleVorDmeSort('surfaceAltFt')}>Surf. Alt (ft){renderVorDmeSortIndicator('surfaceAltFt')}</th>
                    <th style={thSort('clearanceM')} onClick={() => handleVorDmeSort('clearanceM')}>Clearance (m){renderVorDmeSortIndicator('clearanceM')}</th>
                    <th style={thSort('clearanceFt')} onClick={() => handleVorDmeSort('clearanceFt')}>Clearance (ft){renderVorDmeSortIndicator('clearanceFt')}</th>
                    <th style={thSort('isCritical')} onClick={() => handleVorDmeSort('isCritical')}>Critical{renderVorDmeSortIndicator('isCritical')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const id = row._vorDmeId;
                    const isSelected = selectedVorDmeHoldingObstacles.has(id);
                    const isCrit = row.isCritical;
                    const isSecondary = row.zone === 'Secondary';
                    const baseBg = isCrit ? '#fce4ec' : isSecondary ? '#e0f2f1' : 'white';
                    return (
                      <tr
                        key={id}
                        onClick={(e) => {
                          setSelectedVorDmeHoldingObstacles(prev => {
                            const s = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (s.has(id)) s.delete(id); else s.add(id);
                            } else { s.clear(); s.add(id); }
                            return s;
                          });
                        }}
                        style={{ background: isSelected ? '#b2dfdb' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#e0f2f1'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                        <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 'bold', color: isSecondary ? '#00695c' : '#1b5e20' }}>
                          {row.zone || 'Primary'}
                        </td>
                        <td style={tdStyle}>{row.distFromPrimaryBoundaryM != null ? row.distFromPrimaryBoundaryM : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocM != null ? `${row.mocM} m` : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocFt != null ? `${row.mocFt} ft` : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt : '—'}</td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c', fontWeight: isCrit ? 'bold' : 'normal' }}>
                          {row.clearanceM != null ? row.clearanceM : '—'}
                        </td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c' }}>
                          {row.clearanceFt != null ? row.clearanceFt : '—'}
                        </td>
                        <td style={tdStyle}>
                          {isCrit
                            ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                            : <span style={{ color: '#666' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        return (
          <div style={{ marginTop: 10, background: '#e0f2f1', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#00695c' }}>
                VOR/DME Holding (by Distance) Results
              </h3>
              <button
                onClick={() => setShowVorDmeHoldingSurfaces(v => !v)}
                title="Toggle VOR/DME Holding protected-area polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showVorDmeHoldingSurfaces ? '#00695c' : '#b2dfdb',
                  color: showVorDmeHoldingSurfaces ? '#fff' : '#00695c',
                  border: '1px solid #00695c', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showVorDmeHoldingSurfaces ? 'Hide VOR/DME Holding surfaces' : 'Show VOR/DME Holding surfaces'}
              </button>
            </div>

            {vorDmeHoldingAnalysis.results && vorDmeHoldingAnalysis.results.length > 0 ? (() => {
              const resultsWithObs = vorDmeHoldingAnalysis.results.filter(r => r.obsRows && r.obsRows.length > 0);
              if (resultsWithObs.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No obstacles found within any VOR/DME Holding protected area.
                  </p>
                );
              }
              return resultsWithObs.map((result) => {
                const resultIdx = vorDmeHoldingAnalysis.results.indexOf(result);
                const usedFor = result.input.used_for_type || 'Holding';
                const holdingType = result.input.holding_type || 'towards';
                return (
                  <details key={resultIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#00695c' }}>
                      {result.input.airport_name || result.input.airport_code || '—'}
                      {result.input.nav_ident ? ` – ${result.input.nav_ident}` : ''}
                      {` (${usedFor} – ${holdingType})`}
                      {result.criticalCount > 0
                        ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                      }
                      {result.input.error && (
                        <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                      )}
                    </summary>

                    {/* Input parameters */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#e8f5e9', padding: 8, borderRadius: 4 }}>
                      <b>Input Parameters</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>Used for</td>
                            <td style={tdStyle}><b>{usedFor}</b></td>
                            <td style={tdStyle}>Holding Type</td>
                            <td style={tdStyle}><b>{holdingType}</b></td>
                            <td style={tdStyle}>Turn Direction</td>
                            <td style={tdStyle}><b>{result.input.turn_direction || '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>NAV Ident</td>
                            <td style={tdStyle}><b>{result.input.nav_ident || '—'}</b></td>
                            <td style={tdStyle}>Inbound Track (°)</td>
                            <td style={tdStyle}><b>
                              {result.input.inbound_track_deg != null
                                ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}°`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}>Altitude</td>
                            <td style={tdStyle}><b>{result.input.altitude_ft != null ? `${result.input.altitude_ft} ft` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>IAS</td>
                            <td style={tdStyle}><b>{result.input.ias_kt != null ? `${result.input.ias_kt} kt` : '—'}</b></td>
                            <td style={tdStyle}>Holding DME</td>
                            <td style={tdStyle}><b>{result.input.holding_dme_nm != null ? `${result.input.holding_dme_nm} NM` : '—'}</b></td>
                            <td style={tdStyle}>Limiting DME</td>
                            <td style={tdStyle}><b>{result.input.limiting_dme_nm != null ? `${result.input.limiting_dme_nm} NM` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Wind Type</td>
                            <td style={tdStyle}><b>{result.input.wind_type || 'ICAO'}</b></td>
                            <td style={tdStyle}>ISA Δ</td>
                            <td style={tdStyle}><b>{result.input.isa_delta_c != null ? `${result.input.isa_delta_c}°C` : '—'}</b></td>
                            <td style={tdStyle}>MOC</td>
                            <td style={tdStyle}><b>{result.input.moc_m != null ? `${result.input.moc_m} m` : '—'}</b></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Geometry parameters */}
                    {result.geometry && (
                      <div style={{ marginTop: 4, marginBottom: 8, fontSize: '11px', fontFamily: 'monospace', background: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                        <strong>Geometry:</strong>
                        {' '}TAS = {result.geometry.tas_kt} kt
                        {result.geometry.wind_kt != null && ` | Wind = ${result.geometry.wind_kt} kt`}
                        {' '}| Turn radius = {result.geometry.turn_radius_m} m
                        {result.geometry.holdingDistM != null && ` | Holding fix dist = ${result.geometry.holdingDistM} m`}
                        {result.geometry.limitingDistM != null && ` | Limiting dist = ${result.geometry.limitingDistM} m`}
                        {' '}| Primary MOC = {result.geometry.primaryMocM} m
                        {result.geometry.holdingAltM != null && ` | Holding alt = ${result.geometry.holdingAltM} m`}
                      </div>
                    )}

                    {/* Obstacle table */}
                    <div style={{ marginTop: 8, border: '1px solid #80cbc4', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        Obstacles inside protected area
                        {result.criticalCount > 0
                          ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                          : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                        }
                      </div>
                      <div style={{ marginBottom: 4, fontSize: '12px' }}>
                        <strong>Total:</strong> {result.obsRows.length}
                        {' | '}
                        <strong>Primary:</strong> {result.obsRows.filter(r => r.zone === 'Primary' || !r.zone).length}
                        {result.secondaryPolygon && <>
                          {' | '}
                          <strong>Secondary:</strong> {result.obsRows.filter(r => r.zone === 'Secondary').length}
                        </>}
                        {' | '}<strong>Critical:</strong>{' '}
                        <span style={{ color: result.criticalCount > 0 ? '#d32f2f' : 'inherit', fontWeight: result.criticalCount > 0 ? 'bold' : 'normal' }}>
                          {result.criticalCount}
                        </span>
                      </div>
                      {result.obsRows.length > 0
                        ? renderVorDmeTable(result.obsRows, resultIdx)
                        : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                      }
                    </div>
                  </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {vorDmeHoldingAnalysis.message || 'No VOR/DME Holding configurations found within 50 km.'}
              </p>
            )}
          </div>
        );
      })()}

      {vorDmeHoldingError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>VOR/DME Holding Calculation Error:</b> {vorDmeHoldingError}
        </div>
      )}

      {/* ── RNAV Holding Results ─────────────────────────────────────────── */}
      {rnavHoldingAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!rnavHoldingAnalysis.results?.some(r => r.obsRows?.length > 0)) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        const sortRnavRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleRnavSort = (key) => setRnavHoldingSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderRnavSortIndicator = (key) => (
          rnavHoldingSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{rnavHoldingSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          padding: 6, border: '1px solid #80cbc4', textAlign: 'left', whiteSpace: 'nowrap',
          background: rnavHoldingSortConfig.key === key ? '#b2dfdb' : '#e0f2f1',
          cursor: 'pointer', userSelect: 'none',
        });

        const renderRnavTable = (rows, resultIdx) => {
          if (!rows || rows.length === 0) return null;
          const indexedRows = rows.map((row, origIdx) => ({ ...row, _rnavId: `rnav-${resultIdx}-${origIdx}` }));
          const sortedRows = sortRnavRows(indexedRows, rnavHoldingSortConfig.key, rnavHoldingSortConfig.direction);
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '1150px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#b2dfdb' }}>
                    <th style={thSort('obstacleName')} onClick={() => handleRnavSort('obstacleName')}>Obstacle Name{renderRnavSortIndicator('obstacleName')}</th>
                    <th style={thSort('obstacleAltM')} onClick={() => handleRnavSort('obstacleAltM')}>Alt (m){renderRnavSortIndicator('obstacleAltM')}</th>
                    <th style={thSort('obstacleAltFt')} onClick={() => handleRnavSort('obstacleAltFt')}>Alt (ft){renderRnavSortIndicator('obstacleAltFt')}</th>
                    <th style={thSort('zone')} onClick={() => handleRnavSort('zone')}>Zone{renderRnavSortIndicator('zone')}</th>
                    <th style={thSort('distFromPrimaryBoundaryM')} onClick={() => handleRnavSort('distFromPrimaryBoundaryM')}>Dist. from primary bdry (m){renderRnavSortIndicator('distFromPrimaryBoundaryM')}</th>
                    <th style={thSort('mocM')} onClick={() => handleRnavSort('mocM')}>MOC (m){renderRnavSortIndicator('mocM')}</th>
                    <th style={thSort('mocFt')} onClick={() => handleRnavSort('mocFt')}>MOC (ft){renderRnavSortIndicator('mocFt')}</th>
                    <th style={thSort('surfaceAltM')} onClick={() => handleRnavSort('surfaceAltM')}>Surf. Alt (m){renderRnavSortIndicator('surfaceAltM')}</th>
                    <th style={thSort('surfaceAltFt')} onClick={() => handleRnavSort('surfaceAltFt')}>Surf. Alt (ft){renderRnavSortIndicator('surfaceAltFt')}</th>
                    <th style={thSort('clearanceM')} onClick={() => handleRnavSort('clearanceM')}>Clearance (m){renderRnavSortIndicator('clearanceM')}</th>
                    <th style={thSort('clearanceFt')} onClick={() => handleRnavSort('clearanceFt')}>Clearance (ft){renderRnavSortIndicator('clearanceFt')}</th>
                    <th style={thSort('isCritical')} onClick={() => handleRnavSort('isCritical')}>Critical{renderRnavSortIndicator('isCritical')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const id = row._rnavId;
                    const isSelected = selectedRnavHoldingObstacles.has(id);
                    const isCrit = row.isCritical;
                    const isSecondary = row.zone === 'Secondary';
                    const baseBg = isCrit ? '#fce4ec' : isSecondary ? '#e0f2f1' : 'white';
                    return (
                      <tr
                        key={id}
                        onClick={(e) => {
                          setSelectedRnavHoldingObstacles(prev => {
                            const s = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (s.has(id)) s.delete(id); else s.add(id);
                            } else { s.clear(); s.add(id); }
                            return s;
                          });
                        }}
                        style={{ background: isSelected ? '#b2dfdb' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#e0f2f1'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                        <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 'bold', color: isSecondary ? '#00695c' : '#1b5e20' }}>
                          {row.zone || 'Primary'}
                        </td>
                        <td style={tdStyle}>{row.distFromPrimaryBoundaryM != null ? row.distFromPrimaryBoundaryM : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocM != null ? `${row.mocM} m` : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocFt != null ? `${row.mocFt} ft` : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt : '—'}</td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c', fontWeight: isCrit ? 'bold' : 'normal' }}>
                          {row.clearanceM != null ? row.clearanceM : '—'}
                        </td>
                        <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c' }}>
                          {row.clearanceFt != null ? row.clearanceFt : '—'}
                        </td>
                        <td style={tdStyle}>
                          {isCrit
                            ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                            : <span style={{ color: '#666' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        return (
          <div style={{ marginTop: 10, background: '#e0f2f1', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#00695c' }}>
                RNAV Holding Results
              </h3>
              <button
                onClick={() => setShowRnavHoldingSurfaces(v => !v)}
                title="Toggle RNAV Holding protected-area polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showRnavHoldingSurfaces ? '#00695c' : '#b2dfdb',
                  color: showRnavHoldingSurfaces ? '#fff' : '#00695c',
                  border: '1px solid #00695c', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showRnavHoldingSurfaces ? 'Hide RNAV Holding surfaces' : 'Show RNAV Holding surfaces'}
              </button>
            </div>

            {rnavHoldingAnalysis.results && rnavHoldingAnalysis.results.length > 0 ? (() => {
              const resultsWithObs = rnavHoldingAnalysis.results.filter(r => r.obsRows && r.obsRows.length > 0);
              if (resultsWithObs.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No obstacles found within any RNAV Holding protected area.
                  </p>
                );
              }
              return resultsWithObs.map((result) => {
                const resultIdx = rnavHoldingAnalysis.results.indexOf(result);
                return (
                  <details key={resultIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#00695c' }}>
                      {result.input.airport_name || result.input.airport_code || '—'}
                      {result.input.wpt_ident ? ` – WPT: ${result.input.wpt_ident}` : ''}
                      {result.criticalCount > 0
                        ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                      }
                      {result.input.error && (
                        <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                      )}
                    </summary>

                    {/* Input parameters */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#e8f5e9', padding: 8, borderRadius: 4 }}>
                      <b>Input Parameters</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>WPT Ident</td>
                            <td style={tdStyle}><b>{result.input.wpt_ident || '—'}</b></td>
                            <td style={tdStyle}>Outbound Type</td>
                            <td style={tdStyle}><b>{result.input.outbound_type || 'time'}</b></td>
                            <td style={tdStyle}>ATT / XTT</td>
                            <td style={tdStyle}><b>{result.input.att_nm} NM / {result.input.xtt_nm} NM</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Turn Direction</td>
                            <td style={tdStyle}><b>{result.input.turn_direction}</b></td>
                            <td style={tdStyle}>Inbound Track (°)</td>
                            <td style={tdStyle}><b>
                              {result.input.inbound_track_deg != null
                                ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (grid: ${result.input.utm_grid_inbound_deg?.toFixed(2)}°)`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}>Altitude</td>
                            <td style={tdStyle}><b>{result.input.altitude_ft != null ? `${result.input.altitude_ft} ft / ${result.geometry?.holdingAltM} m` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>IAS</td>
                            <td style={tdStyle}><b>{result.input.ias_kt != null ? `${result.input.ias_kt} kt` : '—'}</b></td>
                            <td style={tdStyle}>Outbound Time</td>
                            <td style={tdStyle}><b>{result.input.outbound_time_min != null ? `${result.input.outbound_time_min} min` : '—'}</b></td>
                            <td style={tdStyle}>WPT Fix</td>
                            <td style={tdStyle}><b>
                              {result.input.wpt_lat != null
                                ? `${toDMS(result.input.wpt_lat, true)} / ${toDMS(result.input.wpt_lon, false)}`
                                : '—'}
                            </b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>ISA Δ</td>
                            <td style={tdStyle}><b>{result.input.isa_delta_c != null ? `${result.input.isa_delta_c}°C` : '—'}</b></td>
                            <td style={tdStyle}>Category H</td>
                            <td style={tdStyle}><b>{result.input.use_category_h ? 'Yes' : 'No'}</b></td>
                            <td style={tdStyle}></td><td style={tdStyle}></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Geometry parameters */}
                    {result.geometry && (
                      <div style={{ marginTop: 4, marginBottom: 8, fontSize: '11px', fontFamily: 'monospace', background: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                        <strong>Geometry:</strong>
                        {' '}TAS = {result.geometry.tas_kt} kt
                        {result.geometry.wind_kt != null && ` | Wind = ${result.geometry.wind_kt} kt`}
                        {' '}| Turn radius = {result.geometry.turn_radius_m} m
                        {result.geometry.nominalDistM != null && ` | Nominal leg = ${result.geometry.nominalDistM} m`}
                        {result.geometry.outerDistM != null && ` | Outer leg = ${result.geometry.outerDistM} m`}
                        {result.geometry.innerDistM != null && ` | Inner leg = ${result.geometry.innerDistM} m`}
                        {' '}| Primary MOC = {result.geometry.primaryMocM} m
                        {result.geometry.secondaryMocM != null && ` | Secondary MOC = ${result.geometry.secondaryMocM}`}
                        {' '}| Holding alt = {result.geometry.holdingAltM} m
                      </div>
                    )}

                    {/* Obstacle table */}
                    <div style={{ marginTop: 8, border: '1px solid #80cbc4', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        Obstacles inside protected area
                        {result.criticalCount > 0
                          ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                          : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                        }
                      </div>
                      <div style={{ marginBottom: 4, fontSize: '12px' }}>
                        <strong>Total:</strong> {result.obsRows.length}
                        {' | '}
                        <strong>Primary:</strong> {result.obsRows.filter(r => r.zone === 'Primary' || !r.zone).length}
                        {result.secondaryPolygon && <>
                          {' | '}
                          <strong>Secondary:</strong> {result.obsRows.filter(r => r.zone === 'Secondary').length}
                        </>}
                        {' | '}<strong>Critical:</strong>{' '}
                        <span style={{ color: result.criticalCount > 0 ? '#d32f2f' : 'inherit', fontWeight: result.criticalCount > 0 ? 'bold' : 'normal' }}>
                          {result.criticalCount}
                        </span>
                      </div>
                      {result.obsRows.length > 0
                        ? renderRnavTable(result.obsRows, resultIdx)
                        : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                      }
                    </div>
                  </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {rnavHoldingAnalysis.message || 'No RNAV Holding configurations found within 50 km.'}
              </p>
            )}
          </div>
        );
      })()}

      {rnavHoldingError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>RNAV Holding Calculation Error:</b> {rnavHoldingError}
        </div>
      )}

      {/* ── RNP Holding Results ─────────────────────────────────────────── */}
      {rnpHoldingAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!rnpHoldingAnalysis.results?.some(r => r.obsRows?.length > 0)) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        const sortRnpRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleRnpSort = (key) => setRnpHoldingSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderRnpSortIndicator = (key) => (
          rnpHoldingSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{rnpHoldingSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          padding: 6, border: '1px solid #81c784', textAlign: 'left', whiteSpace: 'nowrap',
          background: rnpHoldingSortConfig.key === key ? '#c8e6c9' : '#e8f5e9',
          cursor: 'pointer', userSelect: 'none',
        });

        const renderRnpTable = (rows, resultIdx) => {
          if (!rows || rows.length === 0) return null;
          const indexedRows = rows.map((row, origIdx) => ({ ...row, _rnpId: `rnp-${resultIdx}-${origIdx}` }));
          const sortedRows = sortRnpRows(indexedRows, rnpHoldingSortConfig.key, rnpHoldingSortConfig.direction);
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '1150px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#c8e6c9' }}>
                    <th style={thSort('obstacleName')} onClick={() => handleRnpSort('obstacleName')}>Obstacle Name{renderRnpSortIndicator('obstacleName')}</th>
                    <th style={thSort('obstacleAltM')} onClick={() => handleRnpSort('obstacleAltM')}>Alt (m){renderRnpSortIndicator('obstacleAltM')}</th>
                    <th style={thSort('obstacleAltFt')} onClick={() => handleRnpSort('obstacleAltFt')}>Alt (ft){renderRnpSortIndicator('obstacleAltFt')}</th>
                    <th style={thSort('zone')} onClick={() => handleRnpSort('zone')}>Zone{renderRnpSortIndicator('zone')}</th>
                    <th style={thSort('distFromPrimaryBoundaryM')} onClick={() => handleRnpSort('distFromPrimaryBoundaryM')}>Dist. from primary bdry (m){renderRnpSortIndicator('distFromPrimaryBoundaryM')}</th>
                    <th style={thSort('mocM')} onClick={() => handleRnpSort('mocM')}>MOC (m){renderRnpSortIndicator('mocM')}</th>
                    <th style={thSort('mocFt')} onClick={() => handleRnpSort('mocFt')}>MOC (ft){renderRnpSortIndicator('mocFt')}</th>
                    <th style={thSort('surfaceAltM')} onClick={() => handleRnpSort('surfaceAltM')}>Surf. Alt (m){renderRnpSortIndicator('surfaceAltM')}</th>
                    <th style={thSort('surfaceAltFt')} onClick={() => handleRnpSort('surfaceAltFt')}>Surf. Alt (ft){renderRnpSortIndicator('surfaceAltFt')}</th>
                    <th style={thSort('clearanceM')} onClick={() => handleRnpSort('clearanceM')}>Clearance (m){renderRnpSortIndicator('clearanceM')}</th>
                    <th style={thSort('clearanceFt')} onClick={() => handleRnpSort('clearanceFt')}>Clearance (ft){renderRnpSortIndicator('clearanceFt')}</th>
                    <th style={thSort('isCritical')} onClick={() => handleRnpSort('isCritical')}>Critical{renderRnpSortIndicator('isCritical')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const id = row._rnpId;
                    const isSelected = selectedRnpHoldingObstacles.has(id);
                    const isCrit = row.isCritical;
                    const isSecondary = row.zone === 'Secondary';
                    const baseBg = isCrit ? '#ffebee' : isSecondary ? '#e8f5e9' : 'white';
                    return (
                      <tr
                        key={id}
                        onClick={(e) => {
                          setSelectedRnpHoldingObstacles(prev => {
                            const s = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (s.has(id)) s.delete(id); else s.add(id);
                            } else { s.clear(); s.add(id); }
                            return s;
                          });
                        }}
                        style={{ background: isSelected ? '#c8e6c9' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#e8f5e9'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                        <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                        <td style={{ ...tdStyle, color: isSecondary ? '#666' : '#333' }}>{row.zone || 'Primary'}</td>
                        <td style={tdStyle}>{row.distFromPrimaryBoundaryM != null ? row.distFromPrimaryBoundaryM : '—'}</td>
                        <td style={tdStyle}>{row.mocM != null ? row.mocM : '—'}</td>
                        <td style={tdStyle}>{row.mocFt != null ? row.mocFt : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltM != null ? row.surfaceAltM : '—'}</td>
                        <td style={tdStyle}>{row.surfaceAltFt != null ? row.surfaceAltFt : '—'}</td>
                        <td style={tdStyle}>{row.clearanceM != null ? row.clearanceM : '—'}</td>
                        <td style={tdStyle}>{row.clearanceFt != null ? row.clearanceFt : '—'}</td>
                        <td style={{ ...tdStyle, color: isCrit ? '#c62828' : '#666', fontWeight: isCrit ? 'bold' : 'normal' }}>
                          {isCrit ? '⚠ YES' : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        return (
          <div style={{ background: '#f1f8e9', borderRadius: 6, padding: 14, marginTop: 16, border: '1px solid #dcedc8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h4 style={{ margin: 0, color: '#2e7d32' }}>
                RNP Holding Calculation Results
              </h4>
              <button
                onClick={() => setShowRnpHoldingSurfaces(!showRnpHoldingSurfaces)}
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showRnpHoldingSurfaces ? '#2e7d32' : '#c8e6c9',
                  color: showRnpHoldingSurfaces ? '#fff' : '#2e7d32',
                  border: '1px solid #2e7d32', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showRnpHoldingSurfaces ? 'Hide RNP Holding surfaces' : 'Show RNP Holding surfaces'}
              </button>
            </div>

            {rnpHoldingAnalysis.results && rnpHoldingAnalysis.results.length > 0 ? (() => {
              const resultsWithObs = rnpHoldingAnalysis.results.filter(r => r.obsRows && r.obsRows.length > 0);
              if (resultsWithObs.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No obstacles found within any RNP Holding protected area.
                  </p>
                );
              }
              return resultsWithObs.map((result) => {
                const resultIdx = rnpHoldingAnalysis.results.indexOf(result);
                const rnpValue = result.input.rnp_value || 1.0;
                return (
                  <details key={resultIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#2e7d32' }}>
                      {result.input.airport_name || result.input.airport_code || '—'}
                      {result.input.wpt_ident ? ` – WPT: ${result.input.wpt_ident}` : ''}
                      {' '}(RNP {rnpValue})
                      {result.criticalCount > 0
                        ? <span style={{ color: '#c62828', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                      }
                      {result.input.error && (
                        <span style={{ color: '#c62828', marginLeft: 8 }}>⚠ {result.input.error}</span>
                      )}
                    </summary>

                    {/* Input parameters */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#e8f5e9', padding: 8, borderRadius: 4 }}>
                      <b>Input Parameters</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>WPT Ident</td>
                            <td style={tdStyle}><b>{result.input.wpt_ident || '—'}</b></td>
                            <td style={tdStyle}>RNP Value</td>
                            <td style={tdStyle}><b>{rnpValue} NM</b></td>
                            <td style={tdStyle}>Turn Direction</td>
                            <td style={tdStyle}><b>{result.input.turn_direction || '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Inbound Track (°)</td>
                            <td style={tdStyle}><b>
                              {result.input.inbound_track_deg != null
                                ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (grid: ${result.input.utm_grid_inbound_deg?.toFixed(2)}°)`
                                : '—'}
                            </b></td>
                            <td style={tdStyle}>Altitude</td>
                            <td style={tdStyle}><b>{result.input.altitude_ft != null ? `${result.input.altitude_ft} ft / ${result.geometry?.holdingAltM} m` : '—'}</b></td>
                            <td style={tdStyle}>IAS</td>
                            <td style={tdStyle}><b>{result.input.ias_kt != null ? `${result.input.ias_kt} kt` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Outbound Time</td>
                            <td style={tdStyle}><b>{result.input.outbound_time_min != null ? `${result.input.outbound_time_min} min` : '—'}</b></td>
                            <td style={tdStyle}>ISA delta</td>
                            <td style={tdStyle}><b>{result.input.isa_delta_c != null ? `${result.input.isa_delta_c} deg C` : '—'}</b></td>
                            <td style={tdStyle}>Category H</td>
                            <td style={tdStyle}><b>{result.input.use_category_h ? 'Yes' : 'No'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Wind Type</td>
                            <td style={tdStyle}><b>{result.input.wind_type || '—'}</b></td>
                            <td style={tdStyle}>TAS</td>
                            <td style={tdStyle}><b>{result.geometry?.tas_kt != null ? `${result.geometry.tas_kt} kt` : '—'}</b></td>
                            <td style={tdStyle}>Wind</td>
                            <td style={tdStyle}><b>{result.geometry?.wind_kt != null ? `${result.geometry.wind_kt} kt` : '—'}</b></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Obstacle table */}
                    <div style={{ marginTop: 4 }}>
                      {result.obsRows && result.obsRows.length > 0
                        ? renderRnpTable(result.obsRows, resultIdx)
                        : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                      }
                    </div>
                  </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {rnpHoldingAnalysis.message || 'No RNP Holding configurations found within 50 km.'}
              </p>
            )}
          </div>
        );
      })()}

      {rnpHoldingError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>RNP Holding Calculation Error:</b> {rnpHoldingError}
        </div>
      )}

      {/* ── Shielding Results ─────────────────────────────────────────────── */}
      {shieldingAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!shieldingAnalysis.results?.some(r => r.rowsWithinRadius?.length > 0)) return null;
        const tdStyle = { border: '1px solid #ccc', padding: '3px 6px', fontSize: '12px' };

        const sortShieldingRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (typeof av === 'number' && typeof bv === 'number') {
            const an = av ?? -Infinity, bn = bv ?? -Infinity;
            return dir === 'asc' ? an - bn : bn - an;
          }
          if (typeof av === 'boolean' && typeof bv === 'boolean') {
            return dir === 'asc' ? (av === bv ? 0 : av ? 1 : -1) : (av === bv ? 0 : av ? -1 : 1);
          }
          av = String(av ?? ''); bv = String(bv ?? '');
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleShieldingSort = (key) => setShieldingSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderShieldingSortIndicator = (key) => (
          shieldingSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{shieldingSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thSort = (key) => ({
          border: '1px solid #ccc', padding: '3px 6px', fontSize: '12px', fontWeight: 'bold',
          background: shieldingSortConfig.key === key ? '#b9f6ca' : '#e8f5e9',
          cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        });
        const thPlain = { border: '1px solid #ccc', padding: '3px 6px', fontSize: '12px', fontWeight: 'bold', background: '#e8f5e9', whiteSpace: 'nowrap' };

        /**
         * Render the obstacle rows table for one nav aid.
         * expandedIdx is used for stable row IDs.
         */
        const renderShieldingTable = (rows, expandedIdx) => {
          if (!rows || rows.length === 0) return null;
          const M_TO_FT = 3.28084;

          // Human-readable labels for protected-zone names (translated)
          const AREA_NAME_LABELS = {
            ground:   t('shieldingZoneGround')   || 'Ground',
            lateral:  t('shieldingZoneLateral')  || 'Lateral',
            sloping:  t('shieldingZoneSloping')  || 'Sloping',
            inner:    t('shieldingZoneInner')     || 'Inner',
            cone:     t('shieldingZoneCone')      || 'Cone',
            extended: t('shieldingZoneExtended')  || 'Extended',
          };

          // Attach stable IDs before sorting
          const indexedRows = rows.map((row, origIdx) => ({
            ...row,
            _shldId: `shld-${expandedIdx}-${origIdx}`,
          }));
          const sorted = sortShieldingRows(indexedRows, shieldingSortConfig.key, shieldingSortConfig.direction);

          return (
            <div style={{ overflowX: 'auto', marginTop: 6 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px', minWidth: '900px' }}>
                <thead>
                  <tr style={{ background: '#c8e6c9' }}>
                    <th style={thSort('obstacleName')} onClick={() => handleShieldingSort('obstacleName')}>{t('shieldingColObstacleName') || 'Obstacle Name'}{renderShieldingSortIndicator('obstacleName')}</th>
                    <th style={thSort('obstacleElevM')} onClick={() => handleShieldingSort('obstacleElevM')}>{t('shieldingColAltM') || 'Alt (m)'}{renderShieldingSortIndicator('obstacleElevM')}</th>
                    <th style={thPlain}>{t('shieldingColAltFt') || 'Alt (ft)'}</th>
                    <th style={thSort('areaName')} onClick={() => handleShieldingSort('areaName')}>{t('shieldingColSurface') || 'Surface'}{renderShieldingSortIndicator('areaName')}</th>
                    <th style={thSort('surfaceAltM')} onClick={() => handleShieldingSort('surfaceAltM')}>{t('shieldingColSurfAltM') || 'Surf. Alt (m)'}{renderShieldingSortIndicator('surfaceAltM')}</th>
                    <th style={thPlain}>{t('shieldingColSurfAltFt') || 'Surf. Alt (ft)'}</th>
                    <th style={thPlain}>{t('shieldingColCurvCorr') || 'Curvature Corr. (m)'}</th>
                    <th style={thSort('heightDeltaM')} onClick={() => handleShieldingSort('heightDeltaM')}>{t('shieldingColDiffM') || 'Diff (m)'}{renderShieldingSortIndicator('heightDeltaM')}</th>
                    <th style={thPlain}>{t('shieldingColDiffFt') || 'Diff (ft)'}</th>
                    <th style={thSort('isCritical')} onClick={() => handleShieldingSort('isCritical')}>{t('shieldingColCritical') || 'Critical'}{renderShieldingSortIndicator('isCritical')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row) => {
                    const id = row._shldId;
                    const isSelected = selectedShieldingObstacles.has(id);
                    const isCrit = row.isCritical;
                    const baseBg = isCrit ? '#ffebee' : '#f9fbe7';
                    const altM  = row.obstacleElevM  != null ? row.obstacleElevM  : null;
                    const altFt = altM  != null ? altM  * M_TO_FT : null;
                    const surfAltM  = row.surfaceAltM != null ? row.surfaceAltM
                      : (row.navAidElevM != null ? row.navAidElevM : null);
                    const surfAltFt = surfAltM != null ? surfAltM * M_TO_FT : null;
                    const diffM  = row.heightDeltaM != null ? row.heightDeltaM : null;
                    const diffFt = diffM != null ? diffM * M_TO_FT : null;
                    const curvStr = (row.applyEarthCurv && row.earthCurvCorrM != null)
                      ? row.earthCurvCorrM.toFixed(3)
                      : (row.areaName != null ? '0.000' : '—');
                    return (
                      <tr
                        key={id}
                        data-shielding-id={id}
                        onClick={(e) => {
                          setSelectedShieldingObstacles(prev => {
                            const s = new Set(prev);
                            if (e.ctrlKey || e.metaKey) {
                              if (s.has(id)) s.delete(id); else s.add(id);
                            } else { s.clear(); s.add(id); }
                            return s;
                          });
                        }}
                        style={{ background: isSelected ? '#bbdefb' : baseBg, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = isCrit ? '#ffcdd2' : '#f5f5f5'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}><b>{row.obstacleName}</b></td>
                        <td style={tdStyle}>{altM  != null ? altM.toFixed(1)  : '—'}</td>
                        <td style={tdStyle}>{altFt != null ? altFt.toFixed(1) : '—'}</td>
                        <td style={tdStyle}>{AREA_NAME_LABELS[row.areaName] || row.navAidTypeLabel || row.navAidType}</td>
                        <td style={tdStyle}>{surfAltM  != null ? surfAltM.toFixed(1)  : '—'}</td>
                        <td style={tdStyle}>{surfAltFt != null ? surfAltFt.toFixed(1) : '—'}</td>
                        <td style={tdStyle}>{curvStr}</td>
                        <td style={{ ...tdStyle, color: isCrit ? '#b71c1c' : 'inherit' }}>
                          {diffM  != null ? diffM.toFixed(1)  : '—'}
                        </td>
                        <td style={{ ...tdStyle, color: isCrit ? '#b71c1c' : 'inherit' }}>
                          {diffFt != null ? diffFt.toFixed(1) : '—'}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 'bold', color: isCrit ? '#b71c1c' : '#388e3c' }}>
                          {isCrit ? 'Yes' : 'No'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        return (
          <div style={{ marginTop: 10, background: '#e8f5e9', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#1b5e20' }}>
                {t('shieldingNavAidTitle') || 'Shielding – Nav Aid Obstacle Analysis'}
              </h3>
              <button
                onClick={() => setShowNavSurfaces(v => !v)}
                title={showNavSurfaces
                  ? (t('shieldingToggleHide') || 'Hide NAV surfaces')
                  : (t('shieldingToggleShow') || 'Show NAV surfaces')}
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showNavSurfaces ? '#1b5e20' : '#c8e6c9',
                  color: showNavSurfaces ? '#fff' : '#1b5e20',
                  border: '1px solid #1b5e20', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showNavSurfaces
                  ? (t('shieldingToggleHide') || 'Hide NAV surfaces')
                  : (t('shieldingToggleShow') || 'Show NAV surfaces')}
              </button>
            </div>

            {shieldingAnalysis.results && shieldingAnalysis.results.length > 0 ? (() => {
              const withObstacles = shieldingAnalysis.results.filter(result => result.rowsWithinRadius && result.rowsWithinRadius.length > 0);
              // Expand VOR/DME into two virtual results – one for VOR rows, one for DME_N rows
              const expandedResults = withObstacles.flatMap(result => {
                if (result.input.nav_aid_type !== 'VOR/DME') return [result];
                return ['VOR', 'DME_N'].map(type => ({
                  ...result,
                  input: { ...result.input, nav_aid_type: type },
                  rowsWithinRadius: result.rowsWithinRadius.filter(r => r.navAidType === type),
                  rows: (result.rows || []).filter(r => r.navAidType === type),
                })).filter(sub => sub.rowsWithinRadius.length > 0);
              });
              if (expandedResults.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    {t('shieldingNoObstaclesAny') || 'No obstacles within protected surface for any navigation aid.'}
                  </p>
                );
              }
              return expandedResults.map((result, idx) => {
                const inp = result.input;
                const totalRows     = result.rows ? result.rows.length : 0;
                const withinRows    = result.rowsWithinRadius ? result.rowsWithinRadius.length : 0;
                const criticalCount = result.rowsWithinRadius
                  ? result.rowsWithinRadius.filter(r => r.isCritical).length
                  : 0;

                const navAidTypeLabel = getNavAidTypeLabel(inp.nav_aid_type) || '—';

                return (
                  <details key={idx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#1b5e20' }}>
                      {inp.airport_name || inp.airport_code || '—'}
                      {inp.runway_designation ? ` – RWY ${inp.runway_designation}` : ''}
                      {' – '}
                      {inp.nav_aid_name || inp.nav_ident || navAidTypeLabel}
                      {' ('}
                      {navAidTypeLabel}
                      {')'}
                      {criticalCount > 0
                        ? <span style={{ color: '#b71c1c', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({criticalCount} {t('critical') || 'critical'})</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>
                      }
                    </summary>

                    {/* Input Parameters */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#f1f8e9', padding: 8, borderRadius: 4 }}>
                      <b>{t('omniInputParams') || 'Input Parameters'}</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>{t('airport') || 'Airport Code'}</td>
                            <td style={tdStyle}><b>{inp.airport_code || '—'}</b></td>
                            <td style={tdStyle}>{t('airport') || 'Airport Name'}</td>
                            <td style={tdStyle}><b>{inp.airport_name || '—'}</b></td>
                            <td style={tdStyle}>{t('runway') || 'Runway'}</td>
                            <td style={tdStyle}><b>{inp.runway_designation || '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>{t('shieldingNavAidType') || 'Nav Aid Type'}</td>
                            <td style={tdStyle}><b>{navAidTypeLabel}</b></td>
                            <td style={tdStyle}>{t('shieldingNavAidName') || 'Nav Aid Name'}</td>
                            <td style={tdStyle}><b>{inp.nav_aid_name || '—'}</b></td>
                            <td style={tdStyle}>{t('shieldingIdent') || 'Ident'}</td>
                            <td style={tdStyle}><b>{inp.nav_ident || '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>{t('shieldingFrequency') || 'Frequency'}</td>
                            <td style={tdStyle}><b>{inp.nav_freq || '—'}</b></td>
                            <td style={tdStyle}>{t('shieldingChannel') || 'Channel'}</td>
                            <td style={tdStyle}><b>{inp.nav_channel || '—'}</b></td>
                            <td style={tdStyle}>{t('shieldingElevation') || 'Elevation'}</td>
                            <td style={tdStyle}><b>{inp.nav_elev_ft != null ? `${(inp.nav_elev_ft * 0.3048).toFixed(1)} m / ${inp.nav_elev_ft} ft` : '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>{t('shieldingNavAidPosition') || 'Nav Aid Position'}</td>
                            <td style={tdStyle} colSpan={3}>
                              <b>
                                {inp.nav_position_lat != null && inp.nav_position_long != null
                                  ? `${inp.nav_position_lat} ${inp.nav_position_long}`
                                  : '—'}
                              </b>
                            </td>
                            <td style={tdStyle}>{t('shieldingEarthCurvature') || 'Earth Curvature'}</td>
                            <td style={tdStyle}><b>{inp.earth_curvature ? 'Yes' : 'No'}</b></td>
                          </tr>
                          {(inp.true_track_deg != null || inp.dist_to_thr_m != null) && (
                            <tr>
                              <td style={tdStyle}>{t('shieldingTrueTrack') || 'True Track (°)'}</td>
                              <td style={tdStyle}><b>{inp.true_track_deg != null ? `${parseFloat(inp.true_track_deg).toFixed(2)}° (UTM grid: ${inp.utm_grid_track_deg?.toFixed(2)}°)` : '—'}</b></td>
                              <td style={tdStyle}>{t('shieldingDistToThr') || 'Dist to THR (m)'}</td>
                              <td style={tdStyle}><b>{inp.dist_to_thr_m != null ? `${parseFloat(inp.dist_to_thr_m).toFixed(1)} m` : '—'}</b></td>
                              <td style={tdStyle}>{t('shieldingRemarks') || 'Remarks'}</td>
                              <td style={tdStyle}><b>{inp.nav_remarks || '—'}</b></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary */}
                    <div style={{ marginBottom: 6, fontSize: '12px' }}>
                      <strong>{t('shieldingObstaclesChecked') || 'Obstacles checked'}:</strong> {result.obstaclesChecked}
                      {' | '}
                      <strong>{t('shieldingObstacleEvals') || 'Obstacle evaluations'}:</strong> {totalRows}
                      {' | '}
                      <strong style={{ color: withinRows > 0 ? '#b71c1c' : '#388e3c' }}>
                        {t('shieldingWithinSurface') || 'Within protected surface'}: {withinRows}
                      </strong>
                    </div>

                    {/* Results table – obstacles within the protected surface */}
                    {renderShieldingTable(result.rowsWithinRadius, idx)}
                  </details>
                );
              });
            })() : (
              <p style={{ color: '#666' }}>
                {shieldingAnalysis.message || (t('shieldingNoNavAids') || 'No navigation aids found within 50 km.')}
              </p>
            )}
          </div>
        );
      })()}

      {shieldingError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>Shielding Calculation Error:</b> {shieldingError}
        </div>
      )}

      {/* ── MSA (Minimum Sector Altitude) Results ─────────────────────────── */}
      {msaAnalysis && (isAuthenticated && user?.betald === 'JA') && (() => {
        if (!msaAnalysis.results?.some(r => r.obsRows?.length > 0)) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

        const sortMsaRows = (rows, key, dir) => [...rows].sort((a, b) => {
          let av = a[key], bv = b[key];
          if (av == null && bv == null) return 0;
          if (av == null) return dir === 'asc' ? 1 : -1;
          if (bv == null) return dir === 'asc' ? -1 : 1;
          if (typeof av === 'number' && typeof bv === 'number') {
            return dir === 'asc' ? av - bv : bv - av;
          }
          av = String(av); bv = String(bv);
          return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });

        const handleMsaSort = (key) => setMsaSortConfig(prev => ({
          key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const renderMsaSortIndicator = (key) => (
          msaSortConfig.key === key
            ? <span style={{ marginLeft: 4 }}>{msaSortConfig.direction === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>
        );

        const thMsaSort = (key) => ({
          padding: 6, border: '1px solid #81c784', textAlign: 'left', whiteSpace: 'nowrap',
          background: msaSortConfig.key === key ? '#c8e6c9' : '#e8f5e9',
          cursor: 'pointer', userSelect: 'none',
        });

        const renderMsaObstacleTable = (rows, sectorIdx) => {
          if (!rows || rows.length === 0) return null;
          const indexed = rows.map((row, origIdx) => ({ ...row, _msaId: `msa-${sectorIdx}-${origIdx}` }));
          const sorted = sortMsaRows(indexed, msaSortConfig.key, msaSortConfig.direction);
          return (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '900px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#e8f5e9' }}>
                    <th style={thMsaSort('obstacleName')} onClick={() => handleMsaSort('obstacleName')}>Obstacle Name{renderMsaSortIndicator('obstacleName')}</th>
                    <th style={thMsaSort('obstacleAltM')} onClick={() => handleMsaSort('obstacleAltM')}>Alt (m){renderMsaSortIndicator('obstacleAltM')}</th>
                    <th style={thMsaSort('obstacleAltFt')} onClick={() => handleMsaSort('obstacleAltFt')}>Alt (ft){renderMsaSortIndicator('obstacleAltFt')}</th>
                    <th style={thMsaSort('mocM')} onClick={() => handleMsaSort('mocM')}>MOC (m){renderMsaSortIndicator('mocM')}</th>
                    <th style={thMsaSort('mocFt')} onClick={() => handleMsaSort('mocFt')}>MOC (ft){renderMsaSortIndicator('mocFt')}</th>
                    <th style={thMsaSort('ocaM')} onClick={() => handleMsaSort('ocaM')}>OCA (m){renderMsaSortIndicator('ocaM')}</th>
                    <th style={thMsaSort('ocaFt')} onClick={() => handleMsaSort('ocaFt')}>OCA (ft){renderMsaSortIndicator('ocaFt')}</th>
                    <th style={thMsaSort('roundedAltM')} onClick={() => handleMsaSort('roundedAltM')}>MSA (m){renderMsaSortIndicator('roundedAltM')}</th>
                    <th style={thMsaSort('roundedAltFt')} onClick={() => handleMsaSort('roundedAltFt')}>MSA (ft){renderMsaSortIndicator('roundedAltFt')}</th>
                    <th style={thMsaSort('distFromCentreM')} onClick={() => handleMsaSort('distFromCentreM')}>Dist from centre (m){renderMsaSortIndicator('distFromCentreM')}</th>
                    <th style={thMsaSort('clearanceM')} onClick={() => handleMsaSort('clearanceM')}>Clearance (m){renderMsaSortIndicator('clearanceM')}</th>
                    <th style={thMsaSort('clearanceFt')} onClick={() => handleMsaSort('clearanceFt')}>Clearance (ft){renderMsaSortIndicator('clearanceFt')}</th>
                    <th style={thMsaSort('isCritical')} onClick={() => handleMsaSort('isCritical')}>Critical{renderMsaSortIndicator('isCritical')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row) => {
                    const isControlling = sorted.indexOf(row) === 0;
                    const baseBg = isControlling ? '#c8e6c9' : 'white';
                    return (
                      <tr
                        key={row._msaId}
                        style={{ background: baseBg }}
                        onMouseEnter={(e) => { if (!isControlling) e.currentTarget.style.background = '#f1f8f1'; }}
                        onMouseLeave={(e) => { if (!isControlling) e.currentTarget.style.background = baseBg; }}
                      >
                        <td style={tdStyle}>{row.obstacleName}</td>
                        <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                        <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocM != null ? `${row.mocM} m` : '—'}</td>
                        <td style={{ ...tdStyle, color: '#555' }}>{row.mocFt != null ? `${row.mocFt} ft` : '—'}</td>
                        <td style={tdStyle}>{row.ocaM != null ? row.ocaM : '—'}</td>
                        <td style={tdStyle}>{row.ocaFt != null ? row.ocaFt : '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: isControlling ? 'bold' : 'normal', color: '#1b5e20' }}>
                          {row.roundedAltM != null ? row.roundedAltM : '—'}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: isControlling ? 'bold' : 'normal', color: '#1b5e20' }}>
                          {row.roundedAltFt != null ? row.roundedAltFt : '—'}
                        </td>
                        <td style={tdStyle}>{row.distFromCentreM != null ? row.distFromCentreM : '—'}</td>
                        <td style={{ ...tdStyle, color: row.clearanceM != null && row.clearanceM < 0 ? '#d32f2f' : '#2e7d32' }}>{row.clearanceM != null ? row.clearanceM : '—'}</td>
                        <td style={{ ...tdStyle, color: row.clearanceFt != null && row.clearanceFt < 0 ? '#d32f2f' : '#2e7d32' }}>{row.clearanceFt != null ? row.clearanceFt : '—'}</td>
                        <td style={{ ...tdStyle, color: row.isCritical ? '#d32f2f' : 'inherit', fontWeight: row.isCritical ? 'bold' : 'normal' }}>{row.isCritical ? 'Yes' : 'No'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        return (
          <div style={{ marginTop: 10, background: '#e8f5e9', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#1b5e20' }}>
                MSA Calculation Results
              </h3>
              <button
                onClick={() => setShowMsaSurfaces(v => !v)}
                title="Toggle MSA sector polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showMsaSurfaces ? '#1b5e20' : '#c8e6c9',
                  color: showMsaSurfaces ? '#fff' : '#1b5e20',
                  border: '1px solid #1b5e20', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showMsaSurfaces ? 'Hide MSA sectors' : 'Show MSA sectors'}
              </button>
            </div>

            {msaAnalysis.results && msaAnalysis.results.length > 0 ? (() => {
              const resultsToShow = msaAnalysis.results.filter(r => r.obsRows?.length > 0);
              if (resultsToShow.length === 0) {
                return (
                  <p style={{ color: '#666' }}>
                    No obstacles found within any MSA sector.
                  </p>
                );
              }
              return msaAnalysis.results.map((result, sectorIdx) => (
                <details key={sectorIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#1b5e20' }}>
                    {[result.input.msa_id, result.input.nav_aid_name].filter(Boolean).join(' ')}
                    {[result.input.msa_id, result.input.nav_aid_name].some(Boolean) ? ' ' : ''}
                    {result.input.airport_name || result.input.airport_code || '—'}
                    {result.input.runway_designation ? ` RWY ${result.input.runway_designation}` : ''}
                    {` – MSA ${result.input.sector_from_deg != null ? `${result.input.sector_from_deg}°` : ''}–${result.input.sector_to_deg != null ? `${result.input.sector_to_deg}°` : ''}`}
                    {result.input.radius_nm != null ? ` / ${result.input.radius_nm} NM` : ''}
                    {result.msa?.formatted ? (
                      <span style={{ marginLeft: 12, color: '#2e7d32', fontWeight: 'bold' }}>
                        → {result.msa.formatted}
                      </span>
                    ) : null}
                    {result.criticalCount > 0
                      ? <span style={{ marginLeft: 10, color: '#d32f2f', fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                      : (result.obsRows?.length > 0
                          ? <span style={{ marginLeft: 10, color: '#2e7d32' }}>✓ No impact</span>
                          : null)
                    }
                    {result.input.error && (
                      <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {result.input.error}</span>
                    )}
                  </summary>

                  {/* Input parameters */}
                  <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#f1f8f1', padding: 8, borderRadius: 4 }}>
                    <b>Input Parameters</b>
                    <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                      <tbody>
                        <tr>
                          <td style={tdStyle}>Airport</td>
                          <td style={tdStyle}><b>{result.input.airport_name || result.input.airport_code || '—'}</b></td>
                          <td style={tdStyle}>MSA Reference</td>
                          <td style={tdStyle}><b>{result.input.msa_id || '—'}</b></td>
                          <td style={tdStyle}>Nav Aid</td>
                          <td style={tdStyle}><b>{result.input.nav_aid_name || '—'}</b></td>
                        </tr>
                        <tr>
                          <td style={tdStyle}>Sector From</td>
                          <td style={tdStyle}><b>{result.input.sector_from_deg != null ? `${result.input.sector_from_deg}°` : '—'}</b></td>
                          <td style={tdStyle}>Sector To</td>
                          <td style={tdStyle}><b>{result.input.sector_to_deg != null ? `${result.input.sector_to_deg}°` : '—'}</b></td>
                          <td style={tdStyle}>Radius</td>
                          <td style={tdStyle}><b>{result.input.radius_nm != null ? `${result.input.radius_nm} NM` : '—'}</b></td>
                        </tr>
                        <tr>
                          <td style={tdStyle}>MOC</td>
                          <td style={tdStyle}><b>{result.input.moc != null ? `${result.input.moc} ${result.input.moc_unit || ''}` : '—'}</b></td>
                          <td style={tdStyle}>Centre</td>
                          <td style={tdStyle}>
                            <b>{result.input.centre_lat != null ? `${toDMS(result.input.centre_lat, true)} / ${toDMS(result.input.centre_lon, false)}` : '—'}</b>
                          </td>
                          <td style={tdStyle}>Published MSA</td>
                          <td style={{ ...tdStyle, color: '#1b5e20', fontWeight: 'bold' }}>
                            {result.input.altitude != null ? `${result.input.altitude} ${result.input.altitude_unit || ''}` : '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Obstacle table */}
                  <div style={{ marginTop: 8, border: '1px solid #81c784', borderRadius: 4, padding: 8 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                      Obstacles inside protected area
                      <span style={{ marginLeft: 8, fontWeight: 'normal', color: result.criticalCount > 0 ? '#d32f2f' : '#555' }}>
                        ({result.criticalCount || 0} critical)
                      </span>
                    </div>
                    {result.obsRows && result.obsRows.length > 0
                      ? renderMsaObstacleTable(result.obsRows, sectorIdx)
                      : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                    }
                  </div>
                </details>
              ));
            })() : (
              <p style={{ color: '#666' }}>
                {msaAnalysis.message || 'No MSA sector configurations found.'}
              </p>
            )}
          </div>
        );
      })()}

      {msaError && (
        <div style={{ color: '#b30000', marginTop: 8, background: '#ffebee', padding: 12, borderRadius: 4 }}>
          <b>MSA Calculation Error:</b> {msaError}
        </div>
      )}

      {/* ── TAA (Terminal Arrival Altitude) Results ───────────────────────── */}
      {(taaAnalysis || taaError) && (isAuthenticated && user?.betald === 'JA') && (
        <TaaResultsPanel
          taaAnalysis={taaAnalysis}
          taaError={taaError}
          showTaaSectors={showTaaSectors}
          setShowTaaSectors={setShowTaaSectors}
        />
      )}

      {/* ── Circling Results ──────────────────────────────────────────────── */}
      {(circlingAnalysis || circlingError) && (isAuthenticated && user?.betald === 'JA') && (
        <CirclingResultsPanel
          circlingAnalysis={circlingAnalysis}
          circlingError={circlingError}
          showCirclingAreas={showCirclingAreas}
          setShowCirclingAreas={setShowCirclingAreas}
        />
      )}

      {/* ── GNSS Approach Results ─────────────────────────────────────────── */}
      {(gnssApchAnalysis || gnssApchError) && (isAuthenticated && user?.betald === 'JA') && (() => {
        const gnssResults = Array.isArray(gnssApchAnalysis?.results) ? gnssApchAnalysis.results : [];
        const tableResults = gnssResults.filter(gnssHasObstacleResults);
        if (!tableResults.length && !gnssApchError && !gnssApchAnalysis?.summary?.categoryErrors
          && !gnssResults.some(r => r.input?.error)) return null;
        const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };
        return (
          <div style={{ marginTop: 10, background: '#e3f2fd', padding: 16, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0, color: '#1565c0' }}>
                GNSS Approach Results
              </h3>
              <button
                onClick={() => setShowGnssApchSurfaces(v => !v)}
                title="Toggle GNSS Approach protection area polygons on the map"
                style={{
                  padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  background: showGnssApchSurfaces ? '#1565c0' : '#bbdefb',
                  color: showGnssApchSurfaces ? '#fff' : '#1565c0',
                  border: '1px solid #1565c0', whiteSpace: 'nowrap',
                }}
              >
                🗺 {showGnssApchSurfaces ? 'Hide GNSS surfaces' : 'Show GNSS surfaces'}
              </button>
            </div>

            {gnssApchError && (
              <div style={{ color: '#b30000', background: '#ffebee', padding: 8, borderRadius: 4, marginBottom: 8 }}>
                <b>Error:</b> {gnssApchError}
              </div>
            )}

            {gnssApchAnalysis?.summary?.distanceFilter && (() => {
              const filter = gnssApchAnalysis.summary.distanceFilter;
              return (
                <div style={{ fontSize: 12, marginBottom: 10 }}>
                  Distance filter: {filter.radiusNm} NM from THR to any obstacle (including circle radius).
                  {' '}Procedures retained: {filter.proceduresSelected} of {filter.proceduresAvailable};
                  {' '}outside range: {filter.proceduresExcluded}.
                  {filter.proceduresWithoutReference > 0 && (
                    <div style={{ color: '#b30000' }}>
                      {filter.proceduresWithoutReference} procedures could not be distance-filtered because
                      {' '}THR coordinates are missing or invalid; retained for input validation.
                    </div>
                  )}
                </div>
              );
            })()}

            {tableResults.length > 0 ? (
              <>
              <p style={{ fontSize: 12, marginTop: 0 }}>{language === 'sv'
                ? 'Tabeller visas endast för procedurer och kategorier med hinder inom skyddsytorna.'
                : 'Tables show only procedures and categories with obstacles within the protection areas.'}</p>
              <GnssCategoryComparison results={tableResults} />
              {tableResults.map((proc, procIdx) => {
                const inp = proc.input;
                const iasLabels = { iafwp1_ias_kt: 'IAF 1', iafwp2_ias_kt: 'IAF 2', iafwp3_ias_kt: 'IAF 3',
                  iwp_ias_kt: 'IF', fawp_ias_kt: 'FAF / SOC', baro_ias_at_thr_kt: 'BARO THR', lpv_ias_at_thr_kt: 'LPV THR' };
                const iasRows = [
                  ...Object.entries(inp.ias_selection || {}).map(([field, selection]) => ({
                    label: iasLabels[field] || field, selection, plus10: false,
                  })),
                  ...(inp.missed_legs || []).filter(leg => leg.ias_selection).map(leg => ({
                    label: `MISAP leg ${leg.leg_no}`, selection: leg.ias_selection,
                    plus10: leg.ias_plus10_percent === true,
                  })),
                ];
                const cleanWptIdent = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
                const getSegmentWptPairLabel = (seg) => {
                  if (seg.baroSurface || seg.lpvSurface || seg.xlsSurface) return `${seg.fromPhase} → ${seg.toPhase}`;
                  const segFamily = String(seg?.segFamily || '').toLowerCase();
                  if (segFamily === 'initial') {
                    const idxMatch = String(seg?.segLabel || '').match(/initial\s+(\d+)/i);
                    const idx = idxMatch ? Number(idxMatch[1]) : null;
                    const from = idx != null ? cleanWptIdent(inp?.[`iafwp${idx}_ident`]) : null;
                    const to = cleanWptIdent(inp?.iwp_ident);
                    if (from && to) return `${from}-${to}`;
                    return null;
                  }
                  if (segFamily === 'intermediate') {
                    const from = cleanWptIdent(inp?.iwp_ident);
                    const to = seg.toPhase === 'FAP' ? 'FAP' : cleanWptIdent(inp?.fawp_ident);
                    if (from && to) return `${from}-${to}`;
                    return null;
                  }
                  if (segFamily === 'final') {
                    const from = cleanWptIdent(inp?.fawp_ident);
                    const to = cleanWptIdent(inp?.mawp_ident);
                    if (from && to) return `${from}-${to}`;
                    return null;
                  }
                  return null;
                };
                const obstacleCounts = gnssObstacleCounts(proc);
                const totalCritical = obstacleCounts.criticalObstacles;
                const reviewColor = totalCritical > 0 ? '#b71c1c' : '#555';
                const summary = proc.summary || {};
                const hasMaxMoca = summary.maxMocaM != null && summary.maxMocaFt != null;
                const hasMaxOca = summary.maxOcaM != null && summary.maxOcaFt != null;
                const summaryAltitudeParts = [];
                if (hasMaxMoca) {
                  summaryAltitudeParts.push({
                    label: 'Computed max MOCA',
                    value: `${summary.maxMocaM} m / ${summary.maxMocaFt} ft`,
                  });
                }
                if (hasMaxOca) {
                  summaryAltitudeParts.push({
                    label: 'Computed max OCA',
                    value: `${summary.maxOcaM} m / ${summary.maxOcaFt} ft`,
                  });
                }
                if (summaryAltitudeParts.length === 0) {
                  summaryAltitudeParts.push({ label: 'Computed max MOCA', value: '—' });
                }
                return (
                  <details key={procIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#1565c0' }}>
                      {inp.airport_code || '—'} / RWY {inp.runway_designator || '—'}
                      {inp.approach_id ? ` – ${inp.approach_id}` : ''}
                      {inp.approach_type ? ` (${inp.approach_type})` : ''}
                      {inp.aircraft_category ? ` – Cat ${inp.aircraft_category}` : ''}
                      {inp.error ? <span style={{ color: '#d32f2f', marginLeft: 8 }}>Not evaluated</span> : proc.manualReviewRequired
                        ? <span style={{ color: reviewColor, marginLeft: 8 }}>{totalCritical > 0 ? '⚠' : 'ℹ'} Preliminary – manual review ({totalCritical} critical)</span> : totalCritical > 0
                        ? <span style={{ color: '#d32f2f', marginLeft: 8, fontWeight: 'bold' }}>⚠ Impact ({totalCritical} critical)</span>
                        : <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ No impact</span>}
                      {inp.error && <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {inp.error}</span>}
                      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 'normal', color: '#333' }}>
                        <div>
                          <strong>{language === 'sv' ? 'Flygplanskategori' : 'Aircraft category'}:</strong> {inp.aircraft_category || '—'}
                          {' | '}<strong>{t('oasPublishedMinima')}:</strong> {inp.oca != null ? `${inp.oca} ${inp.oca_unit || 'ft'}` : '—'}
                        </div>
                        <div><strong>{t('totalObstaclesAssessed')}:</strong> {obstacleCounts.obstaclesAssessed}</div>
                        <div><strong>{t('criticalObstacle')}:</strong>{' '}
                          <span style={{ color: totalCritical > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 'bold' }}>{totalCritical}</span>
                        </div>
                      </div>
                    </summary>

                    {proc.manualReviewRequired && <div style={{ color: reviewColor, margin: '8px 0', fontSize: 12 }}>
                      {language === 'sv' ? 'Avvikelse från ordinarie PANS OPS-konstruktion; manuell kontroll mot PANS-OPS krävs.'
                        : 'Departure from the standard PANS-OPS construction; manual review against PANS-OPS is required.'}
                      {(proc.manualReviewWarnings || []).map((warning, index) => <div key={index}>{warning.message}</div>)}
                    </div>}
                    {/* Input summary */}
                    <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#e8f4fd', padding: 8, borderRadius: 4 }}>
                      <b>Input Parameters</b>
                      <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={tdStyle}>Airport</td>
                            <td style={tdStyle}><b>{inp.airport_code || '—'}</b></td>
                            <td style={tdStyle}>Runway</td>
                            <td style={tdStyle}><b>{inp.runway_designator || '—'}</b></td>
                            <td style={tdStyle}>Approach ID</td>
                            <td style={tdStyle}><b>{inp.approach_id || '—'}</b></td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>Approach Type</td>
                            <td style={tdStyle}><b>{inp.approach_type || '—'}</b></td>
                            <td style={tdStyle}>RNAV Spec</td>
                            <td style={tdStyle}><b>{inp.rnav_specification || '—'}</b></td>
                            <td style={tdStyle}>Aircraft Cat</td>
                            <td style={tdStyle}><b>{inp.aircraft_category || '—'}</b>
                              {inp.aircraft_category === 'DL' && <span> (D speeds)</span>}
                            </td>
                          </tr>
                          <tr>
                            <td style={tdStyle}>OCA{inp.oca_source?.table === 'oas_minima' ? ' (oas_minima)'
                              : inp.oca_source?.table === 'gnss_approach_inputs' ? ' (procedure input)' : ''}</td>
                            <td style={tdStyle}>
                              <b>{inp.oca != null ? `${inp.oca} ${inp.oca_unit || 'ft'}` : '—'}</b>
                            </td>
                            <td style={tdStyle}>GPA/VPA (°)</td>
                            <td style={tdStyle}><b>{inp.gpa_vpa != null ? `${inp.gpa_vpa}°` : '—'}</b></td>
                            <td style={tdStyle}>MA Turn Type</td>
                            <td style={tdStyle}><b>{inp.missed_straight_to_altitude ? 'Alt – straight to altitude' : inp.missed_approach_turn_type || '—'}</b></td>
                          </tr>
                        </tbody>
                      </table>
                      {iasRows.length > 0 && <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: 'pointer' }}>IAS selection (kt)</summary>
                        <table style={{ borderCollapse: 'collapse', marginTop: 4 }}>
                          <thead><tr>
                            {['Segment', 'Input', 'Category standard', 'Selected base IAS', '+10%', 'Used IAS'].map(label =>
                              <th key={label} style={tdStyle}>{label}</th>)}
                          </tr></thead>
                          <tbody>{iasRows.map(({ label, selection, plus10 }) => <tr key={label}>
                            <td style={tdStyle}>{label}</td>
                            <td style={tdStyle}>{selection.inputKt ?? '—'}</td>
                            <td style={tdStyle}>{selection.standardKt ?? '—'}</td>
                            <td style={tdStyle}>{selection.iasKt}</td>
                            <td style={tdStyle}>{plus10 ? 'Yes' : 'No'}</td>
                            <td style={tdStyle}><b>{(selection.iasKt * (plus10 ? 1.1 : 1)).toFixed(1)}</b></td>
                          </tr>)}</tbody>
                        </table>
                      </details>}
                      {proc.missedEndpoint && <div style={{ marginTop: 8 }}>
                        <b>{proc.missedEndpoint.pointRole === 'turn' ? 'Missed approach turn point:' : 'Missed approach endpoint:'}</b> {proc.missedEndpoint.source === 'altitude'
                          ? proc.missedEndpoint.pointRole === 'turn' ? 'Calculated altitude turn point' : 'Calculated altitude endpoint'
                          : proc.missedEndpoint.ident || 'MAHWP/TP'}<br />
                        Source: {proc.missedEndpoint.source === 'waypoint' ? 'Waypoint projected onto final axis' : proc.missedEndpoint.source === 'altitude' ? 'SOC + climb gradient to specified altitude' : 'Entered distance'}<br />
                        Distance: {proc.missedEndpoint.distanceNm.toFixed(4)} NM {proc.missedEndpoint.beforeThr ? 'before' : 'past'} THR
                        {proc.missedEndpoint.source === 'waypoint' && <><br />Cross-track offset: {proc.missedEndpoint.crossTrackM.toFixed(2)} m</>}
                      </div>}
                      {(proc.lpv?.parameters || proc.xls?.parameters) && (() => {
                        const p = (proc.lpv || proc.xls).parameters;
                        const f = value => Number.isFinite(value) ? value.toFixed(2) : '—';
                        return <div style={{ marginTop: 8 }}>
                          <b>{inp.approach_type} – {p.oasCategory} / {p.evaluationMethod}</b><br />
                          FAP altitude: {f(p.altitudeAtFapM / 0.3048)} ft | FAP distance: {f(p.xfapM / 1852)} NM<br />
                          GARP → LTP: {f(p.garpM)} m | RDH: {f(p.rdhM)} m | Earth radius: {f(p.earthRadiusM)} m<br />
                          {p.isXls ? 'Height loss: category default or manual input' : `IAS at THR: ${f(p.iasAtThrKt)} kt`} | Height loss: {f(p.heightLossM)} + {f(p.heightLossCorrectionM)} m<br />
                          Wing semi-span: {f(p.k.wssM)} m | Wheel height: {f(p.k.whM)} m<br />
                          OAS MACG: {f(p.oasMacgPct)}% | MISAP MACG: {f(p.macgPct)}% | SOC altitude: {f(p.socAltitudeM / 0.3048)} ft<br />
                          {inp.missed_approach_turn_type === 'Alt' ? `${inp.missed_straight_to_altitude ? 'Straight to altitude' : 'Turn at altitude'}: ${inp.missed_turn_altitude_ft} ft AMSL`
                            : `Missed start: ${f(Math.abs(p.missedStationM) / 1852)} NM ${p.missedStationM < 0 ? 'before' : 'past'} THR`}
                          <details style={{ marginTop: 4 }}><summary>OAS constants</summary>
                            <table style={{ borderCollapse: 'collapse' }}><thead><tr>
                              {['Surface', 'A', 'B', 'C'].map(label => <th key={label} style={tdStyle}>{label}</th>)}
                            </tr></thead><tbody>{[['W', 'WA', null, 'WC'], ['WS', 'WSA', null, 'WSC'],
                              ['X', 'XA', 'XB', 'XC'], ['Y', 'YA', 'YB', 'YC'], ['Z', 'ZA', null, 'ZC']].map(([name, a, b, c]) =>
                              <tr key={name}><td style={tdStyle}>{name}</td>{[a, b, c].map((key, idx) =>
                                <td key={idx} style={tdStyle}>{key && Number.isFinite(p.k[key]) ? p.k[key] : '—'}</td>)}</tr>)}
                            </tbody></table>
                          </details>
                          {p.catIIConstants && <details style={{ marginTop: 4 }}><summary>ILS CAT II constants (inner OAS, 150 m)</summary>
                            <table style={{ borderCollapse: 'collapse' }}><thead><tr>
                              {['Surface', 'A', 'B', 'C'].map(label => <th key={label} style={tdStyle}>{label}</th>)}
                            </tr></thead><tbody>{[['W', 'WA', null, 'WC'], ['X', 'XA', 'XB', 'XC'],
                              ['Y', 'YA', 'YB', 'YC'], ['Z', 'ZA', null, 'ZC']].map(([name, a, b, c]) =>
                              <tr key={name}><td style={tdStyle}>{name}</td>{[a, b, c].map((key, idx) =>
                                <td key={idx} style={tdStyle}>{key && Number.isFinite(p.catIIConstants[key]) ? p.catIIConstants[key] : '—'}</td>)}</tr>)}
                            </tbody></table>
                          </details>}
                        </div>;
                      })()}
                      {proc.baro?.parameters && (() => {
                        const p = proc.baro.parameters;
                        const f = value => Number.isFinite(value) ? value.toFixed(2) : '—';
                        return <div style={{ marginTop: 8 }}>
                          <b>BARO/VNAV – {p.evaluationMethod}</b><br />
                          FAP procedure altitude: {f(p.altitudeAtFapM / 0.3048)} ft | Minimum temperature: {f(p.minTempC)} °C<br />
                          IAS at THR: {f(p.iasAtThrKt)} kt | Height loss: {f(p.heightLossM)} m + {f(p.heightLossCorrectionM)} m<br />
                          Temperature correction: {f(p.temperatureCorrectionM)} m | Minimum VPA: {f(p.minVpaDeg)}° | Maximum temperature: {f(p.maxTempC)} °C<br />
                          XFAP: {f(p.xfapM)} m | Xz: {f(p.xzM)} m | SOC altitude: {f(p.socAltitudeM / 0.3048)} ft<br />
                          MOC intermediate / MA initial / MA final: {f(p.mocIntermediateM)} / {f(p.mocMissedInitialM)} / {f(p.mocMissedFinalM)} m
                        </div>;
                      })()}
                      {proc.summary && (
                        <div style={{ marginTop: 6 }}>
                          {summaryAltitudeParts.map((part, partIdx) => (
                            <span key={`${part.label}-${partIdx}`}>
                              {partIdx > 0 && ' | '}
                              <b>{part.label}:</b> {part.value}
                            </span>
                          ))}
                          {' | '}
                          <b>Segments evaluated:</b> {summary.segsEvaluated}
                        </div>
                      )}
                    </div>

                    {(proc.segments || []).some(seg => seg.ifTurn) && <details style={{ marginTop: 8, fontSize: 12 }}>
                      <summary style={{ cursor: 'pointer' }}>{language === 'sv'
                        ? 'Intermediate – konstruktionsdata för IF-svängar'
                        : 'Intermediate – IF turn construction data'}</summary>
                      {(proc.segments || []).filter(seg => seg.ifTurn).map((seg, index) => {
                      const p = seg.ifTurn;
                      return <div key={`if-turn-${index}`} style={{ fontSize: 12, marginTop: 8, padding: 6, background: '#e3f2fd' }}>
                        <b>{seg.segLabel} – IF turn</b><br />
                        IAS: {p.iasKt.toFixed(1)} kt (IF / category) · TAS: {p.tasKt.toFixed(1)} kt · Wind: {p.windKt?.toFixed(1) ?? '—'} kt<br />
                        TAS altitude: <b>{p.altitudeFt.toFixed(2)} ft</b> = {p.referenceType}
                        {p.referenceIdent !== p.referenceType ? ` (${p.referenceIdent})` : ''} {p.referenceAltitudeFt.toFixed(2)} ft
                        {' + '}{p.gradientPercent.toFixed(1)}% × {p.distanceNm.toFixed(3)} NM
                        {' '}({p.heightGainFt.toFixed(2)} ft rise)
                      </div>;
                    })}
                    </details>}
                    {/* Per-segment obstacle tables */}
                    {gnssResultSegments(proc.segments, language === 'sv').filter(seg => seg.obsRows && seg.obsRows.length > 0).map((seg, segIdx) => {
                      const segFamily = String(seg.segFamily || '').toLowerCase();
                      const segmentRouteLabel = getSegmentWptPairLabel(seg) || `${seg.fromPhase} → ${seg.toPhase}`;
                      const isBaroSurface = !!(seg.baroSurface || seg.lpvSurface || seg.xlsSurface);
                      const isBaroMissed = segFamily === 'missed' && !!seg.missedApproach && seg.missedResultPhase !== 'initial';
                      const altitudeAssessment = isBaroMissed && (seg.missedResultPhase === 'turn_initial'
                        || seg.obsRows.every(row => row.assessmentMethod === 'full_moc_no_climb_credit'));
                      const isFinalSegment = segFamily === 'final' && !isBaroSurface;
                      const usesMoca = segFamily === 'initial' || segFamily === 'intermediate';
                      const missedParameters = gnssMissedParameters(seg, language === 'sv');
                      const calculatedAltLabel = usesMoca ? 'Calculated MOCA' : isBaroMissed ? 'Required altitude' : 'Calculated OCA';
                      const publishedAltLabel = usesMoca ? 'Published MOCA' : 'Published OCA';
                      const tableHeaders = [
                        'Obstacle',
                        'Area',
                        'Elev (m)',
                        'Elev (ft)',
                        'MOC (m)',
                        ...(seg.xlsSurface ? ['OAS category', 'Secondary correction (m)'] : []),
                        ...(isBaroSurface ? ['Surface altitude (m)', 'Difference (m)', 'Equivalent altitude (m)', 'Height loss (m)'] : []),
                        ...(isBaroMissed ? ['Available altitude (m)', 'Required MACG (%)', 'Available MACG (%)'] : []),
                        `${calculatedAltLabel} (m)`,
                        `${calculatedAltLabel} (ft)`,
                        publishedAltLabel,
                        ...(isFinalSegment ? ['Step-down'] : []),
                        `${isBaroSurface ? 'Minima clearance' : altitudeAssessment ? 'Altitude clearance' : isBaroMissed ? 'Climb clearance' : 'Clearance'} (m)`,
                        `${isBaroSurface ? 'Minima clearance' : altitudeAssessment ? 'Altitude clearance' : isBaroMissed ? 'Climb clearance' : 'Clearance'} (ft)`,
                        'Critical',
                      ];
                      return (
                      <div key={segIdx} style={{ marginTop: 8 }}>
                        <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4, color: '#1565c0' }}>
                          Segment: {seg.segLabel || seg.segType} ({segmentRouteLabel}) – {gnssMocLabel(seg, language === 'sv')}
                          {seg.criticalCount > 0 && (
                            <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {seg.criticalCount} critical</span>
                          )}
                        </div>
                        {missedParameters.length > 0 && <div style={{ background: '#e8f4fd', padding: 6, fontSize: 12, marginBottom: 5 }}>
                          {missedParameters.map(([label, value], index) => <div key={index}><b>{label}:</b> {value}</div>)}
                        </div>}
                        {seg.manualReviewRequired && <div style={{ fontSize: 12, color: seg.criticalCount > 0 ? '#b71c1c' : '#555', marginBottom: 5 }}>
                          {language === 'sv' ? 'Hinder i granskningsytan bedöms med full MOC och utan att tillgodoräkna ytterligare höjdmarginal från stiggradienten.'
                            : 'Obstacles in the review area are assessed with full MOC and no additional climb credit.'}
                        </div>}
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: '#bbdefb' }}>
                                {tableHeaders.map(h => (
                                  <th key={h} style={{ ...tdStyle, fontWeight: 'bold' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {seg.obsRows.map((row, rowIdx) => {
                                const isCrit = row.isCritical;
                                const rowPublishedMinimum = row.publishedMinimum || seg.publishedMinimum || null;
                                const rowPublishedAltText = rowPublishedMinimum?.value != null
                                  ? `${rowPublishedMinimum.value} ${rowPublishedMinimum.unit || ''}`.trim()
                                  : '—';
                                const stepdownApplied = isFinalSegment && (
                                  rowPublishedMinimum?.stepdownApplied === true
                                  || rowPublishedMinimum?.stepdownDistanceNm != null
                                );
                                const stepdownText = stepdownApplied
                                  ? `YES${rowPublishedMinimum?.stepdownDistanceNm != null ? ` (${rowPublishedMinimum.stepdownDistanceNm} NM)` : ''}`
                                  : 'NO';
                                return (
                                  <tr key={rowIdx} style={{ background: isCrit ? '#ffebee' : (rowIdx % 2 === 0 ? '#f5f5f5' : 'white') }}>
                                    <td style={tdStyle}>{row.obstacleName}</td>
                                    <td style={tdStyle}>{row.areaType}</td>
                                    <td style={tdStyle}>{row.elevM ?? '—'}</td>
                                    <td style={tdStyle}>{row.elevFt ?? '—'}</td>
                                    <td style={tdStyle}>{row.mocM ?? '—'}</td>
                                    {seg.xlsSurface && <>
                                      <td style={tdStyle}>{row.oasCategory ?? '—'}</td>
                                      <td style={tdStyle}>{row.secondaryCorrectionM ?? '—'}</td>
                                    </>}
                                    {isBaroSurface && <>
                                      <td style={tdStyle}>{row.surfaceAltitudeM ?? '—'}</td>
                                      <td style={tdStyle}>{row.differenceM ?? '—'}</td>
                                      <td style={tdStyle}>{row.equivalentAltitudeM ?? '—'}</td>
                                      <td style={tdStyle}>{row.heightLossM ?? '—'}</td>
                                    </>}
                                    {isBaroMissed && <>
                                      <td style={tdStyle}>{row.availableAltitudeM ?? '—'}</td>
                                      <td style={tdStyle}>{row.requiredMacgPercent ?? '—'}</td>
                                      <td style={tdStyle}>{row.availableMacgPercent ?? '—'}</td>
                                    </>}
                                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{isBaroSurface ? row.ocaM?.toFixed(2) ?? '—' : row.ocaM ?? '—'}</td>
                                    <td style={tdStyle}>{isBaroSurface ? row.ocaFt?.toFixed(2) ?? '—' : row.ocaFt ?? '—'}</td>
                                    <td style={tdStyle}>{rowPublishedAltText}</td>
                                    {isFinalSegment && (
                                      <td style={tdStyle}>
                                        {stepdownApplied
                                          ? <span style={{ color: '#1565c0', fontWeight: 'bold' }}>{stepdownText}</span>
                                          : <span style={{ color: '#666' }}>{stepdownText}</span>}
                                      </td>
                                    )}
                                    <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c', fontWeight: isCrit ? 'bold' : 'normal' }}>
                                      {row.clearanceM ?? '—'}
                                    </td>
                                    <td style={{ ...tdStyle, color: isCrit ? '#d32f2f' : '#388e3c', fontWeight: isCrit ? 'bold' : 'normal' }}>
                                      {row.clearanceFt ?? '—'}
                                    </td>
                                    <td style={tdStyle}>
                                      {isCrit
                                        ? <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>YES</span>
                                        : <span style={{ color: '#666' }}>—</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      );
                    })}

                  </details>
                );
              })}
              </>
            ) : (
              gnssApchAnalysis && (
                <p style={{ color: '#666' }}>
                  {gnssApchAnalysis.message || (gnssResults.length > 0 && gnssResults.every(result => !!result.input?.error)
                    ? language === 'sv' ? 'GNSS-beräkningarna kunde inte slutföras.'
                      : 'GNSS calculations could not be completed.'
                    : gnssResults.length > 0
                    ? language === 'sv' ? 'Inga hinder hittades inom de beräknade GNSS-procedurernas skyddsytor.'
                      : 'No obstacles found within the evaluated GNSS procedure protection areas.'
                    : language === 'sv' ? 'Inga GNSS-procedurer finns att redovisa.'
                      : 'No GNSS approach configurations found.')}
                </p>
              )
            )}
          </div>
        );
      })()}

      {isAuthenticated && user?.betald === 'JA' && <LocApproachResults analysis={locAnalysis} error={locError} language={language} />}

      {error && (
        <div style={{ color: "#b30000", marginTop: 8 }}>
          <b>{t('error')}:</b> {error}
        </div>
      )}

      {analysis && (
        <>
          <div style={{ marginTop: 8 }}>
            <span style={{ background: "#d8ffd8", padding: "2px 6px", marginRight: 8 }}>{t('greenHint')}</span>
            <span style={{ background: "#ffd8d8", padding: "2px 6px" }}>{t('redHint')}</span>
          </div>
          {(isAuthenticated && user?.betald === 'JA') && analysis.map((geoResult, i) =>
              (geoResult.matches || [])
                .filter(row => row.highlight && Array.isArray(row.penetrations) && row.penetrations.length > 0)
                .map((row, idx2) =>
                  <details key={`${i}_${idx2}`} style={{ margin: "10px 0 0 12px" }}>
                    <summary>
                      Penetrationspunkter för {geoResult.properties?.name || `Geometri #${i + 1}`} mot {row.surface_name}:
                    </summary>
                    <ul style={{ fontSize: 14, marginTop: 4 }}>
                      {row.penetrations.map((p, j) =>
                        <li key={j}>
                          Lat:{p.y.toFixed(6)} Lng:{p.x.toFixed(6)} Z:{p.z.toFixed(2)} (yta Z:{p.surface_z.toFixed(2)}) diff:{p.penetration_delta.toFixed(2)}
                        </li>
                      )}
                    </ul>
                  </details>
                )
            )}
        </>
      )}
      <div style={{ marginTop: 16, background: "#f8f8e8", padding: 10, borderRadius: 6, fontSize: 15 }}>
        <b>{t('tips')}:</b>
        <ul style={{ margin: "4px 0 2px 20px" }}>
          <li>{t('toggleLayersTip')}</li>
          <li>{t('switchBasemapTip')}</li>
          <li>{t('drawDeleteTip')}</li>
          <li>{t('coordHeightPanelTip')}</li>
          <li>{t('circleZRadiusTip')}</li>
          <li>{t('importExportTip')}</li>
          <li>
            <b>{t('tipAlwaysChooseSaveLocation')}</b>
            {t('browserDownloadHint')}
          </li>
        </ul>
      </div>
    </div>
  );
}

export default Draw3DMap;
